// ---------------------------------------------------------------------------
// Lecteur video maison.
//
// Les controles natifs sont ecartes pour deux raisons. La premiere est
// l'apparence : chaque navigateur impose la sienne, et aucune ne va avec le
// reste. La seconde est technique et decisive : le flux remuxe sort en MP4
// fragmente `empty_moov`, donc sans duree ni index. Un lecteur natif y voit une
// duree infinie et desactive tout deplacement.
//
// Ce lecteur contourne les deux. La duree vient du serveur (ffprobe sur la
// source), et le deplacement ne demande rien au navigateur : il redemande le
// flux a la seconde voulue et retient ce decalage. Le temps affiche est donc
// toujours `decalage + currentTime`, jamais l'un des deux seul.
// ---------------------------------------------------------------------------

import { lireReglages, ecrireReglages, appliquerStyle, brancherRendu } from './soustitres.js';
// L'ASS avec ses styles et le PGS : un rendu par bibliothèque, monté à la
// demande sur un canvas — voir « sous-titres riches » plus bas.
import { creerRenduRiche, urlsDesOutils } from './st-riches.js';
import { construirePanneau } from './st-panneau.js';
import {
  actionPour, raccourcisAffichables, vitesseSuivante, VITESSES,
} from './raccourcis.js';
import { ICONES, ICONES_DES_BOUTONS } from './icones.js';
import { brancherCast } from './cast.js';
import { entreesDuMenuVoix, boutonVoixActif, lignesDuPanneauSources, nomDeLangue } from './voix-menu.js';
import { guetterLeSon, releverDe } from './son-sorti.js';
import { langueDuCode } from './langues-st.js';

// ⚠️ LA VITESSE DE LECTURE EXISTE VRAIMENT, CETTE FOIS.
//
// `VITESSES` a longtemps traîné ici sans lecteur, puis a été retirée : le
// sélecteur n'existait nulle part, et `l-vit` — la case qui affiche la
// vitesse — n'était même pas dans la page. La ligne qui l'écrit ne faisait
// donc rien depuis toujours, sans que personne le voie : `pose` ignore en
// silence un identifiant absent.
//
// Les crans vivent maintenant dans `raccourcis.js`, avec les touches qui les
// changent et le banc qui les éprouve. Ce fichier ne fait que les appliquer.
const EXEMPLE = 'Ceci est un aperçu des sous-titres.\nDeuxième ligne, pour juger l\u2019interligne.';
const INACTIVITE = 2600; // ms avant que les controles s'effacent

/**
 * Ce qu'il faut ecrire dans le reglage de son, a partir de l'etat de la
 * balise et de ce qui etait retenu avant.
 *
 * ⚠️ UNE SOURDINE TECHNIQUE RENDAIT LES GENS MUETS POUR TOUJOURS.
 *
 * Quand la politique d'autoplay refuse la lecture meme apres un appui, le
 * dernier recours est de partir SANS LE SON : la sourdine, elle, n'est
 * jamais bloquee. Mais `video.muted = true` emet un `volumechange` comme
 * n'importe quel autre, et le reglage partait dans le stockage : qui ne
 * cliquait pas ensuite sur « retablir le son » revenait muet a TOUTES ses
 * seances suivantes, sur cet appareil, sans que rien ne l'explique.
 *
 * Une coupure imposee par le navigateur n'est pas une decision : on garde
 * alors ce qui etait retenu. Le volume, lui, se retient toujours — il n'est
 * pas touche par ce repli.
 */
export function sonARetenir({ volume, coupe, technique }, avant = null) {
  const v = Number(volume);
  return {
    volume: Number.isFinite(v) ? Number(Math.min(1, Math.max(0, v)).toFixed(3)) : 1,
    coupe: technique ? Boolean(avant?.coupe) : Boolean(coupe),
  };
}

/** h:mm:ss, toujours. */
export function horloge(s) {
  if (!Number.isFinite(s) || s < 0) return '-:--:--';
  const t = Math.floor(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, '0');
  // TOUJOURS H:MM:SS, même sous l'heure (Toji, 20/10 : « X:XX:XX, la même
  // des deux côtés ») : écoulé et durée gardent la même forme et la même
  // largeur, rien ne saute quand on passe la première heure.
  return `${h}:${String(m).padStart(2, '0')}:${sec}`;
}

// ---------------------------------------------------------------------------
// LIRE UNE PLANCHE DE VIGNETTES
//
// Une planche est une grille d'images extraites de l'épisode. Pour montrer la
// bonne au survol, il faut savoir QUEL INSTANT montre chaque case — c'est ce
// qu'on appelle ici l'échelle de la planche.
//
// ⚠️ CE CALCUL A ÉTÉ FAUX DEUX FOIS, DE DEUX FAÇONS DIFFÉRENTES.
//
//   1. `floor(seconde / 10)` supposait que la planche couvrait exactement la
//      durée du fichier. Elle ne la couvrait pas : elle avait été fabriquée
//      d'après la durée du CATALOGUE (24 min annoncées, 27 min réelles). Passé
//      24 minutes, le rang plafonnait et le survol restait figé.
//
//   2. Le correctif d'alors — étirer la planche sur la durée réelle — reposait
//      sur une seconde supposition, aussi fausse : qu'une planche de N cases
//      contenait N images régulièrement espacées. Elle n'en contient pas N.
//      Le générateur retient une image CLÉ toutes les dix secondes AU MOINS ;
//      quand le fichier n'a d'image clé que toutes les treize secondes, la
//      planche contient un quart d'images en moins, et les dernières cases sont
//      noires. Mesuré le 05/09 sur un fichier fait pour l'occasion : 300 s,
//      images clés toutes les 13 s, 24 images là où la durée en annonçait 30.
//      Le survol atteignait donc la dernière image du fichier à 230 s sur 300,
//      soit 77 % de la barre. En production : la dernière image d'un épisode de
//      24:33 affichée dès 18:33, soit 75,6 %. Même mécanisme, même rapport.
//
// D'où la règle actuelle : LA PLANCHE PORTE SON ÉCHELLE, ou elle n'est pas lue
// exactement. `instants[i]` donne la seconde du fichier que montre la case i,
// relevée à la génération. Il n'y a plus rien à supposer — ni le nombre
// d'images, ni leur espacement, ni même sa régularité.
// ---------------------------------------------------------------------------

/**
 * Quelle case de la planche montrer pour un survol à `seconde`, ou `null`.
 *
 * `null` veut dire « ne montre pas d'image ». C'est un résultat à part
 * entière, pas un échec : mieux vaut une bulle qui n'affiche que l'heure
 * qu'une bulle qui affiche l'image d'un autre moment — le survol sert à
 * décider où l'on va, une image fausse fait manquer la scène qu'on visait.
 *
 * Deux régimes, selon ce que la planche sait d'elle-même :
 *
 *   ÉCHELLE MESURÉE (`instants`) — la case est celle du dernier instant relevé
 *   qui ne dépasse pas la seconde survolée. Exact, même si l'espacement des
 *   images est irrégulier. Et comme les instants ne décrivent QUE le fichier
 *   sur lequel ils ont été relevés, on refuse de les appliquer à un fichier
 *   d'une autre durée : une autre version de l'épisode, un autre montage, une
 *   source qui a changé — les images ne seraient plus en face de rien.
 *
 *   ÉCHELLE SUPPOSÉE (planches d'avant le correctif) — pas de relevé, donc pas
 *   d'exactitude possible. On répartit les cases sur la durée du fichier. Le
 *   résultat est approximatif, mais il EXISTE sur toute la barre, et c'est ce
 *   qui évite de vider l'aperçu de tout le parc le temps de le refaire. Le
 *   serveur remet ces planches en fabrication au premier survol.
 */
export function caseDeLaSeconde(apercu, seconde, dureeFichier) {
  if (!apercu?.vignette || !(apercu.total > 0) || !(apercu.colonnes > 0)) return null;

  const s = Math.max(0, Number.isFinite(seconde) ? seconde : 0);
  const intervalle = apercu.intervalle > 0 ? apercu.intervalle : 10;
  const releves = Array.isArray(apercu.instants) && apercu.instants.length >= 2
    ? apercu.instants
    : null;

  let rang;
  if (releves) {
    // La planche dit pour quel fichier elle a été faite. Si ce n'est pas
    // celui-là, on ne montre rien — la tolérance vaut un intervalle, parce que
    // ffprobe et le conteneur ne s'accordent pas toujours à la seconde près.
    if (dureeFichier > 0 && Math.abs((apercu.dureeGeneree ?? 0) - dureeFichier) > intervalle) {
      return null;
    }
    // Le dernier instant qui ne dépasse pas. Une recherche linéaire sur cent
    // cinquante valeurs, à chaque déplacement de souris : quelques microsecondes,
    // très en dessous de ce que coûte le repositionnement de la bulle.
    const n = Math.min(releves.length, apercu.total);
    rang = 0;
    for (let i = 1; i < n; i += 1) {
      if (releves[i] <= s) rang = i;
      else break;
    }
  } else {
    const couverte = apercu.dureeGeneree > 0 ? apercu.dureeGeneree : apercu.total * intervalle;
    const brut = dureeFichier > 0 && Math.abs(couverte - dureeFichier) > intervalle
      ? Math.floor((s / dureeFichier) * apercu.total)
      : Math.floor(s / intervalle);
    rang = Math.max(0, Math.min(apercu.total - 1, brut));
  }

  return { rang, col: rang % apercu.colonnes, lig: Math.floor(rang / apercu.colonnes) };
}

// Ce que le bouton de saut propose, selon qui appuie.
//
// Un même bouton, deux gestes très différents : il fait, ou il demande. Le mot
// doit dire lequel AVANT le clic — découvrir après coup qu'on vient d'ouvrir un
// vote, c'est ce qui fait recliquer trois fois.
const MOTS_SAUT = {
  intro: { seul: "Passer l'intro", vote: "Demander à passer l'intro" },
  // Le récap (« previously on ») a son propre bouton, distinct de l'intro (Toji, 22/10).
  // Comme le générique : pas un vote, le serveur n'en connaît que trois.
  recap: { seul: 'Passer le récap', vote: null },
  suivant: { seul: 'Épisode suivant', vote: "Demander l'épisode suivant" },
  // « fin » n'est pas une action votable : le serveur n'en connaît que trois
  // (intro, pause, suivant), et sans épisode suivant il n'y a rien à faire
  // subir au groupe. Le bouton ne s'affiche donc que pour qui tranche seul —
  // proposer un geste qui n'aboutira jamais est pire que ne rien proposer.
  fin: { seul: 'Passer la fin', vote: null },
  // Pendant l'outro, toujours : la carte « Épisode suivant » ne vient qu'après
  // (`momentEpisodeSuivant`), pour ne pas couper une scène post-générique.
  generique: { seul: 'Passer le générique', vote: null },
};

/**
 * L'état du bouton de saut, à cette seconde et pour cette personne.
 *
 * Pure : elle ne lit ni le DOM ni la séance, on lui donne les deux. C'est ce
 * qui permet d'éprouver les libellés et le compte sans navigateur.
 *
 * @param {'intro'|'suivant'|'fin'|''} quoi ce que le minutage propose
 * @param {object|null} seance { actif, jAiLaMain, exemptionPilotes, seuil, votes, mesVoix }
 * @returns {null|{quoi,texte,seul,ouvert,attente,voix,seuil}} null = pas de bouton
 */
export function etatDuSaut(quoi, seance) {
  const mots = MOTS_SAUT[quoi];
  if (!mots) return null;

  // Hors séance, ou pilote quand les pilotes sont exemptés : le clic agit, et
  // le bouton le dit. L'exemption coupée, un pilote redevient un votant comme
  // les autres — administrateurs compris — et son bouton change de mot.
  const exemptes = seance?.exemptionPilotes !== false;
  if (!seance?.actif || (seance.jAiLaMain && exemptes)) {
    return { quoi, texte: mots.seul, seul: true, ouvert: false, attente: false, voix: 0, seuil: 0 };
  }
  if (!mots.vote) return null;

  const vote = (seance.votes ?? []).find((v) => v.action === quoi) ?? null;
  // Le seuil porté par le vote prime sur celui de l'état : c'est celui avec
  // lequel les voix ont été comptées. Prendre l'autre afficherait « 3 / 4 »
  // sur un vote que le serveur tranche à trois.
  const seuil = Math.max(1, Number(vote?.seuil ?? seance.seuil ?? 0) || 1);
  const voix = Math.max(0, Number(vote?.voix ?? 0));
  if (!vote) {
    return { quoi, texte: mots.vote, seul: false, ouvert: false, attente: false, voix: 0, seuil };
  }

  // Ma voix est déjà dans le compte : le bouton n'a plus rien à me proposer, il
  // rend compte. Celle des autres, elle, s'attend encore — d'où « se joindre ».
  const attente = (seance.mesVoix ?? []).includes(quoi);
  return {
    quoi,
    texte: `${attente ? 'En attente' : 'Se joindre'} · ${voix} / ${seuil}`,
    seul: false,
    ouvert: true,
    attente,
    voix,
    seuil,
  };
}

/** L'action d'un vote ouvert que ce poste peut rejoindre, ou '' (pure). */
export function voteOuvert(seance) {
  if (!seance?.actif) return '';
  return (seance.votes ?? []).find((v) => MOTS_SAUT[v.action]?.vote)?.action ?? '';
}

/**
 * La seconde où la carte « Épisode suivant » peut paraître : le début de la
 * scène post-générique s'il y en a une, sinon la fin de l'outro. `null` sans
 * bornes — pas de carte avant la fin (règle de Nuvio, PlayerNextEpisodeRules).
 */
export function momentEpisodeSuivant(b) {
  return b?.postGenerique?.debut ?? b?.outro?.fin ?? null;
}

// ---------------------------------------------------------------------------
// LA FIN D'UN ÉPISODE
//
// ⚠️ ELLE NE MENAIT NULLE PART.
//
// L'épisode se terminait, l'image restait sur la dernière image, la barre à
// 100 %, et rien ne se passait. Le bouton « Épisode suivant » (`l-suiv`) était
// bien là — dans la barre de commandes, qui s'efface au bout de 2,6 s
// d'inactivité. Et `l-saut`, lui, ne vit qu'entre `outro.debut` et
// `outro.fin - 0.5` : à la seconde où l'épisode s'arrête, il a déjà disparu.
// Il ne restait donc qu'à bouger la souris pour retrouver une barre, puis à
// deviner lequel des dix boutons enchaîne.
//
// D'où la carte « Épisode suivant » (Toji, 21/10 : en bas à droite, comme sur
// Netflix) : vignette et titre du suivant, une barre qui se remplit jusqu'à la
// dernière seconde, puis l'enchaînement — sans attente après `ended`.
//
// ⚠️ ET EN SÉANCE, C'EST CELUI QUI PILOTE QUI DÉCIDE — comme pour les sauts
// d'intro. Un décompte qui tourne chez huit personnes, c'est huit changements
// d'épisode concurrents ; et un décompte qui tourne chez quelqu'un dont le clic
// n'aboutit pas est un mensonge à l'écran. La décision est donc EXACTEMENT
// celle du bouton de saut : on la lui demande (`etatDuSaut('suivant', …)`)
// plutôt que d'en écrire une seconde qui dériverait de la première.
// ---------------------------------------------------------------------------

/**
 * La carte « Épisode suivant » paraît à cette distance de la fin, et son compte
 * à rebours va de ce nombre à 0 (Toji, 22/10 : « façon Netflix », 10 s précises).
 */
export const CARTE_SANS_BORNES_S = 10;

/**
 * Ce que le carton de fin d'épisode doit montrer — ou rien du tout.
 *
 * Pure, sur le gabarit d'`etatDuSaut` : ni DOM ni séance, on lui donne les
 * deux. Éprouvée par `essais/fin-de-partie.mjs`.
 *
 * @param {object} o
 * @param {boolean} o.fini le moment de la carte est-il arrivé (ou `video.ended`) ?
 * @param {boolean} o.voisinSuivant y a-t-il un épisode après celui-ci ?
 * @param {object|null} o.seance la vue de séance, telle que `majSeance` la reçoit.
 * @param {boolean} o.annule quelqu'un a appuyé sur « Annuler » sur CE carton.
 * @returns {null|{quoi,texte,seul,ouvert,attente,voix,seuil,decompte,titre,note}}
 *   `null` = aucun carton. `decompte` dit si le compte à rebours TOURNE : il
 *   ne tourne que pour qui peut trancher seul.
 */
export function etatFinDePartie({ fini, voisinSuivant, seance = null, annule = false } = {}) {
  // Le dernier épisode d'une série n'a rien à proposer. Un carton « Épisode
  // suivant » qui ne mène nulle part serait pire que la dernière image.
  if (!fini || !voisinSuivant || annule) return null;

  // La main, l'exemption des pilotes, le vote ouvert, le seuil : tout est déjà
  // décidé là. On n'ajoute que ce qui appartient à la fin — le décompte.
  const saut = etatDuSaut('suivant', seance);
  if (!saut) return null;

  return {
    ...saut,
    titre: 'Épisode suivant',
    // Sur la carte, le bouton dit simplement « Suivant » quand il part seul ; le
    // libellé de vote garde son sens (« Demander l'épisode suivant »).
    texte: saut.seul ? 'Suivant' : saut.texte,
    // ⚠️ LE DÉCOMPTE N'APPARTIENT QU'À CELUI DONT LE CLIC PART.
    //
    // Afficher « dans 3 s… 2… 1… » à quelqu'un qui ne tient pas la séance,
    // c'est promettre un enchaînement qui n'aura pas lieu au zéro — et le
    // laisser croire que le lecteur s'est bloqué. Sans la main, on ne compte
    // pas : on propose de demander, et on attend la salle.
    decompte: saut.seul === true,
    note: saut.seul
      ? null
      : (saut.attente
        ? 'La salle décide. Ta voix est donnée.'
        : 'La salle décide : demande, et l’épisode part quand elle est d’accord.'),
  };
}

// ---------------------------------------------------------------------------
// UNE SOURCE QUI MEURT DOIT SE VOIR
//
// Le démuxeur MKV sait désormais crier (voir `creerRelaisDePanne` dans
// `mkv.js`). Encore faut-il que son cri arrive à qui peut agir : `main.js`,
// qui redemande une source. Le lecteur n'est que le relais — et un relais qui
// répète tout serait pire que rien.
// ---------------------------------------------------------------------------

/**
 * Faut-il faire remonter cette panne de source ?
 *
 * Pure — éprouvée par `essais/panne-source.mjs`. Deux refus, et chacun évite
 * une résolution de source inutile :
 *
 *   · la panne porte sur une source qu'on ne regarde PLUS (on a changé
 *     d'épisode pendant qu'elle mourait) : c'est la même garde que
 *     `reclamerDuree`, `reclamerApercu` et `reclamerBornes` ;
 *   · elle a déjà été signalée pour cette source-là : `main.js` ne compte
 *     qu'une seule reprise par ouverture, un second cri la lui ferait
 *     dépenser pour rien et afficherait « source indisponible » sur une
 *     tentative qui n'a pas encore eu lieu.
 */
/**
 * CE QU'UNE PREUVE DE LECTURE DOIT DÉFAIRE — les deux, INDÉPENDAMMENT.
 *
 * ⚠️ « LA SOURCE NE DÉMARRE PAS » S'AFFICHAIT PENDANT QUE ÇA JOUAIT.
 *
 * Toji, 14/09, capture à l'appui : « alors qu'elle est en train de tourner.
 * C'est bugué parce que ça n'affiche pas l'état direct. » Un faux négatif est
 * pire qu'une absence de message : il dit d'abandonner une lecture qui marche.
 *
 * Le mécanisme d'effacement EXISTAIT (`oublierAbandon`) et il était APPELÉ —
 * mais derrière une porte que l'abandon venait lui-même de fermer :
 *
 *     function preuveDeLecture() {
 *       if (!sourceVierge || video.paused || video.seeking) return;   // ← ici
 *       if (…) { devoiler(); oublierAbandon(); }
 *     }
 *
 * `devoiler()` pose `sourceVierge = false`. Et le chemin d'abandon appelle
 * `devoiler()` UNE LIGNE avant d'écrire son message (voir `amorcer`). Donc, à
 * la seconde où le message apparaît, `sourceVierge` vaut déjà `false` : la
 * fonction chargée d'effacer le message sortait à sa première ligne, à chaque
 * appel, pour toujours. Quatre appels par seconde pendant tout l'épisode, et
 * pas un seul qui pouvait atteindre l'effacement. Le code était juste ; la
 * garde d'un AUTRE effet le rendait inatteignable.
 *
 * D'où cette fonction : un effet, une condition. Le voile se lève si le voile
 * est là ; le message s'efface s'il est là. Aucun des deux ne dépend de
 * l'autre.
 *
 * @param {boolean} joue           la lecture avance vraiment (mesuré, pas cru)
 * @param {boolean} voileEnCours   `sourceVierge` — la source n'a encore rien montré
 * @param {boolean} abandonAffiche « la source ne démarre pas » est à l'écran
 */
export function aDefaireApresPreuve({ joue, voileEnCours, abandonAffiche } = {}) {
  if (!joue) return { devoiler: false, effacerAbandon: false };
  return { devoiler: !!voileEnCours, effacerAbandon: !!abandonAffiche };
}

export function panneASignaler({ ref, refCourante, dejaSignalee = null }) {
  if (!ref) return false;
  if (ref !== refCourante) return false;
  if (dejaSignalee === ref) return false;
  return true;
}

/**
 * Le relais de panne du lecteur : une panne par source, vers un seul repreneur.
 *
 * C'est le corps de l'ancienne `signalerPanneSource`, sorti de `creerLecteur`
 * pour être éprouvé sans DOM (voir `essais/panne-source.mjs`) — ce qui décide
 * ne touche ni la balise ni le réseau. Le lecteur ne résout aucune source
 * lui-même : il ne connaît ni les add-ons, ni les liens, ni la trace. Son rôle
 * s'arrête à porter la panne jusqu'à `main.js`, qui traite exactement comme
 * `v.onerror` (voir `brancherReprise`) : la ligne d'informations dit « source
 * perdue, nouvelle tentative… » et la résolution repart.
 *
 * @param {object} o
 * @param {() => (string|null)} o.refCourante la source qu'on regarde MAINTENANT
 *   — lue à chaque cri, jamais figée : c'est elle qui périme une panne tardive.
 * @param {(raison: string) => void} [o.noter] la trace locale, pour qui lit la
 *   console.
 * @param {(texte: string) => void} [o.annoncer] la ligne d'informations, quand
 *   personne n'est branché pour reprendre.
 */
export function creerSignalDePanne({ refCourante, noter = () => {}, annoncer = () => {} }) {
  // La source pour laquelle une panne a déjà été criée. Remise à zéro par
  // `nouvelleSource` — que `poserSource` appelle : une panne par source, pas
  // une par session.
  let dejaSignalee = null;
  // Posé par `main.js` (voir la méthode `surPanneSource` du lecteur) : c'est
  // lui qui sait redemander une source. Absent, on ne peut au moins pas rester
  // muet — voir `signaler`.
  let rappel = null;
  return {
    /** Qui reprend. `null` — ou tout ce qui n'est pas une fonction — débranche. */
    brancher(r) { rappel = typeof r === 'function' ? r : null; },
    /** Chaque source a droit à son propre cri. */
    nouvelleSource() { dejaSignalee = null; },
    /**
     * Une source vient de mourir en cours de lecture : on le fait savoir.
     * Rend vrai si la panne a été portée — au repreneur, ou à l'écran.
     */
    signaler(ref, raison) {
      if (!panneASignaler({ ref, refCourante: refCourante(), dejaSignalee })) return false;
      dejaSignalee = ref;
      noter(raison);
      if (rappel) { rappel({ ref, raison }); return true; }
      // ⚠️ RIEN DE BRANCHÉ N'EST PAS UNE RAISON DE SE TAIRE.
      //
      // C'est précisément le défaut qu'on corrige : une panne qui ne se voit
      // nulle part. Sans repreneur, on ne peut pas relancer la résolution — mais
      // on peut au moins dire que l'image ne reviendra pas toute seule, plutôt
      // que de laisser quelqu'un attendre devant une image figée.
      annoncer('Source perdue — reviens en arrière et relance l’épisode.');
      console.warn('[lecteur] source perdue, aucun repreneur branché :', raison);
      return true;
    },
  };
}

// ---------------------------------------------------------------------------
// LE CAPTEUR DE CHEMIN : PAR OÙ CHAQUE LECTURE EST PASSÉE, ET POURQUOI
//
// Trois chemins mènent une image à l'écran — la balise lit le fichier tel quel
// (`direct`), le démuxeur le lit dans la page (`mse`), ou le serveur le remuxe
// (`remux`) — et le choix se fait dans `poserSource`, sur ce que le démuxeur
// dit du fichier. Il le disait déjà, dans la console : autant le dire à
// personne. L'activité Discord n'a pas de console, et une console ne se compte
// pas. Mesuré à l'audit du 10/09 : `noterMkv` n'écrivait QUE là.
//
// Ce qui est compté doit l'être en UNE requête Loki, donc dans un vocabulaire
// FERMÉ : `chemin` et `raison` ne prennent que les valeurs ci-dessous, et tout
// couple hors vocabulaire est REJETÉ — pas posté. Le texte libre du démuxeur ne
// franchit jamais cette porte ; `lireLeDemuxeur` est la traduction.
//
// Ce que les raisons disent :
//   · `tout`                la page lit l'image et le son (`direct`, `mse`) ;
//   · `video-seule`         seule l'image passe — tant que le lecteur n'a qu'un
//                           SourceBuffer, on rend la main au serveur ;
//   · `audio-seul`          seul le son passe ;
//   · `rien`                ni l'un ni l'autre, ou aucune piste, ou pas de
//                           MediaSource du tout ;
//   · `piste-inconnue`      le démuxeur rend une piste qu'il ne sait pas
//                           nommer (TrueHD, XviD…) ;
//   · `conteneur-illisible` le fichier n'est pas un Matroska lisible ;
//   · `chunk-absent`        le morceau du démuxeur (`morceaux/mkv-*.js`, chargé
//                           par `import()`) n'est pas venu — ou son premier
//                           fragment ;
//   · `direct-refuse`       la balise a refusé, en erreur 4 (format non pris
//                           en charge), le flux qu'on lui donnait : l'escalier
//                           descend d'un cran sur la MÊME source, et `chemin`
//                           dit lequel (voir « L'ESCALIER » plus bas). Sur
//                           `mse`, c'est le relais direct d'un .mp4 qui a été
//                           refusé ; sur `remux`, le cran du dessus — le
//                           direct sans MediaSource, ou (rare) le flux du
//                           démuxeur. Une source reposée sous son plancher
//                           (autre piste, saut hors tampon) la porte aussi.
//
// Plafond de volume : au plus une ligne par (chemin, raison) par lecture. Une
// lecture commence à `poserSource` — un changement de langue ou de piste
// audio en est une nouvelle, puisque le choix se refait. Une DESCENTE
// d'escalier n'en est pas une : ses lignes s'ajoutent à celles de la lecture
// qu'elle sauve, et Loki lit l'escalier entier sous une seule trace.
// ---------------------------------------------------------------------------

/**
 * La marche MSE est-elle possible sur CETTE machine ?
 *
 * Deux portes, pas une : Safari iOS n'expose pas `MediaSource` mais
 * `ManagedMediaSource` (17.1+), qui fait le meme travail — voir « QUELLE MEDIA
 * SOURCE » dans mkv.js, qui choisit l'implementation. Tester la seule premiere
 * enjambait le cran sur iPhone.
 */
export function mseDisponible() {
  return typeof ManagedMediaSource !== 'undefined' || typeof MediaSource !== 'undefined';
}

/**
 * Combien de temps une source posée a pour livrer ses métadonnées avant que
 * l'escalier descende. Mesuré sur la prod (Loki, 24-25/10) : `play-now` rend
 * en 1 à 4 s, le relais sert son premier octet en 0,2-0,4 s, et une lecture
 * qui marche reçoit ses métadonnées dans la foulée. Quinze secondes sans rien,
 * c'est un fichier que cette machine ne lira pas par ce chemin. Au remux (le
 * dernier cran) le serveur doit d'abord lancer ffmpeg : on lui laisse le triple.
 */
export const DELAI_DEMARRAGE_MS = 15_000;

/** Les seuls chemins que le journal accepte. */
export const CHEMINS = Object.freeze(['direct', 'mse', 'remux']);
/** Les seules raisons que le journal accepte. */
export const RAISONS = Object.freeze([
  'tout', 'video-seule', 'audio-seul', 'rien', 'piste-inconnue', 'conteneur-illisible', 'chunk-absent',
  'direct-refuse',
  // ⚠️ LA LECTURE EST PARFAITE ET IL N'Y A PAS DE SON. Un .mp4 dont seule la
  // piste audio est illisible (E-AC-3, DTS, AC-3 : Chrome les refuse pour des
  // raisons de licence) n'est pas refusé par la balise — elle joue l'image et
  // ignore la piste, sans lever la moindre erreur. `direct-refuse` ne pouvait
  // donc pas décrire ce cas : rien n'avait été refusé. C'est le compteur
  // d'octets audio décodés qui tranche (voir `son-sorti.js`), et c'est ce
  // chiffre, dans Loki, qui dira combien de fichiers sortaient muets.
  'son-muet',
  // ⚠️ RIEN N'EST REFUSÉ ET RIEN N'ARRIVE. Snow, 25/10 02:20 : un .mp4 2160p
  // Dolby Vision en relais direct, la balise lit l'en-tête puis se tait —
  // ni `loadedmetadata`, ni erreur 4. Le chargement tournait à l'infini, parce
  // que l'escalier ne descendait que sur un refus. Voir `guetterLeDemarrage`.
  'sans-metadonnees',
  // Le serveur a refuse la conversion : plafond atteint (conversions.js). Ce
  // n'est pas un defaut du fichier mais de la soiree — on change de SOURCE,
  // pas de cran, et la requete Loki compte combien de fois ca arrive.
  'conversions-saturees',
  // ⚠️ `rien` DISAIT DEUX CHOSES, ET L'UNE ÉTAIT FAUSSE.
  //
  // Un .mp4 posé au cran `remux` parce qu'on demande une AUTRE piste audio
  // (voir `cranPropose`) n'a rien à voir avec « la page ne sait rien lire » :
  // la page lisait très bien, c'est la bande-son demandée qui n'existe que par
  // le serveur. Les compter ensemble sous `rien`, c'est rendre la requête Loki
  // « combien de machines sont sans MediaSource » définitivement fausse le
  // jour où la préférence de langue se met à mordre — c'est-à-dire maintenant.
  'piste-audio',
]);

/**
 * La ligne de journal d'une décision de chemin — ou `null` si le couple sort
 * du vocabulaire.
 *
 * Pure. Le message est fixe (`chemin · raison`) : c'est lui qu'une requête
 * Loki filtre. Les codecs vont dans le détail, et toujours sous les DEUX clés,
 * à `null` quand on ne sait pas — un champ tantôt là, tantôt absent ne se
 * compte pas.
 */
export function ligneDeChemin({ chemin, raison, codecVideo = null, codecAudio = null } = {}) {
  if (!CHEMINS.includes(chemin) || !RAISONS.includes(raison)) return null;
  return {
    ou: 'chemin',
    message: `${chemin} · ${raison}`,
    detail: { chemin, raison, codecVideo: codecVideo || null, codecAudio: codecAudio || null },
  };
}

/**
 * Le capteur : poste par `journal` — le `tracer` de main.js, celui qui va sur
 * `/api/journal` — au plus une ligne par (chemin, raison) par lecture.
 *
 * @param {((ou: string, message: string, detail: any) => void)} [journal]
 *   absent, le capteur compte pour lui-même et ne poste rien.
 */
export function creerCapteurDeChemin(journal) {
  let vues = new Set();
  return {
    /** Une lecture commence : chaque couple retrouve son droit à une ligne. */
    nouvelleLecture() { vues = new Set(); },
    /** Rend vrai si une ligne est partie. */
    noter(quoi) {
      const ligne = ligneDeChemin(quoi ?? {});
      if (!ligne) return false;
      const cle = `${ligne.detail.chemin}/${ligne.detail.raison}`;
      if (vues.has(cle)) return false;
      vues.add(cle);
      journal?.(ligne.ou, ligne.message, ligne.detail);
      return true;
    },
  };
}

// Ce qui, dans une chaîne de codec, dit « du son ». Sert à ranger les codecs
// d'un type MIME quand il n'en porte qu'un : `mimeDe([video, audio])` les met
// dans cet ordre, mais « image seule » et « son seul » n'en donnent qu'un.
const CODECS_AUDIO = /^(mp4a|opus|flac|vorbis|ec-3|ac-3|mp3|dts|mlpa|alac|pcm)/i;
const estCodecAudio = (c) => CODECS_AUDIO.test(String(c ?? '').trim());

/** Les codecs d'un type MIME MSE (`video/mp4; codecs="a, b"`), rangés. */
function codecsDuMime(mime) {
  const m = /codecs="([^"]*)"/.exec(String(mime ?? ''));
  const liste = m ? m[1].split(',').map((c) => c.trim()).filter(Boolean) : [];
  if (liste.length >= 2) return { codecVideo: liste[0], codecAudio: liste[1] };
  if (liste.length === 1) {
    return estCodecAudio(liste[0])
      ? { codecVideo: null, codecAudio: liste[0] }
      : { codecVideo: liste[0], codecAudio: null };
  }
  return { codecVideo: null, codecAudio: null };
}

/**
 * Un mot du démuxeur qui veut dire « pas de codec » — voir `ceQuOnPeutLire`
 * (« aucune », « aucun ») et `ouvrirMkv` (« absente », « codec inconnu »,
 * « / sans chaine »).
 */
const sansCodec = (c) => {
  const t = String(c ?? '').replace(/\s*\/\s*sans chaine\s*$/, '').trim();
  return !t || /^(aucune?|absente?|codec inconnu)$/.test(t) ? null : t;
};

/**
 * Ce que le démuxeur dit, traduit dans le vocabulaire fermé du capteur.
 *
 * Pure — éprouvée par `essais/trace-chemin.mjs`, qui vérifie AUSSI que les
 * phrases attendues sont toujours celles de `mkv.js` : ce module n'est pas
 * importé ici (il pèse un mégaoctet, voir `poserSource`), la traduction tient
 * donc à ses mots. Tout ce qui n'est pas une DÉCISION de chemin — un saut, une
 * purge de tampon, une panne en cours de lecture — rend `null` : la console
 * l'aura, le journal non.
 *
 * @returns {{chemin: string, raison: string, codecVideo?: string|null, codecAudio?: string|null}|null}
 */
export function lireLeDemuxeur(message, detail) {
  const m = String(message ?? '').trim();
  const d = String(detail ?? '');
  if (m === 'codecs acceptes') return { chemin: 'mse', raison: 'tout', ...codecsDuMime(d) };
  const refus = /^codecs refuses \((video-seule|audio-seul|rien)\)$/.exec(m);
  if (refus) {
    // `pourquoi` nomme la piste refusée entre parenthèses — voir `ceQuOnPeutLire`.
    const image = /image(?: refusee)? \(([^)]+)\)/.exec(d);
    const son = /(?:son(?: refuse)?|refuses ensemble) \(([^)]+)\)/.exec(d);
    return {
      chemin: 'remux', raison: refus[1],
      codecVideo: sansCodec(image?.[1]), codecAudio: sansCodec(son?.[1]),
    };
  }
  if (m === 'conteneur illisible') return { chemin: 'remux', raison: 'conteneur-illisible' };
  if (m === 'aucune piste exploitable') return { chemin: 'remux', raison: 'rien' };
  if (m === 'piste non reconnue par le demuxeur') return { chemin: 'remux', raison: 'piste-inconnue' };
  if (m === 'piste au codec inconnu — repli serveur') {
    // `image <codec> · son <codec>`, chacun pouvant être absent ou inconnu.
    const parts = /^image (.+?) · son (.+)$/.exec(d);
    return {
      chemin: 'remux', raison: 'piste-inconnue',
      codecVideo: sansCodec(parts?.[1]), codecAudio: sansCodec(parts?.[2]),
    };
  }
  if (m === 'production impossible — repli serveur') return { chemin: 'remux', raison: 'chunk-absent' };
  return null;
}

// ---------------------------------------------------------------------------
// L'ESCALIER : LA MÊME SOURCE, REJOUÉE AUTREMENT, AVANT D'EN CHERCHER UNE AUTRE
//
// Audit du 10/09 : un .mp4 était un cul-de-sac. `etat.direct` vient du serveur
// (`lisibleTelQuel`, stream.js), qui ne regarde que le type MIME et l'extension
// — .mp4, .m4v, .webm — et jamais un codec. Un .mp4 en HEVC sur une machine
// sans décodeur, en Dolby Vision, en E-AC3 ou en DTS était donc « lisible tel
// quel » sur la foi de trois lettres, partait en relais direct dans la balise,
// et n'avait AUCUN repli : la balise levait l'erreur 4 (format non pris en
// charge), et `main.js` re-résolvait une AUTRE source — le même fichier, neuf
// fois sur dix, ou un autre aux mêmes codecs — au lieu de rejouer celle-ci
// autrement. Un .mkv, lui, avait deux chemins de secours depuis longtemps.
//
// L'escalier rend ces chemins au .mp4, côté client, sur la MÊME référence :
//
//   cran `direct`  la balise lit le relais tel quel — gratuit, avec son index ;
//   cran `mse`     le démuxeur de la page (mkv.js, qui lit aussi le MP4) rejoue
//                  la même ref en `?brut=1`, à la même seconde ;
//   cran `remux`   le serveur ré-emballe — et pour un fichier qu'il croit
//                  lisible tel quel, on doit le lui dire (`remux=1`) ;
//   épuisé         « source indisponible » — PAS de quatrième tentative, et
//                  pas de re-résolution pour ce motif-là : une autre source
//                  aux mêmes codecs ne changerait rien. La re-résolution reste
//                  ce qu'elle est pour le reste (réseau, lien mort), portée par
//                  `v.dataset.reprise` dans `brancherReprise` — l'escalier est
//                  un SECOND garde-fou, pas un contournement du premier.
//
// `direct` n'est donc plus un ordre, c'est un indice : on le croit — charger
// 894 Ko de démuxeur pour un fichier que la balise ouvre gratuitement serait
// absurde — mais on ne s'y tient pas. Et l'escalier se souvient de ce que la
// BALISE a refusé : la même source reposée (autre piste audio, saut hors
// tampon) repart sous ce cran-là, pas de l'indice démenti. Le verdict du
// démuxeur, lui, n'est pas retenu — il dépend de la piste audio demandée
// (E-AC3 refusé, AAC accepté) et se refait à chaque pose, comme il l'a
// toujours fait sur un .mkv. Une autre source repart du haut.
//
// Ce qui décide est pur, et éprouvé sans navigateur (`essais/repli-direct.mjs`).
// ---------------------------------------------------------------------------

/** Les crans, du plus léger au plus coûteux — l'ordre EST l'escalier. */
export const ESCALIER = Object.freeze(['direct', 'mse', 'remux']);

/** Le cran sous `cran`, ou `null` quand il n'y en a plus. Pure. */
export function cranSuivant(cran) {
  const i = ESCALIER.indexOf(cran);
  return i >= 0 && i + 1 < ESCALIER.length ? ESCALIER[i + 1] : null;
}

/**
 * LE SERVEUR VA-T-IL REMUXER CETTE POSE, OU RELAYER LE FICHIER TEL QUEL ?
 *
 * ⚠️ LE CHANGEMENT DE PISTE AU CRAN `direct` NE CHANGEAIT RIEN — ET LE FAISAIT
 * PAYER PLEIN TARIF.
 *
 * La règle valait `cran === 'remux'`, et elle mentait sur un cas exact : au
 * cran `direct`, `parLeServeur` posait `/api/play/<ref>?audio=2`. Or
 * `/api/play` (server.js) part en `relayerDirect` dès que `lisibleTelQuel` dit
 * oui, et le relais direct NE LIT JAMAIS `audio` : il renvoie les octets du
 * fichier, piste 0 comprise. Résultat mesuré côté Toji : on clique « Français »
 * sur un .mp4, la balise est vidée et reposée — 479 ms d'image gelée,
 * `essais/bascule-de-source.mjs` — et on réécoute EXACTEMENT la même bande-son.
 * Un geste sans effet, au prix fort, et rien nulle part ne le disait.
 *
 * Demander une autre piste que la piste 0 est donc, en soi, une demande de
 * remux : c'est le seul chemin par lequel le serveur sait servir une autre
 * bande-son (`-map 0:a:${rangServi}?`, stream.js). La décision vit ICI, pure,
 * parce que TROIS endroits doivent en tirer la même conclusion — le mot
 * `remux=1` envoyé au serveur, le `decalage` (le remux coupe à `pos`, le relais
 * direct non) et le replacement de la balise après une bascule de piste. Les
 * laisser deviner chacun de son côté, c'est le double compte de la seconde,
 * déjà payé une fois ici.
 *
 * Au cran `mse` le serveur ne sert que du brut (`?brut=1`) : il ne remuxe rien,
 * c'est le démuxeur de la page qui choisit la piste.
 */
export function serveurRemuxe({ cran, audio = 0 } = {}) {
  if (cran === 'remux') return true;
  if (cran === 'direct') return Number(audio) > 0;
  return false;
}

/**
 * LE CRAN PROPOSÉ POUR CETTE POSE — ET IL DOIT DIRE LA VÉRITÉ.
 *
 * ⚠️ « SUR UN .mp4, CHANGER DE PISTE AUDIO RECHARGE TOUT. » Et surtout : après
 * la bascule, cliquer à 12:00 partait à 22:00.
 *
 * La règle valait `etat.direct ? 'direct' : …`, sans regarder la piste
 * demandée. `serveurRemuxe` avait déjà tranché que demander une piste autre
 * que la 0 EST une demande de remux — le mot `remux=1` partait, le serveur
 * coupait bien à `pos`, `etat.decalage` valait bien `pos`. Mais l'ESCALIER,
 * lui, restait au cran `direct`. Et le cran n'est pas décoratif : trois
 * décisions le lisent.
 *
 *   · `choisirLeDeplacement` — au cran `direct` elle répond « natif, va à
 *     `cible` » et IGNORE le décalage, puisqu'un relais direct n'en a pas.
 *     Sur un flux remuxé à partir de 600 s, poser `currentTime = 720` mène à
 *     1320 s dans l'œuvre : c'est le saut que Toji voyait, et il ne venait pas
 *     de la bascule mais du PREMIER déplacement qui la suivait ;
 *   · le capteur de chemin — Loki comptait ces lectures en `direct`, donc le
 *     partage direct / mse / remux était faux d'autant ;
 *   · `guetterLeSilence`, qui croit pouvoir descendre d'un cran alors qu'on
 *     est déjà au dernier.
 *
 * Corriger chacune dans son coin, c'est le double compte de la seconde une
 * troisième fois. La vérité tient donc à UN endroit : le cran.
 *
 * ⚠️ POURQUOI `remux` ET PAS `mse` QUAND ON DEMANDE UNE AUTRE PISTE D'UN .mp4.
 * Le démuxeur de la page sait choisir une piste (`ouvrirMkv`, champ `audio`)
 * et ce serait séduisant — sauf qu'il ne le sait que s'il DÉCODE cette
 * piste-là. Sur le fichier mesuré (Rick et Morty S9E1) les deux bandes-son
 * sont en E-AC-3, que `audioRecopiable` refuse : `ouvrirMkv` rendrait la main
 * (`ceder`) et l'on paierait DEUX poses au lieu d'une, plus 894 Ko de
 * démuxeur, pour finir au remux quand même. Le serveur, lui, est le seul
 * chemin MESURÉ qui sert une autre bande-son sur ce conteneur-là.
 *
 * @param {boolean} direct ce que le serveur dit du fichier (`lisibleTelQuel`).
 * @param {number} [audio] le rang de piste audio demandé.
 * @returns {'direct'|'mse'|'remux'}
 */
export function cranPropose(direct, audio = 0) {
  if (!direct) return mseDisponible() ? 'mse' : 'remux';
  return Number(audio) > 0 ? 'remux' : 'direct';
}

// ---------------------------------------------------------------------------
// « VO » NE VEUT PAS DIRE « JAPONAIS » — ÇA DÉPEND DE CE QU'ON REGARDE
//
// ⚠️ LA PRÉFÉRENCE NE CHOISISSAIT QUE LE FICHIER, JAMAIS LA PISTE.
//
// Toji, 14/09 : « pour les animés c'est du japonais, pour les séries de
// l'anglais, pour les films de l'anglais ». Et le symptôme, sur le fichier
// mesuré (Rick et Morty S9E1, Comet 1080p TeamFR) :
//
//   0 hevc video · 1 eac3 audio fre VFF · 2 eac3 audio eng
//
// La piste 0 du conteneur est française : on ouvrait donc en français, une
// série, avec « VO » réglé, alors que l'anglais était juste à côté.
//
// La cause tenait en une ligne : `MARQUES.vo` valait `/^(ja|jpn|jp)$/i`, en
// dur. `rangDeLangue('vo')` répondait -1 sur ce fichier — aucune piste
// japonaise —, le filet renonçait, et rien nulle part ne disait pourquoi.
//
// ⚠️ ET LE CODE SE CONTREDISAIT DÉJÀ. La lecture INVERSE, elle, existe depuis
// toujours et dit le contraire : `/^(fr|fre|fra)$/i.test(p.langue) ? 'vf' :
// 'vo'` — pour elle, `eng` EST de la VO. `voix-menu.js` dit la même chose au
// spectateur (`motDeVersion` : « Japonais » sur un animé, « Anglais »
// ailleurs). Seule la recherche du rang l'ignorait. On rend donc la
// symétrie, et le TYPE de l'œuvre est ce qui tranche — exactement comme dans
// le menu qu'on lui montre.
//
// L'ordre, et pourquoi il y a un second puis un dernier recours :
//   1. la langue attendue pour ce type — japonais sur un animé, anglais
//      ailleurs (série, film, type inconnu) ;
//   2. l'autre des deux — une série japonaise mal étiquetée, un animé qui
//      n'a qu'un doublage anglais : mieux vaut cette piste-là que la VF qu'on
//      n'a pas demandée ;
//   3. à défaut, la première piste qui n'est PAS française — c'est
//      littéralement ce que la lecture inverse appelle déjà « vo ».
//
// Une piste sans code de langue n'est jamais choisie par le dernier recours :
// « non étiquetée » n'est pas « pas française », et se tromper ici coûterait
// une réouverture de flux pour rien. Aucune piste ne convient ⇒ -1, et le
// filet ne bascule pas : c'est une réponse, pas un silence.
//
// Pure, donc éprouvée sans navigateur (`essais/piste-suit-la-preference.mjs`).
// ---------------------------------------------------------------------------

/** Les codes qu'un conteneur écrit à sa guise, par langue. */
const FRANCAIS = /^(fr|fre|fra)$/i;
const JAPONAIS = /^(ja|jpn|jp)$/i;
const ANGLAIS = /^(en|eng)$/i;

/**
 * Le rang de la piste qui porte la langue voulue, ou -1.
 *
 * @param {object} o
 * @param {Array<{langue?: string}>} o.pistes les pistes du fichier COURANT.
 * @param {'vf'|'vo'|string|null} o.vise la préférence, dans le vocabulaire du
 *   réglage — ou une langue PRÉCISE (`ja`, `en`, `ko`…, clé de `LANGUES_ST`) :
 *   Toji (23/10) « les animés, c'est tout en japonais ; les séries, tout le
 *   temps en anglais ». Elle ne vise que sa langue : absente ⇒ -1.
 * @param {'anime'|string|null} [o.typeOeuvre] ce qui donne son sens à « vo ».
 */
export function rangDeLaLangueVoulue({ pistes = [], vise = null, typeOeuvre = null } = {}) {
  const liste = Array.isArray(pistes) ? pistes : [];
  const code = (p) => String(p?.langue ?? '').trim();
  const rang = (motif) => liste.findIndex((p) => motif.test(code(p)));
  if (vise === 'vf') return rang(FRANCAIS);
  if (vise !== 'vo') {
    const voulue = langueDuCode(vise);
    return voulue ? liste.findIndex((p) => langueDuCode(code(p)) === voulue) : -1;
  }
  const attendue = typeOeuvre === 'anime' ? JAPONAIS : ANGLAIS;
  const autre = typeOeuvre === 'anime' ? ANGLAIS : JAPONAIS;
  const premier = rang(attendue);
  if (premier >= 0) return premier;
  const second = rang(autre);
  if (second >= 0) return second;
  return liste.findIndex((p) => code(p) !== '' && !FRANCAIS.test(code(p)));
}

/**
 * La préférence reçue, ramenée au vocabulaire du lecteur — ou `null`.
 *
 * ⚠️ `MARQUES[vise].test` LEVAIT SUR TOUT LE RESTE, et l'exception tombait au
 * milieu de `chargerAudio` : le menu « Voix » restait désactivé pour tout
 * l'épisode. La garde d'`installer` n'acceptait donc QUE 'vf' et 'vo'.
 *
 * On garde cette fermeture — c'est elle qui protège — mais on traduit d'abord
 * les orthographes qui disent la même chose et qui, aujourd'hui, tombent
 * silencieusement à `null` : la clé `langue` du compte parle encore `vostfr`
 * (voir `LANGUE_DU_GOUT`, gouts.js), et un réglage par type peut arriver en
 * `fr`. Traduire coûte une ligne ; ne pas traduire coûte une préférence qui
 * ne mord jamais, et personne pour le dire.
 *
 * Une langue précise reconnue (`ja`, `jpn`, `en`…) revient en sa clé de
 * `LANGUES_ST` : le français reste « vf », c'est le même choix.
 *
 * @returns {'vf'|'vo'|string|null}
 */
export function langueVoulueLue(valeur) {
  const v = String(valeur ?? '').trim().toLowerCase();
  if (v === 'vf' || v === 'vo') return v;
  if (FRANCAIS.test(v) || v === 'vff' || v === 'vfq') return 'vf';
  if (v === 'vost' || v === 'vostfr') return 'vo';
  return langueDuCode(v)?.cle ?? null;
}

// ---------------------------------------------------------------------------
// SE DÉPLACER DANS LE FLUX — QUI SAIT SERVIR LA SECONDE DEMANDÉE
//
// ⚠️ « JE LANCE L'ANTENNE, J'AVANCE DE 10 SECONDES, ÉCRAN NOIR, JE PEUX PLUS
// RIEN FAIRE. » (Toji, 14/09)
//
// La décision tenait dans `escalier.cran() === 'direct' || demuxeur`, et c'est
// le `|| demuxeur` qui mentait — pas parce qu'un démuxeur ne sait pas se
// déplacer (il sait : voir `doitReproduire`, mkv.js), mais parce que cette
// variable était TOUJOURS NULLE. `ouvrirMkv` n'attendait pas que son montage
// tienne, il attendait la fin de l'écoulement — donc la fin de l'épisode
// (mesuré : 66 s de lecture, promesse toujours en attente). C'est corrigé dans
// `mkv.js`, mais la leçon reste : la bonne question n'est pas « ai-je un objet
// démuxeur en main », c'est « QUI peut servir cette seconde ».
//
//   · cran `direct` — le serveur relaie le fichier tel quel, la balise a
//     l'index de tout : elle se déplace seule, où qu'on aille ;
//   · cran `mse` — le flux est produit DANS la page. La balise saute où elle
//     veut et le démuxeur se replace derrière elle sur `seeking`. On ne
//     redemande donc JAMAIS la source pour un déplacement à ce cran-là — y
//     compris pendant l'ouverture, où `demuxeur` est encore nul : reposer
//     alors relançait une seconde ouverture par-dessus la première, laissait
//     la première tourner en orpheline (mesuré : « SourceBuffer has been
//     removed from the parent media source », donc un cri de panne à chaque
//     saut), vidait la balise — écran noir — et repartait du DÉBUT du fichier,
//     puisque la seconde voulue n'était appliquée que dans un `.then` qui ne
//     venait jamais ;
//   · cran `remux` — seul le serveur sait produire une seconde qu'on n'a pas :
//     là, et là seulement, on repose la source. Mais pas pour rien : ce qui
//     est DÉJÀ dans le tampon se rejoue sans rien redemander (Toji, avant :
//     « quand on passe de 10 en 10, ça met énormément de temps à charger »).
//
// Pure, donc éprouvée sans navigateur : `essais/deplacement-dans-le-flux.mjs`.
// ---------------------------------------------------------------------------

/**
 * La seconde `seconde` est-elle déjà chargée dans la balise ?
 *
 * On garde une marge d'une demi-seconde de chaque côté : viser le bord exact
 * d'une plage tamponnée fait repartir la lecture en bufferisation, ce qui
 * annule le bénéfice.
 *
 * @param {TimeRanges|Array<[number, number]>} plages `video.buffered`, ou une
 *   liste de paires `[début, fin]` — c'est ce qui rend la règle éprouvable.
 * @param {number} seconde
 */
export function dansLeTampon(plages, seconde) {
  const debut = (i) => (plages.start ? plages.start(i) : plages[i][0]);
  const fin = (i) => (plages.end ? plages.end(i) : plages[i][1]);
  for (let i = 0; i < (plages?.length || 0); i += 1) {
    if (seconde >= debut(i) + 0.5 && seconde <= fin(i) - 0.5) return true;
  }
  return false;
}

/**
 * Que faire du déplacement demandé — la décision, sans la balise.
 *
 * @param {object} o
 * @param {string|null} o.cran le cran de l'escalier (`direct`, `mse`, `remux`).
 * @param {boolean} [o.demuxeur] un démuxeur est-il en main ? Il ne sert que de
 *   FILET : au cran `mse` la réponse est la même avec ou sans lui, et c'est
 *   volontaire (voir le bloc ci-dessus).
 * @param {TimeRanges|Array<[number, number]>} [o.tampon] ce que la balise a déjà.
 * @param {number} [o.decalage] ce que le remux a sauté côté serveur : la balise
 *   repart de zéro, la seconde locale vaut donc `cible - decalage`.
 * @param {number} o.cible la seconde demandée, en temps de l'ŒUVRE.
 * @returns {{quoi: 'natif'|'tampon'|'reposer', seconde: number}} `natif` et
 *   `tampon` se posent tous deux sur `video.currentTime` — ils ne diffèrent que
 *   par la raison, et par le temps employé (œuvre pour l'un, balise pour
 *   l'autre). `reposer` redemande la source à `seconde`.
 */
export function choisirLeDeplacement({ cran, demuxeur = false, tampon = [], decalage = 0, cible }) {
  // Le fichier entier est indexé dans la balise : elle se débrouille.
  if (cran === 'direct') return { quoi: 'natif', seconde: cible };
  // Le flux est produit dans la page : la balise saute, le démuxeur suit.
  if (cran === 'mse' || demuxeur) return { quoi: 'natif', seconde: cible };
  // Reste le remux. Le décalage n'a de sens que là — voir `parLeServeur`.
  const local = cible - decalage;
  if (local >= 0 && dansLeTampon(tampon, local)) return { quoi: 'tampon', seconde: local };
  return { quoi: 'reposer', seconde: cible };
}

// ---------------------------------------------------------------------------
// GELER L'IMAGE PENDANT QU'ON CHANGE DE FICHIER
//
// ⚠️ « POURQUOI IL Y A DES ÉCRANS NOIRS QUAND ON CHANGE DE LANGUE ? » ET
// « POURQUOI, QUAND ON CHANGE DE LIEN, ÇA MET UNE ÉTERNITÉ À SWITCH ? »
// (Toji, 14/09) — ce sont les deux moitiés du MÊME défaut.
//
// Changer de fichier passe par `ouvrirUneSource`, donc par `poserSource`, donc
// par `viderLaBalise` : `removeAttribute('src')` puis `load()`. C'est le seul
// geste qui purge vraiment le tampon (voir `viderLaBalise`, et le symptôme qui
// l'a écrit), et il met le cadre au NOIR dans la frame qui suit. La nouvelle
// source, elle, repart intégralement de zéro — requête, démuxage ou remux,
// tampon vide. Mesuré par `essais/bascule-de-source.mjs` dans un vrai Chrome,
// sur un lien à 450 ms de latence et 3 Mo/s — la vitesse d'un lien débridé
// ordinaire — le 14/09 :
//
//   bascule de langue (cran direct) : 479 ms sans image, 480 ms découverts
//   changement de lien (démuxeur)   : 3 309 ms sans image, 3 280 ms découverts
//   au-dessus du centre du cadre, dans les deux cas : #video — la balise VIDE
//
// « Découvert » est la mesure qui compte : ce n'est pas l'attente qui est le
// défaut — rouvrir un autre fichier prend le temps qu'il prend, et personne ne
// peut avoir ces octets d'avance —, c'est de la passer devant du noir. D'où la
// réponse à la seconde question : ça ne met pas « une éternité » à basculer,
// ça met trois secondes SANS RIEN DIRE, et trois secondes de noir muet se
// vivent comme une éternité.
//
// Rien ne couvrait ce trou :
//   · `voiler()` a été vidé de sa substance (voir son corps) parce qu'il
//     revenait par-dessus l'image EN PLEINE LECTURE, à chaque `waiting` ;
//   · l'écran d'attente de `main.js` n'est posé qu'à l'OUVERTURE d'un épisode,
//     et `changerLangue` l'enjambe précisément sur son chemin RAPIDE — celui
//     où la langue a été préparée d'avance, donc le cas courant ;
//   · `choisirSource` (« Changement de source ») ne l'a jamais posé du tout.
//
// La réouverture, elle, est structurelle : un autre fichier, c'est d'autres
// octets, et personne ne peut les avoir d'avance. Ce qui n'est pas
// structurel, c'est de regarder du noir pendant ce temps. On garde donc la
// DERNIÈRE IMAGE — une copie dans un canvas, posée par-dessus la balise vidée
// — et on ne la retire qu'une fois la nouvelle source réellement peinte.
//
// ⚠️ ET CE N'EST PAS LE VOILE QUI REVIENT. Le voile couvrait l'image d'un
// aplat noir pendant la lecture, sur un évènement (`waiting`) que la balise
// émet dès que son tampon se vide. Le gel ne part QUE d'un geste explicite qui
// rouvre un fichier, il montre l'image au lieu de la cacher, et il ne peut pas
// survivre à la reprise (voir `degeler`).
// ---------------------------------------------------------------------------

/**
 * Y a-t-il quelque chose à garder à l'écran ?
 *
 * ⚠️ LA RÉPONSE FAUSSE SERAIT PIRE QUE LE DÉFAUT. Geler une balise qui n'a
 * rien peint, c'est recopier du noir et le poser par-dessus du noir : on aurait
 * exactement le même écran, plus un calque qui lui survit. D'où les trois
 * conditions, et pas une de moins :
 *   · `readyState >= 2` (HAVE_CURRENT_DATA) — le navigateur a une image
 *     courante ; en dessous il n'a rien, et `drawImage` rend un cadre vide ;
 *   · des dimensions non nulles — un flux audio seul, ou une balise vidée,
 *     n'ont pas d'image du tout, et `canvas.width = 0` lève à la lecture.
 *
 * Pure, et exportée, parce que c'est la seule partie de ce mécanisme qui puisse
 * se tromper en silence : le reste se voit à l'œil.
 *
 * @param {{readyState?: number, videoWidth?: number, videoHeight?: number}|null} balise
 */
export function imageGelable(balise) {
  if (!balise) return false;
  const { readyState = 0, videoWidth = 0, videoHeight = 0 } = balise;
  return readyState >= 2 && videoWidth > 0 && videoHeight > 0;
}

/**
 * L'escalier d'une source : où l'on en est, et où descendre.
 *
 * @param {object} o
 * @param {() => (string|null)} o.refCourante la source qu'on regarde MAINTENANT
 *   — lue à chaque descente, jamais figée : une erreur tardive de la source
 *   précédente ne fait pas descendre la suivante.
 * @param {(cran: string) => boolean} [o.peutServir] un cran que cette machine
 *   ne peut pas tenir (`mse` sans MediaSource) est enjambé, pas essayé.
 */
export function creerEscalier({ refCourante, peutServir = () => true }) {
  let ref = null;
  let cran = null;
  // Le plancher : le cran juste sous le dernier refus de la BALISE pour `ref`,
  // ou `null` tant qu'elle n'a rien refusé. C'est la seule mémoire de
  // l'escalier — un verdict du démuxeur (`ceder`) n'y touche pas.
  let plancher = null;
  const rang = (c) => ESCALIER.indexOf(c);
  // Le cran servable sous `c`, ou `null` : ce que cette machine ne peut pas
  // tenir est enjambé.
  const dessous = (c) => {
    let vers = cranSuivant(c);
    while (vers && !peutServir(vers)) vers = cranSuivant(vers);
    return vers;
  };
  const glisser = (r) => {
    if (!r || r !== ref || r !== refCourante()) return null;
    const vers = dessous(cran);
    if (!vers) return null;
    const de = cran;
    cran = vers;
    return { de, vers };
  };
  return {
    /** Le cran courant, ou `null` avant toute pose. */
    cran: () => cran,
    /**
     * Une source est posée au cran `propose`. Rend le cran EFFECTIF — le
     * plancher quand c'est la même source et que `propose` est au-dessus :
     * la balise l'a déjà refusé là, l'indice a été démenti, on ne le recroit
     * pas. Une autre source repart du cran proposé, plancher effacé. Un cran
     * inconnu n'est pas posé (`null`).
     */
    poser(r, propose) {
      if (rang(propose) < 0) return null;
      if (r !== ref) { ref = r; plancher = null; }
      cran = (plancher !== null && rang(plancher) > rang(propose)) ? plancher : propose;
      return cran;
    },
    /**
     * La balise a refusé le format au cran courant : descend d'un cran, le
     * retient comme plancher, et rend `{ de, vers }` — ou `null` quand
     * l'escalier est épuisé, ou que `r` n'est pas (plus) la source qu'on
     * regarde. Épuisé reste épuisé : appeler encore ne rend jamais une
     * quatrième tentative.
     */
    descendre(r) {
      const marche = glisser(r);
      if (marche) plancher = marche.vers;
      return marche;
    },
    /**
     * Le cran courant rend la main sans que la balise ait rien refusé — le
     * démuxeur ne sait pas lire ce couple de codecs, ou son morceau n'est pas
     * venu. Même descente, mais RIEN n'est retenu : la prochaine pose de cette
     * source repassera par lui, et son verdict pourra changer avec la piste.
     */
    ceder(r) { return glisser(r); },
  };
}

/** « variante X indisponible, Y utilisée » — null quand rien à dire (signal absent). */
export function phraseVariante(v) {
  if (!v?.signal) return null;
  return `variante ${v.demandee ?? '?'} indisponible, ${v.id} utilisée`;
}

export function creerLecteur({ base, $, trace, jeton, journal, chargerDemuxeur = () => import('./mkv.js'), delaiDemarrage = DELAI_DEMARRAGE_MS }) {
  // `journal` est optionnel : ce fichier n'a par lui-même aucun accès à la
  // télémétrie centralisée. Quand l'appelant le fournit (voir `reclamerBornes`
  // plus bas), c'est une fonction du même gabarit que `tracer` dans main.js —
  // celle qui poste sur `/api/journal` — passée telle quelle.
  //
  // `chargerDemuxeur` est le chargement du démuxeur, injectable pour le banc
  // (`essais/repli-direct.mjs` en pose un de figuration). Le vrai `import()`
  // reste écrit ici, en clair : c'est lui qu'esbuild découpe en morceau
  // séparé (voir build.mjs) — une variable à sa place casserait le découpage.
  /**
   * Ajoute a une adresse ce qu'un en-tete porterait ailleurs.
   *
   * Une balise <video> ou <track> n'accepte aucun en-tete. Hors Discord le
   * cookie de session suffit, mais dans l'iframe il n'y en a pas : la session
   * vient du SDK. Sans jeton dans l'adresse, le flux repartait en 401 et le
   * navigateur ne pouvait dire que « format error ».
   */
  const avecAcces = (chemin) => {
    const params = [];
    const j = jeton?.();
    if (j) params.push('t=' + encodeURIComponent(j));
    const t = trace?.();
    if (t) params.push('tr=' + encodeURIComponent(t));
    if (!params.length) return chemin;
    return chemin + (chemin.includes('?') ? '&' : '?') + params.join('&');
  };
  const video = $('video');
  const racine = $('lecteur');
  if (!video || !racine) return null;

  const etat = {
    voisins: null, // { precedent, suivant } fournis a l'ouverture
    ref: null,
    audio: 0, // rang de la piste audio servie
    pistes: [], // pistes audio du fichier courant
    // Le verdict du démuxeur sur la piste audio DEMANDÉE : « ma machine lit ce
    // codec ». Un seul bit, transmis au serveur au cran remux (`son=recopiable`)
    // pour qu'il recopie l'AC-3/E-AC-3 au lieu de le réencoder — Edge et Safari
    // le lisent, Chrome non, et lui seul le sait. Remis à faux à chaque
    // nouvelle source et à chaque changement de piste.
    sonRecopiable: false,
    langue: null, // langue écoutée — nulle tant que les pistes ne l'ont pas dite
    // ⚠️ LA PRÉFÉRENCE VO/VF N'ATTEIGNAIT JAMAIS LA PISTE.
    //
    // Elle servait à choisir un FICHIER (`changerLangue`), jamais une piste
    // DANS le fichier ouvert : une release MULTI dont la piste 0 est japonaise
    // s'ouvrait en japonais même avec « VF » réglé, à chaque épisode.
    langueVoulue: null, // 'vf' | 'vo' — le réglage du spectateur, quand on le lui a passé
    rangImpose: false, // qui ouvre a tranché le rang : le filet ci-dessous se tait
    langueAppliquee: false, // le filet a déjà statué sur CETTE source — une fois, pas deux
    pisteChoisieALaMain: false, // quelqu'un a choisi sa piste : on ne la lui reprend pas
    changerLangue: null, // fourni par l'appelant : va chercher un autre fichier
    // Les rappels du menu « Voix » et du panneau de sources, fournis par
    // l'appelant comme `changerLangue` : le lecteur ne connaît aucune route.
    proprietaire: false, // le propriétaire seul voit « Changement de source »
    voixDe: null, // (ref) → les langues des AUTRES fichiers de l'épisode (/api/voix)
    sourcesDe: null, // (ref, frais) → toutes les sources de l'épisode, triées
    ouvrirSourceDe: null, // (url) → vérifie ce lien et rend de quoi l'ouvrir
    surSourceOuverte: null, // (avant, apres, seconde) → mesure l'écart entre les deux montages
    surSousTitres: null, // rappel chargé d'attacher les pistes de texte
    apercu: null, // planche de vignettes, quand elle est prête
    episode: null, // identifiant de l'épisode : la planche lui appartient
    direct: true,
    totale: 0, // duree reelle, donnee par le serveur
    decalage: 0, // point de depart du flux courant (mode remux)
    visee: null, // { t, at } : la seconde demandee par un deplacement, tant que la balise ne l'a pas rejointe
    saisie: false, // l'utilisateur tient la tete de lecture
    vitesse: 1, // vitesse VOULUE ; la seance, elle, ecrit `playbackRate` pour rattraper
  };

  const CLE_SON = '14d:son';

  /** Volume et coupure retenus d'une séance à l'autre. */
  function lireSon() {
    try {
      const brut = localStorage.getItem(CLE_SON);
      return brut ? JSON.parse(brut) : null;
    } catch {
      return null; // stockage refusé : on garde le réglage par défaut
    }
  }

  function retenirSon() {
    try {
      // `sourdineTechnique` est pose par le repli d'autoplay (`main.js`) :
      // ce n'est pas un choix de la personne, ca ne s'ecrit pas.
      localStorage.setItem(CLE_SON, JSON.stringify(sonARetenir({
        volume: video.volume,
        coupe: video.muted,
        technique: video.dataset.sourdineTechnique === '1',
      }, lireSon())));
    } catch { /* rien à retenir, le son reste correct pour la séance */ }
  }

  const el = (id) => $(id);
  const pose = (id, prop, valeur) => { const n = el(id); if (n) n[prop] = valeur; };

  // --- position et duree -----------------------------------------------------
  // ⚠️ LE TEMPS AFFICHÉ SUIT LA CIBLE, PAS LA BALISE, TANT QUE LE DÉPLACEMENT CHARGE.
  //
  // Pendant un saut, la balise passe par des valeurs qui ne sont pas la cible : `currentTime` à 0 le temps
  // que le flux remuxé arrive (le décalage n'étant pas encore compté), l'ancienne position, etc. Toji :
  // « je reviens de 10 secondes, ça me remet le tout début », « la barre bugue à l'endroit d'où ça revient ».
  // La cible est donc affichée tout de suite, et lâchée dès que la balise l'a rejointe (à 2,5 s près)
  // et peut jouer, ou au bout de 15 s pour ne jamais mentir durablement.
  const viseeEnCours = () => {
    const v = etat.visee;
    if (!v) return false;
    const reel = etat.decalage + (video.currentTime || 0);
    const prete = video.readyState >= 3 && !video.seeking;
    if (prete && (Math.abs(reel - v.t) < 2.5 || performance.now() - v.at > 15000)) { etat.visee = null; return false; }
    return true;
  };
  const position = () => {
    const p = etat.decalage + (video.currentTime || 0);
    // La durée du catalogue est approximative : sans borne, le compteur file
    // au-delà de la fin annoncée et la barre reste collée à droite.
    return etat.totale > 0 ? Math.min(p, etat.totale) : p;
  };
  /** Ce que l'on montre et ce dont part un recul : la cible tant qu'on charge, sinon la position réelle. */
  const positionVue = () => (viseeEnCours() ? etat.visee.t : position());
  const totale = () => (etat.totale > 0
    ? etat.totale
    : (Number.isFinite(video.duration) ? video.duration : 0));

  // --- barre de progression --------------------------------------------------
  function peindre() {
    const T = totale();
    racine.classList.toggle('cherche', viseeEnCours());
    const p = T > 0 ? Math.min(100, (positionVue() / T) * 100) : 0;
    const joue = el('l-joue');
    const tete = el('l-tete');
    // Pendant un glisser, la tête suit la main : la lecture ne la ramène pas.
    if (joue && !etat.saisie) joue.style.width = `${p}%`;
    if (tete && !etat.saisie) tete.style.left = `${p}%`;

    // ⚠️ LA PLAGE QUI CONTIENT LA TETE, PAS LA DERNIERE QUI COMMENCE AVANT.
    //
    // Toji : « je vois pas la deuxième barre chargée ». La condition retenait
    // toute plage COMMENCANT avant la tete — donc, apres un saut en arriere, une
    // vieille plage deja depassee, dont la fin est DERRIERE la position : la
    // barre de tampon se retrouvait plus courte que la barre de lecture, donc
    // entierement cachee dessous. Ce qu'on veut est la plage ou l'on se trouve,
    // et a defaut la plus avancee.
    //
    // Le tampon se compte depuis le decalage : en mode remux le flux commence a
    // zero alors que la lecture est peut-etre a la vingtieme minute.
    let fin = 0;
    for (let i = 0; i < video.buffered.length; i += 1) {
      const d0 = video.buffered.start(i);
      const f0 = video.buffered.end(i);
      if (d0 <= video.currentTime && f0 >= video.currentTime) { fin = f0; break; }
      if (f0 > fin) fin = f0;
    }
    const tampon = el('l-tampon');
    if (tampon) tampon.style.width = `${T > 0 ? Math.min(100, ((etat.decalage + fin) / T) * 100) : 0}%`;

    const piste = el('l-piste');
    if (piste) piste.setAttribute('aria-valuenow', String(Math.round(p)));

    pose('l-t', 'textContent', horloge(positionVue()));
    pose('l-d', 'textContent', T > 0 ? horloge(T) : '-:--:--');
    majPositionMedia(T);
  }

  // --- amorcage d'une lecture ------------------------------------------------
  //
  // Le symptome, tel qu'on le vit : le voile de chargement tourne sur une image
  // figee, et une pause suivie d'un play debloque tout. Autrement dit la
  // lecture n'a jamais demarre — et personne ne l'a su, parce que la promesse
  // de `video.play()` etait rejetee dans un `catch` vide, aux cinq endroits qui
  // l'appelaient.
  //
  // Trois causes possibles, et elles ne se soignent pas pareil :
  //
  //   AbortError       un autre chargement a pris la main entre-temps. Ce n'est
  //                    pas une panne, et il ne faut surtout pas relancer : le
  //                    nouveau chargement s'en charge.
  //   NotAllowedError  le navigateur refuse de jouer sans geste de la personne.
  //                    Retenter ne changera rien ; il faut le dire.
  //   le reste         la source n'est pas encore prete. On retente quand elle
  //                    s'annonce, et un chien de garde reprend la main si elle
  //                    ne s'annonce jamais.
  //
  // Dans tous les cas le voile finit par se lever : un voile qui tourne pour
  // toujours ne dit rien de ce qui se passe, et c'est ce qui rendait la panne
  // indechiffrable.

  const DELAI_RELANCE = 3_500; // avant de reprendre la main sur une lecture qui ne part pas
  const ESSAIS_AMORCE = 3;

  let amorce = null;
  // Posé par la relance du chien de garde, lu (et remis à faux) par l'écouteur
  // `play`. Seulement si la balise est en pause : sinon aucun `play` ne suivra
  // et le drapeau mangerait le prochain vrai.
  let relanceAmorce = false;
  // LE JETON DE LA SÉANCE DE LECTURE : vrai dès qu'on quitte l'écran
  // (`mettreDeCote`, `preparerEnFond`), faux seulement quand on y revient ou
  // qu'on ouvre une source pour la montrer. Tout ce qui relance la balise de
  // soi-même — l'amorce, la séance (`estHorsEcran`) — le lit avant de jouer.
  let horsEcran = false;

  function annulerAmorce() {
    if (!amorce) return;
    clearTimeout(amorce.garde);
    amorce = null;
  }

  /**
   * Lance la lecture et s'assure qu'elle part vraiment.
   * @param {Function} [apres] joue une fois la lecture effectivement partie.
   */
  function amorcer(apres = null) {
    annulerAmorce();
    // ⚠️ HORS DE L'ÉCRAN, PERSONNE NE RELANCE (bug SEANCE-2610222121-pws).
    // Des `amorcer()` partent en différé — le démuxeur qui réamorce une fois sa
    // source posée, une descente d'escalier — et retombaient après la sortie :
    // la balise mise de côté repartait toute seule derrière la liste.
    if (horsEcran) return;
    // `vu` est un RELEVÉ, pas un point de départ. Poser une source remet
    // `currentTime` à zéro — mais sur le chemin MSE elle est posée APRÈS cet
    // appel, et le relevé valait alors la position de l'épisode précédent :
    // une vingtième minute qu'on ne dépasserait jamais. Le chien de garde
    // concluait donc à l'échec sur une vidéo qui jouait, et annonçait « la
    // source ne démarre pas ». Deux relevés successifs ne dépendent d'aucune
    // base.
    const mien = { garde: 0, essais: 0, vu: video.currentTime };
    amorce = mien;

    const tenter = () => {
      if (amorce !== mien) return;
      mien.essais += 1;
      const promesse = video.play();
      if (!promesse?.catch) return;
      promesse.then(
        () => { if (amorce === mien) apres?.(); },
        (err) => {
          // Un `play()` refusé n'émet pas `play` : le drapeau d'une relance
          // resterait levé et mangerait l'éclair du prochain vrai geste.
          relanceAmorce = false;
          if (amorce !== mien) return;
          // Un chargement plus recent a pris la main : le laisser faire.
          if (err?.name === 'AbortError') { annulerAmorce(); return; }
          if (err?.name === 'NotAllowedError') {
            devoiler();
            demanderUnGeste();
            annulerAmorce();
            return;
          }
          console.warn('[lecteur] lecture refusee :', err?.name, err?.message);
        },
      );
    };

    const veiller = () => {
      if (amorce !== mien) return;
      // La seule preuve qui vaille : le temps avance. `paused` peut mentir le
      // temps d'un tampon, `readyState` aussi.
      const ou = video.currentTime;
      if (!video.paused && ou > mien.vu + 0.05) { annulerAmorce(); return; }
      mien.vu = ou;
      if (mien.essais < ESSAIS_AMORCE) {
        relanceAmorce = video.paused;
        tenter();
        mien.garde = setTimeout(veiller, DELAI_RELANCE);
        return;
      }
      devoiler();
      // ⚠️ CE MESSAGE PEUT ÊTRE DÉMENTI, et il faut alors qu'il s'efface.
      //
      // L'amorce renonce après quelques tentatives ; sur un MKV que le
      // démuxeur ouvre lentement, la lecture démarre parfois APRÈS cet abandon.
      // Le message restait alors à l'écran pendant tout l'épisode : « la source
      // ne démarre pas » au-dessus d'une image qui joue. Une information fausse
      // est pire qu'absente — on revient en arrière pour rien.
      //
      // On le marque donc comme périmable : la première preuve de lecture
      // l'efface. Voir `oublierAbandon`.
      abandonAffiche = true;
      pose('l-infos', 'textContent', 'La source ne démarre pas — reviens en arrière et réessaie.');
      console.warn('[lecteur] abandon apres', mien.essais, 'tentatives de lecture');
      annulerAmorce();
    };

    tenter();
    mien.garde = setTimeout(veiller, DELAI_RELANCE);
  }

  // --- d'où vient l'image ----------------------------------------------------
  //
  // Deux chemins, et le second n'existe que depuis peu :
  //
  //   · le serveur convertit le fichier en MP4 et nous l'envoie. Ça marche
  //     partout, mais il n'y a ni durée ni index — donc pas de déplacement
  //     natif — et ffmpeg tourne pour chaque spectateur.
  //   · la page démuxe elle-même le fichier (voir mkv.js). Rien à convertir,
  //     la durée est connue tout de suite, le déplacement est instantané, et
  //     le serveur ne fait que relayer des octets.
  //
  // On tente le second, et on retombe sur le premier dès qu'il ne convient
  // pas : codecs que cette machine ne sait pas décoder, MediaSource absente,
  // ou n'importe quel accroc. Le repli n'est pas une panne — c'est le
  // fonctionnement normal sur une machine qui ne suit pas.
  let demuxeur = null;

  // Branché par la séance (voir `brancherSeance`). Hors séance il n'y a rien à
  // taire : on pose le geste tel quel.
  let sansEchoSeance = null;

  /**
   * Pose un geste qui vient du LECTEUR, et pas de la personne.
   *
   * La balise n'a qu'un `pause` pour les deux, et la séance ne peut pas les
   * distinguer toute seule : ce qu'elle voit ici, elle le renvoie au serveur —
   * en pause imposée à toute la salle quand on pilote, en vote de pause quand
   * on regarde. C'est donc à l'appelant de dire lequel des deux il est.
   */
  const sansEcho = (faire) => (sansEchoSeance ? sansEchoSeance(faire) : faire());

  function fermerDemuxeur() {
    if (!demuxeur) return;
    try { demuxeur.fermer(); } catch { /* déjà fermé */ }
    demuxeur = null;
  }

  /**
   * Vide la balise avant d'y poser autre chose.
   *
   * ⚠️ VIDER LA VIDÉO AVANT D'EN POSER UNE AUTRE.
   *
   * Fermer le démuxeur détache la source, mais la balise garde ce qu'elle a
   * déjà mis en mémoire tampon. Changer d'épisode en cours de lecture
   * laissait donc voir, pendant quelques secondes, des fragments de
   * l'épisode PRÉCÉDENT — puis l'image se remettait d'un coup sur le bon.
   * Constaté en séance : c'est déroutant, et sur un épisode qu'on n'a pas
   * encore vu, ça montre exactement ce qu'on ne voulait pas montrer.
   *
   * `removeAttribute` puis `load()` est le seul geste qui vide vraiment le
   * tampon : poser un nouveau `src` par-dessus ne le purge pas, il empile.
   * C'est aussi lui qui efface `video.error` : une balise en erreur 4 ne
   * repart sur un autre cran qu'après ça (voir `descendre`).
   *
   * ⚠️ ET C'EST LE SYSTÈME QUI LE FAIT, PAS LA PERSONNE.
   *
   * `pause()` sur une balise qui joue émet un `pause`, que la séance lisait
   * comme un clic : elle poussait une pause à toute la salle depuis qui
   * pilote, et ouvrait un vote de pause chez qui regarde — à chaque
   * déplacement en mode remux, à chaque changement de piste audio, à chaque
   * bascule de langue. Le vidage du tampon a créé ces trois cas : poser un
   * `src` par-dessus, ce qu'on faisait avant, laisse le navigateur remettre
   * l'élément en pause SANS rien émettre.
   */
  // Le guetteur de silence de la source courante (voir `guetterLeSilence`).
  let guetteur = null;

  function viderLaBalise() {
    // Le guetteur appartient à la source qu'on quitte : le laisser tourner le
    // ferait juger la suivante sur les compteurs de la précédente.
    guetteur?.arreter();
    guetteur = null;
    try {
      sansEcho(() => {
        video.pause();
        video.removeAttribute('src');
        video.load();
      });
    } catch { /* la balise n'avait rien à libérer */ }
  }

  // Le ticket de la pose en cours : chaque pose l'incrémente, et une réponse
  // asynchrone qui revient avec un ticket périmé ne touche plus à la balise.
  // Voir la garde du `.then` dans `parLeDemuxeur`.
  let poses = 0;

  /**
   * Met le fichier `ref` dans la balise, par le meilleur chemin disponible.
   * `pos` ne sert qu'au serveur : le démuxage local se déplace tout seul.
   */
  function poserSource(ref, pos = 0, audio = 0) {
    // Le tampon du suivant : lâché si on part ailleurs, gardé jusqu'à la
    // première image si c'est lui qu'on pose (la balise y puise encore).
    if (chaud && chaud.ref !== ref) lacherChaud();
    else if (chaud) video.addEventListener('playing', lacherChaud, { once: true });
    fermerDemuxeur();
    // Chaque source a droit à son propre cri : sans cette remise à zéro, une
    // panne sur l'épisode 4 rendrait muette celle de l'épisode 5. Et à sa
    // propre ligne de journal par (chemin, raison) — voir `creerCapteurDeChemin`.
    signalDePanne.nouvelleSource();
    capteur.nouvelleLecture();
    viderLaBalise();

    // ⚠️ `direct` EST UN INDICE, PAS UN ORDRE — voir « L'ESCALIER ».
    //
    // Un fichier que la balise sait déjà ouvrir n'a rien à gagner au démuxeur :
    // elle le lit nativement, avec son index et son déplacement. Le détour ne
    // vaut que pour ce qu'elle refuse — un Matroska, typiquement. Mais
    // `direct` ne dit que « .mp4 » : si la balise refuse quand même, l'escalier
    // descend (voir `descendre`), et la même source reposée repart sous le
    // cran refusé, pas du cran proposé.
    // ⚠️ SUR iPHONE, `MediaSource` N'EXISTE PAS — `ManagedMediaSource` OUI.
    // Tester la seule premiere enjambait le cran mse sur iOS, et `ouvrirMkv`
    // n'y etait jamais appele : un ffmpeg par personne, sans deplacement
    // natif. Trouve par la verification adversariale du 10/09 (lot K).
    // ⚠️ ET LA PISTE DEMANDÉE FAIT PARTIE DE LA QUESTION — voir `cranPropose`.
    // Un .mp4 sur lequel on demande la piste 2 n'est plus « lisible tel
    // quel » : le serveur va le remuxer, et le cran doit le dire à tout le
    // monde plutôt que de laisser trois endroits le redeviner.
    const propose = cranPropose(etat.direct, audio);
    const cran = escalier.poser(ref, propose);
    // Le capteur compte AUSSI ce qui ne passe pas par le démuxeur : sans ces
    // lignes, Loki ne verrait que les MKV, et le partage direct / mse / remux
    // serait faux. « rien » sans MediaSource : la page ne peut rien lire
    // elle-même, c'est la même conclusion qu'un couple de codecs refusé — sans
    // codecs dans le détail, ce qui les distingue. Un cran plus bas que
    // proposé, c'est l'escalier qui se souvient d'un refus : la raison est la
    // sienne. Le cran `mse`, lui, se compte par la voix du démuxeur.
    if (cran !== propose) capteur.noter({ chemin: cran, raison: 'direct-refuse' });
    else if (cran === 'direct') capteur.noter({ chemin: 'direct', raison: 'tout' });
    // Un remux DEMANDÉ (autre piste audio d'un fichier que la balise lisait
    // très bien) n'est pas un remux SUBI : `rien` mentirait sur la machine.
    else if (cran === 'remux' && Number(audio) > 0) capteur.noter({ chemin: 'remux', raison: 'piste-audio' });
    else if (cran === 'remux') capteur.noter({ chemin: 'remux', raison: 'rien' });
    poserAuCran(ref, cran, pos, audio);
    guetterLeSilence(cran);
    guetterLeDemarrage(cran);
  }

  /**
   * ⚠️ LE CAS QUE L'ESCALIER NE POUVAIT PAS VOIR : la lecture est parfaite et
   * il n'y a pas de son.
   *
   * Toji, 14/09 : « j'avais testé justement un fichier et on n'avait aucun
   * son. » Les trois crans s'enchaînent sur un REFUS de la balise ; un .mp4
   * dont seule la piste audio est illisible n'est pas refusé du tout. Chrome
   * joue l'image, ignore la piste qu'il ne décode pas, `error` reste nul. Rien,
   * nulle part, ne le disait — ni au spectateur, ni dans Loki.
   *
   * La règle vit dans `son-sorti.js`, sans DOM, et ne conclut que sur une
   * CERTITUDE : compteurs absents (Firefox, Safari), fichier sans piste audio,
   * ou lecture trop jeune, elle rend « inconnu » et on ne touche à rien.
   */
  /**
   * Une source qui ne livre JAMAIS ses métadonnées fait descendre l'escalier.
   *
   * L'escalier descendait sur un refus (erreur 4) ou un son muet. Une balise
   * qui lit l'en-tête puis se tait — décodeur absent pour un HEVC / Dolby
   * Vision 2160p sur un téléphone, mesuré le 25/10 — ne refuse rien : sans ce
   * guet, le chargement tournait sans fin et personne n'en savait rien.
   * `readyState` à 0 (HAVE_NOTHING) au bout du délai, sur la pose qu'on
   * regarde encore : on descend. Au dernier cran, on le dit.
   */
  let guetDemarrage = null;
  function guetterLeDemarrage(cran) {
    clearTimeout(guetDemarrage);
    const posee = poses;
    const dernier = cranSuivant(cran) === null;
    guetDemarrage = setTimeout(() => {
      // Une pose plus récente, des métadonnées reçues, une erreur (l'escalier
      // la traite déjà), un lecteur mis de côté ou préparé en fond, une balise
      // vidée : rien à faire. Reposer une source derrière un lecteur fermé
      // ferait parler une vidéo que personne ne regarde.
      if (posee !== poses || (Number(video.readyState) || 0) >= 1 || video.error || horsEcran || !(video.currentSrc || video.src)) return;
      capteur.noter({ chemin: cran, raison: 'sans-metadonnees' });
      // Au dernier cran : la panne de source ordinaire — `brancherReprise`
      // re-résout une fois, puis montre l'écran d'erreur (« Réessayer »).
      if (dernier) { signalerPanneSource(etat.ref, 'sans-metadonnees'); return; }
      annoncer('La vidéo ne démarre pas — on change de chemin');
      descendreDUnCran({ raison: 'sans-metadonnees' });
    }, dernier ? 3 * delaiDemarrage : delaiDemarrage);
  }

  function guetterLeSilence(cran) {
    // Au dernier cran, descendre n'est plus possible : le remux réencode déjà
    // en AAC, et un silence ici vient du serveur. On le NOTE — c'est tout ce
    // qu'on peut faire, et c'est déjà infiniment plus que rien.
    const dernier = cranSuivant(cran) === null;
    const posee = poses;
    // ⚠️ L'ORIGINE SE RELÈVE AU PREMIER TOUR, PAS À LA POSE.
    //
    // « Secondes jouées » ne peut pas se compter depuis `currentTime` brut : au
    // relais direct la balise est replacée à la seconde de reprise (720 s
    // dirait « 720 s jouées » dès la première image, et le guetteur conclurait
    // aussitôt), tandis qu'au remux le serveur coupe à la position demandée et
    // la balise repart de zéro. Les deux cas se règlent d'un coup en prenant
    // pour origine ce que la balise affiche au premier relevé — après que
    // `amorcer` l'a replacée.
    let origine = null;
    guetteur = guetterLeSon({
      relever: () => {
        const t = Number(video.currentTime) || 0;
        if (origine === null) origine = t;
        return releverDe(video, {
          depuis: origine,
          // Sans liste de pistes, on ne sait pas si le fichier a du son : la
          // règle rendra « inconnu », et c'est voulu.
          pisteAnnoncee: (etat.pistes?.length ?? 0) > 0,
        });
      },
      surMuet: (r) => {
        // Une pose plus récente a eu lieu : ce verdict porte sur un fichier
        // qu'on ne regarde plus.
        if (posee !== poses) return;
        capteur.noter({ chemin: cran, raison: 'son-muet' });
        console.warn('[lecteur] aucun son décodé :', r.pourquoi);
        // Au dernier cran il n'y a plus de chemin à essayer : le dire au
        // spectateur vaut mieux que le laisser chercher son volume.
        if (dernier) { annoncer('Ce fichier ne rend aucun son'); return; }
        annoncer('Aucun son — on change de chemin');
        descendreDUnCran({ raison: 'son-muet' });
      },
      planifier: (fn, ms) => setInterval(fn, ms),
      annuler: (id) => clearInterval(id),
    });
  }

  /**
   * Descend d'un cran sur la source courante, en gardant la seconde.
   *
   * ⚠️ `raison` EXISTE PARCE QUE TOUTES LES DESCENTES NE SE VALENT PAS. Elle
   * valait `direct-refuse` en dur, ce qui était vrai de la seule descente qui
   * existait alors — la balise avait refusé le flux. Le silence, lui, ne refuse
   * rien : la lecture est parfaite. Écrire `direct-refuse` sur ces descentes-là
   * aurait mélangé dans Loki deux pannes qui n'ont ni la même cause ni le même
   * remède, et on n'aurait jamais su laquelle on comptait.
   */
  function descendreDUnCran({ enFond = false, raison = 'direct-refuse' } = {}) {
    const ref = etat.ref;
    const marche = escalier.descendre(ref);
    if (!marche) return null;
    // La seconde AVANT de vider : `load()` remet `currentTime` à zéro, et
    // `position()` sait déjà rajouter le décalage d'un remux.
    const reprise = positionVue();
    fermerDemuxeur();
    signalDePanne.nouvelleSource();
    viderLaBalise();
    // La descente se compte, sur la lecture qu'elle sauve : `mse ·
    // direct-refuse`, puis la voix du démuxeur ; ou `remux · direct-refuse`.
    capteur.noter({ chemin: marche.vers, raison });
    if (!enFond) voiler();
    poserAuCran(ref, marche.vers, reprise, etat.audio);
    guetterLeSilence(marche.vers);
    guetterLeDemarrage(marche.vers);
    // Comme `choisirAudio` : le remux part d'ici ; le démuxeur, lui,
    // réamorce quand sa source est posée, et ce `play`-ci s'efface devant.
    if (!enFond) amorcer();
    return { ...marche, reprise };
  }

  /**
   * Pose `ref` par le chemin du cran `cran`. Ne compte rien : c'est à qui
   * choisit le cran de dire pourquoi (`poserSource`, `descendre`, le démuxeur).
   */
  function poserAuCran(ref, cran, pos, audio) {
    poses += 1;
    // La promesse appartenait à la barre d'AVANT : une autre source, une autre
    // durée, et l'endroit survolé ne veut plus dire la même chose.
    promesse = null;
    // Remis à zéro avant toute chose, pour que le décalage d'une source
    // précédente ne survive pas à celle-ci. Seul le remux le repose.
    etat.decalage = 0;
    decalerRenduRiche();
    if (cran === 'mse') parLeDemuxeur(ref, pos, audio);
    else parLeServeur(ref, pos, audio);
  }

  /**
   * Le serveur sert `ref` : relais direct au cran `direct`, remux au cran
   * `remux`. `pos` et `audio` ne valent que pour le remux — le relais direct
   * les ignore, il sert le fichier depuis le début (voir `relayerDirect`,
   * stream.js), et la balise s'y déplace elle-même (voir `choisirAudio`).
   */
  function parLeServeur(ref, pos = 0, audio = 0) {
    const q = [];
    // Au dixième : le serveur lit un nombre, et le décalage compté ici doit être celui qu'il a reçu.
    pos = Math.round(pos * 10) / 10;
    if (pos > 0) q.push(`pos=${pos}`);
    if (audio > 0) q.push(`audio=${audio}`);
    // ⚠️ PAS `escalier.cran() === 'remux'` — voir `serveurRemuxe`. Au cran
    // `direct`, demander une piste autre que la 0 EST une demande de remux :
    // le relais direct ignore `audio` et rejouait la même bande-son, après
    // avoir vidé la balise pour rien.
    const remux = serveurRemuxe({ cran: escalier.cran(), audio });
    // ⚠️ LE SERVEUR NE DOIT PAS CROIRE SON PROPRE INDICE.
    //
    // Sur un fichier qu'il juge lisible tel quel, `/api/play/<ref>` part en
    // relais direct — le cran `direct`, celui que la balise vient de refuser.
    // Rejouer les mêmes octets par le même chemin, c'est se faire refuser
    // exactement pareil : au cran `remux`, on le lui dit. (Côté serveur, ce
    // mot doit passer devant `lisibleTelQuel` dans `/api/play` — tant qu'il
    // n'y est pas, le cran `remux` d'un .mp4 rejoue le cran `direct`, et
    // l'escalier finit simplement un cran plus tôt.)
    if (remux && etat.direct) q.push('remux=1');
    // Le seul bit que le client transmet sur ses capacités : « ma machine lit
    // le codec de la piste demandée ». Le serveur recopie alors l'AC-3/E-AC-3
    // au lieu de le réencoder — voir `pisteAudioLaMoinsChere` (stream.js).
    if (remux && etat.sonRecopiable) q.push('son=recopiable');
    // ⚠️ LE DÉCALAGE SE POSE ICI, ET NULLE PART AILLEURS.
    //
    // Le serveur remuxe À PARTIR de `pos` : la balise repart donc de zéro, et
    // `position()` doit rajouter ce qu'on a sauté. C'est la seule branche où
    // ça vaut.
    //
    // Ses trois appelants le posaient eux-mêmes, en devinant la branche par
    // `etat.direct`. Or `direct` dit ce que le SERVEUR fait du fichier, pas
    // quel chemin sera pris : sur un MKV il vaut faux, mais c'est le démuxeur
    // local qui prend la main, et LUI se déplace nativement. Le décalage
    // était donc compté DEUX FOIS — cliquer à 12:00 affichait 24:00, et sur
    // un pilote toute la salle sautait à la fin de l'épisode.
    //
    // Ils ne peuvent pas le deviner : le choix de la branche est asynchrone,
    // il se fait après leur retour.
    //
    // Et il ne vaut QUE pour le remux : le relais direct ignore `pos` et sert
    // le fichier depuis le début — c'est la balise qui s'y déplace ensuite
    // (voir `choisirAudio`). Le poser aussi sur ce cran-là, c'était le même
    // double compte, au changement de piste audio d'un .mp4.
    etat.decalage = remux ? pos : 0;
    // Le rendu riche lit `video.currentTime` lui-même : il doit apprendre le
    // décalage, sinon un ASS au cran remux serait en avance de `pos` secondes.
    decalerRenduRiche();
    // L'adresse du tampon préparé, À L'OCTET PRÈS : `avecAcces` y met la trace
    // du lancement, et une autre trace serait une autre entrée de cache.
    video.src = (!q.length && chaud?.ref === ref && chaud.balise?.src)
      || avecAcces(base(`/api/play/${ref}${q.length ? `?${q.join('&')}` : ''}`));
  }

  /**
   * Ouvre `ref` par le démuxeur de la page — le cran `mse` —, et rend la main
   * au serveur s'il ne convient pas. Le fichier brut, avec le Range : c'est
   * tout ce dont il a besoin, .mkv ou .mp4.
   */
  /**
   * LE MORCEAU DU DÉMUXEUR NE SE RETÉLÉCHARGE PAS, ET IL PEUT PARTIR AVANT.
   *
   * ⚠️ 900 977 OCTETS EN SÉRIE, JUSTE LÀ OÙ TOJI ATTEND DÉJÀ.
   *
   * `parLeDemuxeur` commençait par `chargerDemuxeur()` — un `import('./mkv.js')`
   * qu'esbuild sert en `public/morceaux/mkv-*.js` — et n'ouvrait
   * `/api/play/<ref>?brut=1` qu'ENSUITE. Pour tout .mkv (donc la majorité des
   * releases) cet aller-retour s'intercalait entre « source trouvée » et le
   * premier octet de vidéo, alors que le navigateur était resté oisif pendant
   * toute la résolution. Le morceau ne dépend d'AUCUNE information du serveur :
   * il peut être tiré dès que la fiche s'ouvre (voir `prechauffer`).
   *
   * ⚠️ LE `morceauMkv = null` DU `.catch` EST OBLIGATOIRE. Sans lui, un échec
   * transitoire (un déploiement entre deux ouvertures — l'ancienne empreinte
   * n'existe plus, exactement le cas décrit dans le `.catch` de
   * `parLeDemuxeur`) figerait ici une promesse rejetée, et TOUTES les lectures
   * suivantes de la page tomberaient en `chunk-absent` : le cran `mse` serait
   * définitivement inatteignable jusqu'au prochain rechargement.
   *
   * Mémoïsé AU NIVEAU DE L'INSTANCE, jamais du module : `chargerDemuxeur` est
   * injecté par l'appelant (les bancs en posent un de figuration), et un cache
   * de module ferait fuir le démuxeur d'un lecteur dans le suivant.
   */
  let morceauMkv = null;
  const demuxeurPret = () => (morceauMkv ??= chargerDemuxeur().catch((err) => {
    morceauMkv = null;
    throw err;
  }));

  // ---------------------------------------------------------------------------
  // LE SUIVANT SE TIENT PRÊT (Toji, 21/10 : « moins d'une seconde » entre la fin
  // et la première image du suivant)
  //
  // Dans les dernières secondes, on remplit en fond le tampon de la source
  // suivante — la MÊME adresse que `poserSource` posera, pour que la balise
  // principale la retrouve dans le cache média du navigateur au lieu de
  // l'attendre du débrideur :
  //   · `direct` : une seconde balise, détachée, muette, `preload="auto"` ;
  //   · `mse`    : le conteneur ouvert et son premier bloc lu (`ouvrirEntree`),
  //     repris tel quel par `parLeDemuxeur` ;
  //   · `remux`  : rien — ce serait un ffmpeg de plus par spectateur.
  // On garde la balise principale (et donc volume, vitesse, pistes, sous-titres,
  // plein écran) : échanger deux <video> couperait la séance et la centaine
  // d'écouteurs posés sur celle-ci. Un seul suivant à la fois.
  // ---------------------------------------------------------------------------
  let chaud = null; // { ref, balise?, entree? }
  // ⚠️ La balise de réserve ne part qu'à 12 s de la fin : Chrome suspend une
  // balise en pause au bout de ~15 s et jette ses octets (servis en no-store).
  // Mesuré (essais/parcours/enchainement.mjs) : partie 30 s avant, la balise
  // principale retéléchargeait tout (500 ms) ; 12 s avant, 83 ms.
  const RESERVE_DIRECT_S = 12;
  function lacherChaud() {
    if (!chaud) return;
    try { chaud.balise?.removeAttribute('src'); chaud.balise?.load(); } catch { /* rien à lâcher */ }
    try { chaud.entree?.dispose?.(); } catch { /* déjà lâchée */ }
    chaud = null;
  }
  function prechaufferSuivant({ ref, direct }, reste) {
    if (chaud?.ref === ref || etat.ref === ref) return;
    const cran = cranPropose(!!direct, 0);
    if (cran === 'direct' && reste > RESERVE_DIRECT_S) return;
    lacherChaud();
    if (cran === 'direct') {
      const balise = document.createElement('video');
      balise.muted = true;
      balise.preload = 'auto';
      balise.src = avecAcces(base(`/api/play/${ref}`));
      chaud = { ref, balise };
    } else if (cran === 'mse') {
      chaud = { ref };
      demuxeurPret()
        .then(({ ouvrirEntree }) => {
          if (chaud?.ref !== ref || !ouvrirEntree) return;
          chaud.entree = ouvrirEntree(avecAcces(base(`/api/play/${ref}?brut=1`)));
        })
        .catch(() => { /* l'ouverture normale refera le chemin */ });
    } else return;
    journal?.('prechargement', `tampon du suivant (${cran})`, { ref });
  }

  function parLeDemuxeur(ref, pos, audio) {
    const pose = poses;
    // L'entrée ouverte pendant l'épisode précédent, si c'est elle — prise UNE fois.
    const entree = chaud?.ref === ref && pos === 0 ? chaud.entree ?? null : null;
    if (entree) chaud.entree = null;
    // Le démuxeur rend la main : même source, même seconde, même piste, un
    // cran plus bas. Sa raison est déjà au journal — son verdict par
    // `noterMkv`, ou `chunk-absent` dans le `.catch`. Il CÈDE, il ne fait pas
    // descendre : la balise n'a rien refusé, et la prochaine pose de cette
    // source repassera par lui (voir `creerEscalier`).
    const rendreLaMain = () => { escalier.ceder(ref); parLeServeur(ref, pos, audio); };

    // Le démuxeur pèse près d'un mégaoctet. Il reste chargé à la demande, mais
    // par `demuxeurPret` : si `prechauffer()` l'a déjà tiré pendant la
    // résolution, l'import est déjà résolu et on enchaîne sur `?brut=1` sans
    // l'attendre. Et deux sources d'affilée ne le retéléchargent plus.
    demuxeurPret()
      // Le rang de piste voyage avec l'ouverture : sans lui, le démuxeur
      // reprenait la piste par défaut du conteneur à chaque fois.
      .then(({ ouvrirMkv }) => ouvrirMkv(
        video,
        avecAcces(base(`/api/play/${ref}?brut=1`)),
        // ⚠️ `surPanne` EST LE SEUL CHEMIN DE RETOUR D'UNE SOURCE QUI MEURT.
        //
        // Le démuxeur ferme désormais son flux en erreur, ce qui réveille en
        // principe le `error` de la balise. « En principe » : la panne peut
        // aussi tomber avant que le `MediaSource` soit ouvert, et tous les
        // moteurs ne posent pas `video.error` au même moment. Ce rappel-ci ne
        // dépend de rien — il part toujours, et `panneASignaler` le rend
        // idempotent (voir `signalerPanneSource`).
        {
          tracer: noterMkv,
          audio,
          entree,
          surPanne: (raison) => signalerPanneSource(ref, raison),
          // Le verdict par piste du démuxeur, même quand il rend la main : c'est
          // là que le bit `sonRecopiable` naît, et c'est au cran remux qu'il sert.
          surVerdict: (lisible) => { etat.sonRecopiable = lisible?.audioLisible === true; },
        },
      ))
      .then((r) => {
        // ⚠️ MÊME GARDE QUE `reclamerDuree`/`reclamerApercu`/`reclamerBornes`,
        // ET POUR LA MÊME RAISON.
        //
        // `ref` est celui qu'on ouvrait au DÉPART de cet appel, et tous les
        // appelants posent `etat.ref` sur la même valeur avant d'appeler
        // `poserSource` — mais entre-temps, l'ouverture traverse un import
        // dynamique PUIS une lecture réseau (voir `ouvrirMkv`), largement de
        // quoi laisser deux appels se chevaucher : un double-clic sur
        // « épisode suivant », deux bascules de piste audio coup sur coup, deux
        // sauts hors tampon. Sans cette garde, la réponse la plus LENTE des
        // deux gagnait quand même en arrivant en dernier — elle écrasait
        // `demuxeur` par une instance déjà obsolète, réglait `etat.totale` sur
        // la durée du MAUVAIS fichier, et lançait la lecture dessus.
        //
        // Le ticket complète la ref : la MÊME source reposée entre-temps —
        // autre piste, ou l'escalier descendu au remux pendant l'ouverture —
        // périme cette réponse tout autant, et la ref seule ne le voyait pas.
        //
        // Le démuxeur qu'on referme ici a bien été ouvert — juste pour un
        // fichier qu'on ne regarde plus — donc on le ferme comme n'importe
        // quel démuxeur remplacé (voir `fermerDemuxeur`), pour ne pas laisser
        // sa connexion ouverte pour rien.
        if (etat.ref !== ref || pose !== poses) { try { r?.fermer(); } catch { /* déjà fermé */ } return; }
        if (!r) { rendreLaMain(); return; }
        demuxeur = r;
        // La durée est connue et l'index existe : le déplacement redevient
        // natif. On ne touche pas à `etat.direct` pour autant — il dit ce que
        // le SERVEUR fait du fichier, et il servira encore au prochain repli.
        if (r.duree > 0) { etat.totale = r.duree; etat.approx = false; }
        if (pos > 0) video.currentTime = pos;
        peindre();
        // ⚠️ On réamorce ICI, et c'est indispensable depuis que le voile ne se
        // lève qu'à la première seconde jouée.
        //
        // L'appelant a lancé son `amorcer()` bien avant que ce chemin-là ne
        // pose la source : le démuxeur remplace `video.src` par le flux
        // MediaSource, ce qui rejette la promesse de lecture en cours avec
        // AbortError — traité, à raison, comme « un chargement plus récent a
        // pris la main ». Sauf que ce chargement-là, personne ne le lançait :
        // on s'en remettait à l'attribut `autoplay`. Quand il ne prenait pas,
        // rien ne jouait, et plus rien n'aurait baissé le voile.
        //
        // Le voile est reposé juste avant : entre l'appel et ici, la balise
        // portait encore le fichier PRÉCÉDENT, et le relancer a pu émettre un
        // `playing` qui ne parlait pas de celui-ci.
        voiler();
        amorcer();
      })
      // Même garde qu'au `.then` ci-dessus : si une source plus récente a déjà
      // pris la main, ce repli-ci n'a plus rien à corriger — le repli du bon
      // appel s'en charge, ou s'en est déjà chargé. La ligne de journal suit
      // la même garde : elle compterait sinon pour la NOUVELLE lecture, dont
      // le capteur vient d'être remis à zéro.
      .catch((err) => {
        if (etat.ref !== ref || pose !== poses) return;
        // ⚠️ CE REPLI AVALAIT SA RAISON.
        //
        // `.catch(() => parLeServeur())` : le serveur reprenait, l'image
        // revenait, et personne ne savait POURQUOI le démuxeur n'avait pas
        // servi. Or ce qui tombe ici n'est pas une décision du démuxeur — lui
        // rend `null` quand il refuse, et c'est le `.then` qui replie — mais
        // l'`import()` lui-même : le morceau `morceaux/mkv-*.js` n'est pas
        // venu. Un déploiement entre deux ouvertures suffit (l'ancienne
        // empreinte n'existe plus), un proxy qui coupe aussi. C'est donc
        // `chunk-absent`, et la cause va dans la console avec.
        noterMkv('mkv', 'chunk absent — repli serveur', err?.message || err?.name || 'cause inconnue');
        capteur.noter({ chemin: 'remux', raison: 'chunk-absent' });
        rendreLaMain();
      });
  }

  // Ce que chaque lecture a emprunté, et pourquoi — posté par `journal`, le
  // `tracer` de main.js. Voir « LE CAPTEUR DE CHEMIN » en tête de fichier.
  const capteur = creerCapteurDeChemin(journal);

  /**
   * Le démuxeur parle. La console garde sa voix entière — utile en local — ;
   * le journal, lui, ne reçoit que les DÉCISIONS de chemin, traduites dans le
   * vocabulaire fermé du capteur par `lireLeDemuxeur`. Le reste (sauts,
   * purges, pannes en cours de lecture) n'y va pas : ça ne se compte pas, et
   * ça ferait du volume pour rien.
   */
  const noterMkv = (zone, message, detail) => {
    console.log(`[${zone}] ${message}${detail ? ' — ' + detail : ''}`);
    const decision = lireLeDemuxeur(message, detail);
    if (decision) capteur.noter(decision);
  };

  // Le relais de panne : une panne par source, vers le repreneur que `main.js`
  // branche par `surPanneSource`. `etat.ref` est lu à chaque cri, pas figé ici.
  const signalDePanne = creerSignalDePanne({
    refCourante: () => etat.ref,
    noter: (raison) => noterMkv('mkv', 'source perdue', raison),
    annoncer,
  });
  /** Une source vient de mourir en cours de lecture : on le fait savoir. */
  const signalerPanneSource = (ref, raison) => signalDePanne.signaler(ref, raison);

  // L'escalier de la source courante : le cran où l'on est, et où descendre
  // quand la balise refuse le format (voir « L'ESCALIER »). `etat.ref` est lu
  // à chaque descente, pas figé ici. Sans MediaSource, le cran `mse` n'existe
  // pas sur cette machine : on l'enjambe plutôt que de charger un démuxeur
  // qui rendrait la main aussitôt.
  const escalier = creerEscalier({
    refCourante: () => etat.ref,
    peutServir: (cran) => cran !== 'mse' || mseDisponible(),
  });

  // --- le voile de chargement ------------------------------------------------
  //
  // ⚠️ Le voile ne suit PAS le tampon. C'est tout le correctif.
  //
  // Il était posé par `waiting` et retiré par `playing`, `canplay` et deux
  // sondages, chacun avec sa propre condition. Or ces deux évènements ne vont
  // pas par paires. `waiting` part aussi quand l'élément est en pause ou quand
  // un déplacement demande des octets qu'on n'a pas ; la sortie de ces
  // situations-là passe par `seeked`, pas par `playing`. Et le seul retrait
  // CONTINU — la boucle de rendu — s'arrête de lui-même dès qu'une image trouve
  // `video.paused` vrai, ce que poser une source fait sans émettre `pause` (le
  // navigateur remet l'élément en pause pendant l'algorithme de chargement, en
  // silence). Plus rien ne baissait alors le voile : il fallait une pause suivie
  // d'une relecture — les deux clics — pour produire enfin un `playing`.
  //
  // Désormais le voile ne répond qu'à une question : CETTE source a-t-elle déjà
  // montré une image en mouvement ? Posé quand on en ouvre une, retiré à la
  // première seconde qui avance, et plus jamais reposé avant la suivante. Un
  // fichier prêt qui se remplit n'est pas un chargement.
  let sourceVierge = false;

  /**
   * Fige ou relance la vague du blason d'attente.
   *
   * Elle est décrite en SMIL, qui expose exactement cela, et sans toucher à la
   * phase. Elle ne tourne QUE derrière le voile : ailleurs elle est invisible,
   * et une vague arrêtée pendant un chargement est précisément ce qui donnait
   * « ça tourne sur une image figée ».
   */
  function animerAttente(active) {
    for (const svg of racine.querySelectorAll('.l-attente svg.embleme')) {
      try { active ? svg.unpauseAnimations() : svg.pauseAnimations(); } catch { /* pas de SMIL */ }
    }
  }

  // Le blason tourne, mais il ne dit pas ce qu'il attend : une attente de dix
  // secondes sans un mot se lit comme un lecteur figé. Le texte est fabriqué
  // ici plutôt qu'écrit dans la page, pour qu'il ne puisse pas subsister à
  // l'écran sans l'état qui le justifie. `role="status"` plutôt qu'une région
  // muette : l'annonce part quand le mot est posé, et rien n'est annoncé quand
  // on l'efface.
  const motAttente = document.createElement('p');
  motAttente.className = 'l-attente-mot';
  motAttente.setAttribute('role', 'status');
  racine.querySelector('.l-attente')?.append(motAttente);

  /** Une source vient d'être posée : le voile reprend son sens. */
  function voiler() {
    // Une nouvelle source : le verdict rendu sur la précédente n'a plus cours.
    abandonAffiche = false;
    sourceVierge = true;
    // Une nouvelle source repart d'une ardoise propre : ce qui suit est une
    // vraie lecture, et ses pauses devront se voir.
    misDeCote = false;

    // ⚠️ CE VOILE N'EXISTE PLUS. IL N'Y A QU'UN ÉCRAN D'ATTENTE, ET C'EST L'AUTRE.
    //
    // Toji, deux fois plutôt qu'une : « ce deuxième chargement en cours, je veux
    // vraiment que tu l'enlèves complètement … on a le fond, c'est la preview de
    // l'épisode, et une fois que l'épisode est prêt, bam, ça switch sur
    // l'épisode. Il n'y a rien d'autre. »
    //
    // Le lecteur posait son propre voile — blason gris, « Chargement en cours… »
    // — alors que l'écran d'attente montre déjà la vignette de l'épisode. Deux
    // écrans disant la même chose, dont le second est plus pauvre que le premier.
    //
    // J'avais d'abord tenté de le TAIRE tant que l'autre était là. Insuffisant :
    // la balise émet `waiting` dès que son tampon se vide, y compris EN PLEINE
    // LECTURE, et le voile revenait alors par-dessus l'image — après la bascule,
    // quand l'écran d'attente avait disparu. C'est ce retour-là qu'il voyait.
    //
    // On ne le conditionne donc plus : on le retire. Ce qu'il apportait est déjà
    // ailleurs — l'écran d'attente pour l'ouverture, la barre de progression pour
    // le tampon, et le message d'abandon quand une source ne démarre pas. Un
    // indicateur de plus, qui recouvre l'image qu'on regarde, n'apprend rien à
    // personne.
    motAttente.textContent = '';
    // Le symbole de lecture posé par la source précédente survivrait à celle-ci
    // et annoncerait une pause là où rien n'a encore commencé.
    peindreLecture();
  }

  /**
   * Le message d'abandon est-il encore à l'écran ? S'il l'est et que la lecture
   * démarre, il ment : on l'efface.
   */
  let abandonAffiche = false;

  function oublierAbandon() {
    if (!abandonAffiche) return;
    abandonAffiche = false;
    pose('l-infos', 'textContent', '');
  }

  /**
   * La source a montré quelque chose — ou a renoncé. Dans les deux cas le voile
   * tombe, et il ne se relèvera pas avant la prochaine source.
   */
  function devoiler() {
    if (!sourceVierge) return;
    sourceVierge = false;
    // `charge` et l'animation ne sont plus jamais posées (voir `voiler`), mais on
    // les retire quand même : une page ouverte avant ce changement peut encore
    // les porter, et un voile orphelin recouvrirait l'épisode indéfiniment.
    racine.classList.remove('charge');
    motAttente.textContent = '';
    animerAttente(false);
    peindreLecture();
  }

  /**
   * Ce qui prouve qu'une source a démarré.
   *
   * `readyState` dit ce que le navigateur CROIT pouvoir jouer et `paused` ment
   * le temps d'un tampon : on regarde donc d'abord si la tête de lecture a
   * quitté zéro. Un flux remuxé repart de zéro à chaque saut, d'où la seconde
   * porte — un élément qui n'est ni en pause ni en déplacement et qui annonce
   * de quoi tenir joue déjà, même à la première image.
   */
  function preuveDeLecture() {
    // ⚠️ PAS DE `return` SUR `!sourceVierge` ICI. C'était la première ligne de
    // cette fonction, et elle rendait `oublierAbandon()` inatteignable dès que
    // le voile était tombé — c'est-à-dire exactement dans le cas où le message
    // d'abandon est à l'écran, puisque l'abandon dévoile avant de l'écrire.
    // Voir `aDefaireApresPreuve`, qui tient la règle et le détail du défaut.
    const joue = !video.paused && !video.seeking
      && (video.currentTime > 0 || video.readyState >= 3);
    const quoi = aDefaireApresPreuve({ joue, voileEnCours: sourceVierge, abandonAffiche });
    if (quoi.devoiler) devoiler();
    if (quoi.effacerAbandon) oublierAbandon();
  }

  // --- le gel de l'image pendant une bascule de source -----------------------
  //
  // Le POURQUOI, les deux phrases de Toji et les durées mesurées sont en tête
  // de fichier, au-dessus de `imageGelable`. Ici, la mécanique.

  /**
   * Le plafond du gel : au-delà, la dernière image MENTIRAIT.
   *
   * Une source qui ne revient jamais laisserait sinon un arrêt sur image
   * définitif — un lecteur qui a l'air de marcher et qui ne joue rien, ce qui
   * est le pire des deux états possibles. Trente secondes : la même échéance
   * que `ATTENTE_MAX_IMAGE` dans main.js, et pour la même raison — c'est ce
   * que met le démuxage le plus lent, et au-delà c'est l'abandon qui parle
   * (voir `amorcer`, qui écrit dans `l-infos`, au-dessus du gel).
   */
  const GEL_MAX_MS = 30_000;

  /** Ce qui est posé par-dessus la balise, ou `null`. */
  let gel = null;

  /**
   * Le style du gel, posé par le code qui le fabrique — même règle que
   * `poserStyleSaut`. Il n'existe que pendant une bascule : le laisser dans la
   * feuille de la page, c'est y laisser des règles dont plus rien ne dira à
   * quoi elles servaient.
   */
  function poserStyleGel() {
    if (document.getElementById('l-gel-style')) return;
    const style = document.createElement('style');
    style.id = 'l-gel-style';
    style.textContent = `
      /* z-index 2 : au-dessus de la balise et de la cible de clic plein cadre
         (\`.l-central\`, z-index 1), SOUS le voile (3), le panneau d'amorce (3)
         et les commandes (4) — le message d'abandon doit rester lisible.

         ⚠️ LES DEUX CALQUES DE SOUS-TITRES SONT AUSSI EN 2 (\`.st-calque\`,
         \`#st-riches\`). C'est donc l'ORDRE DANS LE DOM qui départage, et le gel
         doit passer devant : les répliques affichées appartiennent au fichier
         qu'on quitte. \`gelerLImage\` l'ajoute toujours en DERNIER (\`racine.append\`)
         et le recrée à chaque bascule — c'est ce qui le garantit. Déplacer
         cette insertion casserait le gel en silence. */
      .l-gel {
        position: absolute; inset: 0; z-index: 2;
        width: 100%; height: 100%;
        /* Exactement le cadrage de la balise : les deux ont le même rapport,
           donc la copie se pose au pixel sur l'image qu'elle remplace. */
        object-fit: contain; background: #000;
        /* ⚠️ ASSOMBRI, MAIS PAS ÉTEINT. Une image gardée à pleine luminosité se
           lit comme une lecture qui continue — et on chercherait pourquoi le
           temps n'avance plus. Assombrie d'un tiers, elle dit « on tient ta
           place » sans jamais redevenir l'écran noir qu'on corrige. */
        filter: brightness(.66);
      }
      /* Le mot : sans lui, une image figée est indiscernable d'un lecteur
         planté. Discret et en bas — il ne recouvre pas ce qu'on regarde. */
      .l-gel-mot {
        position: absolute; z-index: 2; left: 50%; bottom: 12%;
        transform: translateX(-50%);
        display: flex; align-items: center; gap: 10px;
        padding: 8px 16px; border-radius: 999px;
        background: rgba(10,10,12,.74);
        font-family: "Bebas Neue", "Archivo Narrow", sans-serif;
        font-size: 14px; letter-spacing: .16em; text-transform: uppercase;
        color: var(--texte, #fff); pointer-events: none; white-space: nowrap;
      }
      /* ⚠️ ELLE TOURNAIT DÉJÀ — ELLE NE SE LISAIT PAS.
         
         Toji (16/09), capture à l'appui : « la petite roue, elle ne tourne
         pas ». Mesuré : l'animation était bien en cours ("l-gel-tourne",
         "running", 0,8 s), et deux captures à 0 et 200 ms montrent l'arc clair
         passer du haut à la droite. Elle tournait.
         
         Ce qui ne marchait pas, c'est la LISIBILITÉ. Sur un cercle de 12 px,
         une seule bordure colorée fait un arc d'à peine trois pixels, et le
         reste de l'anneau était à .26 — assez visible pour que l'œil lise « un
         anneau » et rate le quart qui bouge. Une rotation qu'on ne perçoit pas
         ne vaut pas mieux qu'une image figée : elle dit « c'est planté » au
         moment précis où elle devrait dire « ça travaille ».
         
         Trois gestes, et aucun n'est décoratif : l'anneau s'efface (.26 -> .10)
         pour que l'arc ressorte, l'arc occupe DEUX bordures au lieu d'une (un
         demi-tour de cercle se suit à l'œil, un quart non), et le cercle passe
         à 14 px. */
      .l-gel-mot i {
        width: 14px; height: 14px; border-radius: 50%;
        border: 2px solid rgba(255,255,255,.10);
        border-top-color: currentColor; border-right-color: currentColor;
        flex: none; will-change: transform;
        animation: l-gel-tourne .8s linear infinite;
      }
      /* Une animation qui tourne DIT que ça travaille — c'est toute la
         différence avec un écran immobile. Respectée, la préférence de
         mouvement réduit la remplace par un battement d'opacité. */
      @keyframes l-gel-tourne { to { transform: rotate(360deg); } }
      /* ⚠️ SANS ROTATION, PAS DE DEMI-ANNEAU. L'arc de deux bordures, immobile,
         se lisait comme une roue figée et coupée (Toji, 21/10 ; Windows sans
         « effets d'animation » = mouvement réduit). Ici l'anneau est entier,
         et c'est lui qui bat. */
      @media (prefers-reduced-motion: reduce) {
        .l-gel-mot i { border-color: currentColor; will-change: auto;
          animation: l-gel-pulse 1.4s ease-in-out infinite; }
        @keyframes l-gel-pulse { 50% { opacity: .3; } }
      }
    `;
    document.head.appendChild(style);
  }
  poserStyleGel();

  /**
   * Garde la dernière image à l'écran, et dit qu'on travaille.
   *
   * À appeler AVANT `poserSource` — donc avant `viderLaBalise`, qui est le
   * geste qui met le cadre au noir.
   *
   * @param {string} mot ce qu'on est en train de faire, en clair.
   */
  function gelerLImage(mot) {
    // ⚠️ UN SECOND GEL RECOPIERAIT DU NOIR. L'escalier repose la même source un
    // cran plus bas (`descendre`), et le démuxeur rend la main au serveur
    // (`parLeDemuxeur`) : dans les deux cas la balise a DÉJÀ été vidée, et une
    // nouvelle capture ne ramènerait qu'un rectangle vide — qui survivrait
    // ensuite à la reprise. Le gel en place couvre toute la bascule, replis
    // compris.
    if (gel) { gel.mot.lastChild.textContent = mot; return; }
    if (!imageGelable(video)) return; // rien à garder : première ouverture, ou flux sans image

    const image = document.createElement('canvas');
    image.className = 'l-gel';
    image.width = video.videoWidth;
    image.height = video.videoHeight;
    try {
      image.getContext('2d').drawImage(video, 0, 0, image.width, image.height);
    } catch (err) {
      // Rien n'est perdu — on se retrouve simplement dans l'état d'avant ce
      // correctif — mais ça ne doit pas disparaître sans un mot : c'est la
      // seule façon de savoir un jour qu'un chemin de lecture rend une balise
      // qu'on ne sait pas recopier.
      console.warn('[lecteur] dernière image non recopiable :', err?.name ?? err);
      return;
    }

    const boite = document.createElement('p');
    boite.className = 'l-gel-mot';
    // `role="status"` : l'annonce part au moment où le mot est posé, et rien
    // n'est annoncé quand on l'efface — même règle que `motAttente`.
    boite.setAttribute('role', 'status');
    boite.append(document.createElement('i'), document.createTextNode(mot));
    // L'image est décorative : ce qu'elle montre, c'est l'épisode d'avant.
    image.setAttribute('aria-hidden', 'true');

    racine.append(image, boite);
    gel = {
      image,
      mot: boite,
      plafond: setTimeout(() => {
        // Ce n'est pas une panne — `amorcer` a son propre abandon —, mais le
        // gel a fini de rendre service : il faut voir ce qu'il y a dessous.
        console.warn('[lecteur] la nouvelle source n’a rien peint en', GEL_MAX_MS, 'ms — on rend l’écran');
        degeler();
      }, GEL_MAX_MS),
      lacher: null,
    };
  }

  /** Retire le gel. Sans effet s'il n'y en a pas. */
  function degeler() {
    if (!gel) return;
    clearTimeout(gel.plafond);
    gel.lacher?.();
    gel.image.remove();
    gel.mot.remove();
    gel = null;
  }

  /**
   * Lève le gel à la PREMIÈRE IMAGE PEINTE de la nouvelle source, et pas avant.
   *
   * ⚠️ NI `canplay`, NI `readyState`, NI `buffered` : AUCUN NE DIT « PEINTE ».
   *
   * C'est la leçon déjà écrite dans `basculerQuandPret` (main.js) : sur le
   * chemin du démuxeur la balise reste à `readyState: 1` du début à la fin,
   * parce qu'elle ne lit pas le fichier — on la nourrit. Et même quand elle
   * annonce « je peux jouer », il reste le décodage de la première image et un
   * cycle de composition : lâcher le gel à ce moment-là redonnerait quelques
   * dizaines de millisecondes de noir, c'est-à-dire exactement le défaut, en
   * plus court.
   *
   * `requestVideoFrameCallback` répond à la seule question qui vaille : une
   * image a-t-elle été présentée ? Le navigateur l'appelle APRÈS l'avoir
   * composée.
   *
   * Deux filets, parce qu'elle ne suffit pas seule :
   *   · Firefox ne la connaît pas ;
   *   · une balise EN PAUSE ne présente jamais de frame — et une bascule
   *     pendant une pause de séance est un cas ordinaire. `loadeddata` dit
   *     alors qu'il y a une image à montrer, et on la laisse se composer le
   *     temps d'une frame.
   */
  function leverLeGelALaPremiereImage() {
    // ⚠️ UNE SEULE ARMEMENT PAR GEL. Deux bascules coup sur coup — un
    // double-clic sur une entrée du menu « Voix », un repli du démuxeur juste
    // après un changement de lien — repassent ici avec le MÊME gel : sans
    // cette garde, la seconde pose une seconde paire d'écouteurs et écrase
    // `lacher`, donc la première paire ne se retire jamais et se déclenche sur
    // la source d'APRÈS. Les écouteurs déjà en place couvrent la nouvelle
    // tentative : ils attendent une image, peu importe laquelle.
    if (!gel || gel.lacher) return;
    let fait = false;
    const lacher = () => {
      if (fait) return;
      fait = true;
      degeler();
    };
    const surDonnees = () => {
      // `loadeddata` part aussi sur une balise sans image (flux audio) : on ne
      // lâche que s'il y a vraiment quelque chose à voir.
      if (!imageGelable(video)) return;
      requestAnimationFrame(lacher);
    };
    video.addEventListener('loadeddata', surDonnees);
    video.addEventListener('playing', surDonnees);
    if (typeof video.requestVideoFrameCallback === 'function') {
      try { video.requestVideoFrameCallback(lacher); } catch { /* pas de rVFC utilisable */ }
    }
    // Ce que `degeler` doit défaire : les écouteurs ne doivent pas survivre au
    // gel qu'ils servaient — la source SUIVANTE les déclencherait.
    gel.lacher = () => {
      fait = true;
      video.removeEventListener('loadeddata', surDonnees);
      video.removeEventListener('playing', surDonnees);
    };
  }

  /**
   * Le geste complet d'une bascule de source : on garde l'image, on repose, on
   * ne rend l'écran qu'une fois la nouvelle image peinte.
   *
   * Les trois appelants (`ouvrirUneSource`, `choisirAudio`, et la reprise d'un
   * cran plus bas) font exactement la même chose, et l'oubli de l'un des trois
   * gestes est précisément ce qui produisait l'écran noir.
   */
  function basculerVers(ref, { mot, seconde = 0, audio = 0 }) {
    gelerLImage(mot);
    voiler();
    poserSource(ref, seconde, audio);
    leverLeGelALaPremiereImage();
  }

  /**
   * Déplacement. QUI peut servir la seconde demandée décide du geste — toute
   * la règle, et les symptômes qui l'ont écrite, sont dans « SE DÉPLACER DANS
   * LE FLUX » plus haut, où elle est pure et éprouvée.
   *
   * ⚠️ CE QUI EST DEJA CHARGE NE SE REDEMANDE PAS. Toji : « quand on passe de
   * 10 en 10, ça met énormément de temps à charger ». En mode remux, chaque
   * saut reposait une source : le serveur relance ffmpeg a la seconde voulue,
   * le tampon repart de zero, et l'image met plusieurs secondes a revenir.
   * Pour DIX SECONDES, alors que les dix secondes en question sont, neuf fois
   * sur dix, deja dans le tampon — c'est meme la raison d'etre d'un tampon.
   * C'est le verdict `tampon`, et il reste intact.
   */
  function chercher(secondes) {
    const T = totale();
    const t = Math.max(0, T > 0 ? Math.min(secondes, T - 1) : secondes);
    // La cible est affichée et l'attente se voit dès maintenant (voir `viseeEnCours`).
    etat.visee = { t, at: performance.now() };
    peindre();

    const choix = choisirLeDeplacement({
      // Le CRAN, pas `etat.direct` : un .mp4 descendu au remux (voir
      // `descendre`) n'a plus d'index, et un saut natif dessus ne mènerait
      // nulle part.
      cran: escalier.cran(),
      demuxeur: !!demuxeur,
      tampon: video.buffered,
      // Le décalage est posé par `poserSource`, sur la seule branche où il vaut.
      decalage: etat.decalage,
      cible: t,
    });
    if (choix.quoi !== 'reposer') { video.currentTime = choix.seconde; return; }

    voiler();
    // ⚠️ `poserSource(etat.ref, choix.seconde)` — SANS LE RANG — RENDAIT LA
    // PISTE AUDIO CHOISIE, EN SILENCE.
    //
    // Le troisième paramètre vaut 0 par défaut : chaque déplacement hors
    // tampon reposait donc le flux sur la PISTE 0. On choisissait l'anglais,
    // on avançait de dix secondes, et le français revenait — sans un mot, et
    // sans que le menu « Voix » change de coche, puisque `etat.audio`, lui,
    // n'avait pas bougé. Le défaut se voyait à peine tant que la piste 0 était
    // celle qu'on écoutait ; il devient systématique dès que la préférence de
    // langue mord (voir `appliquerLaLangueVoulue`).
    //
    // `descendreDUnCran` passait déjà `etat.audio` ici, et pour cette raison
    // exacte. Le trouvé par le banc `essais/piste-suit-la-preference.mjs` :
    // après une bascule à 600 s, un clic à 12:00 repartait en piste 0, donc au
    // cran `direct`, donc sans décalage — et la position affichait 0.
    poserSource(etat.ref, choix.seconde, etat.audio);
    amorcer();
  }

  const fraction = (evt) => {
    const piste = el('l-piste');
    if (!piste) return 0;
    const r = piste.getBoundingClientRect();
    return Math.max(0, Math.min(1, (evt.clientX - r.left) / r.width));
  };

  /**
   * CE QUE LA BARRE A PROMIS SOUS LA MAIN — `{ fraction, seconde }`.
   *
   * ⚠️ « JE METS 5 MINUTES, ÇA MET 11. » (Toji, 16/09)
   *
   * La bulle et le clic ne se calculaient pas au même instant. Au survol, la
   * bulle annonçait `fraction × durée` avec la durée d'alors ; au relâchement,
   * `chercher` refaisait le même produit avec celle du moment. Entre les deux,
   * la durée CHANGE : le catalogue n'en donne qu'une estimation, et la vraie
   * n'arrive qu'après la sonde du serveur — donc pendant les premières
   * secondes, exactement quand on cherche sa scène. La barre changeait d'échelle
   * sous une main immobile, et servait une autre heure que celle affichée.
   *
   * Mesuré par `essais/parcours/deplacement.mjs` sur une œuvre annoncée à 3 s
   * pour un fichier de 600 : la bulle disait 0:00, le clic partait à 151 s.
   *
   * La promesse est donc attachée à un ENDROIT de la barre, pas à un instant :
   * tant que la main ne bouge pas, cet endroit vaut l'heure annoncée. Dès
   * qu'elle bouge d'un pixel, la fraction change et tout se recale sur la durée
   * du moment — qui est alors la bonne.
   */
  let promesse = null;

  function brancherPiste() {
    const piste = el('l-piste');
    if (!piste) return;

    const survol = (evt) => {
      const T = totale();
      const bulle = el('l-bulle');
      if (!bulle || T <= 0) return;
      const f = fraction(evt);
      // Un demi-millième de la barre : bien en deçà du pixel, donc « la main n'a
      // pas bougé » au sens où l'œil l'entend.
      const memeEndroit = promesse && Math.abs(promesse.fraction - f) < 0.0005;
      const seconde = memeEndroit ? promesse.seconde : f * T;
      promesse = { fraction: f, seconde };
      bulle.hidden = false;
      posBulle = f;

      const heure = bulle.querySelector('.l-bulle-heure');
      if (heure) heure.textContent = horloge(seconde);
      else bulle.textContent = horloge(seconde);

      const vue = bulle.querySelector('.l-bulle-vue');
      if (!vue) return;
      const a = etat.apercu;

      // Tout le calcul est dans `caseDeLaSeconde`, au-dessus : pure, donc
      // éprouvée sans navigateur (`essais/apercus-lecture.mjs`). Ici on ne
      // fait qu'obéir à son verdict.
      //
      // ⚠️ `null` VEUT DIRE « PAS D'IMAGE », ET C'EST UNE RÉPONSE.
      //
      // La classe `avec-vue` est le seul interrupteur : sans elle, la bulle
      // n'affiche que l'heure, ce qu'elle a toujours su faire pendant que la
      // planche se fabrique. On la retire donc quand la planche ne correspond
      // pas au fichier joué, au lieu de montrer une image prise ailleurs. Une
      // bulle sans image se comprend ; une bulle qui montre le carton de fin
      // au milieu de l'épisode fait cliquer au mauvais endroit.
      const place = a ? caseDeLaSeconde(a, seconde, T) : null;
      if (!place) { bulle.classList.remove('avec-vue'); return; }
      bulle.classList.add('avec-vue');
      const { col, lig } = place;

      // AU PIXEL, jamais en pourcentage.
      //
      // `scale=L:-2` arrondit la hauteur au multiple de deux : une vignette
      // demandée en 192×108 peut sortir en 192×106. Un placement en
      // pourcentage dérive alors d'une case à l'autre, et l'on finit par voir
      // un morceau de la vignette voisine. Le serveur mesure la planche qu'il
      // a produite ; on se cale dessus.
      vue.style.width = `${a.vignette.l}px`;
      vue.style.height = `${a.vignette.h}px`;
      vue.style.backgroundSize = `${a.planche.l}px ${a.planche.h}px`;
      vue.style.backgroundPosition = `-${col * a.vignette.l}px -${lig * a.vignette.h}px`;
    };
    // La bulle suit le X, bornée aux bords de l'image (et non de la piste, qui
    // s'écarte de 24 px des bords) : en tête ou en fin d'épisode, elle ne sort
    // plus à moitié du cadre. Posée après la vignette, dont dépend sa largeur.
    let posBulle = 0;
    const survolBorne = (evt) => {
      survol(evt);
      const bulle = el('l-bulle');
      if (!bulle || bulle.hidden) return;
      const r = piste.getBoundingClientRect();
      const L = racine.getBoundingClientRect();
      const demi = bulle.offsetWidth / 2;
      const x = Math.max(L.left - r.left + demi, Math.min(L.right - r.left - demi, posBulle * r.width));
      bulle.style.left = `${x}px`;
    };
    // Un vrai bouton sous le pointeur l'emporte toujours sur l'aperçu (Toji,
    // 21/10 : la vignette restait affichée, souris sur le bouton « liste »).
    // Pendant une saisie, la capture du pointeur reste prioritaire.
    const surUnBouton = (n) => !!n?.closest?.('button, [role=button]') && !n.closest('.l-piste');
    const cacherSiBouton = (e) => {
      if (etat.saisie || !surUnBouton(e.target)) return;
      const bulle = el('l-bulle');
      if (bulle) bulle.hidden = true;
    };
    piste.closest('.l-bas')?.addEventListener('pointerover', cacherSiBouton);

    piste.addEventListener('pointermove', (e) => {
      survolBorne(e);
      // Pendant la saisie la tete suit le doigt sans que la video bouge : le
      // deplacement n'est valide qu'au relachement.
      if (etat.saisie) {
        const f = fraction(e) * 100;
        const joue = el('l-joue');
        const tete = el('l-tete');
        if (joue) joue.style.width = `${f}%`;
        if (tete) tete.style.left = `${f}%`;
      }
    });
    // ⚠️ UNE TOLÉRANCE VERTICALE, PARCE QU'UNE MAIN N'EST PAS UNE RÈGLE.
    //
    // `pointerleave` cachait l'aperçu au PREMIER pixel sorti de la piste. En
    // parcourant la barre de gauche à droite, la main descend et remonte de
    // deux ou trois pixels sans qu'on le veuille : l'aperçu clignotait, et à
    // chaque retour il repartait de zéro.
    //
    // La piste fait 26 px de haut pour un rail de 4 : la zone de saisie est
    // déjà plus généreuse que le trait. Ce qui manquait, c'est qu'en SORTIR
    // n'annule pas tout de suite — on garde l'aperçu tant que le pointeur reste
    // dans la bande de la piste élargie, et on ne l'efface que lorsqu'on s'en
    // va franchement.
    const TOLERANCE = 28;   // px au-dessus et au-dessous de la piste

    piste.addEventListener('pointerleave', (e) => {
      const bulle = el('l-bulle');
      if (!bulle || etat.saisie) return;
      if (surUnBouton(e.relatedTarget)) { bulle.hidden = true; return; }
      const r = piste.getBoundingClientRect();
      const dedans = e.clientX >= r.left - TOLERANCE && e.clientX <= r.right + TOLERANCE
        && e.clientY >= r.top - TOLERANCE && e.clientY <= r.bottom + TOLERANCE;
      // Toujours dans la bande : la main a juste dérivé, on garde l'aperçu.
      if (dedans) return;
      bulle.hidden = true;
    });

    // ⚠️ MAIS ELLE DOIT FINIR PAR S'EFFACER. Une tolérance sans sortie laisse
    // l'aperçu flotter sur l'image dès qu'on quitte la barre par un chemin que
    // `pointerleave` n'a pas vu — un plein écran, un menu qui s'ouvre. Le bas
    // du lecteur, lui, voit tous les départs.
    {
      const bas = piste.closest('.l-bas');
      if (bas) {
        bas.addEventListener('pointerleave', () => {
          const bulle = el('l-bulle');
          if (bulle && !etat.saisie) bulle.hidden = true;
        });
      }
    }
    // ⚠️ LA SAISIE PEUT NE JAMAIS SE TERMINER.
    //
    // `pointerleave` ne cache la bulle que si l'on n'est pas en train de
    // saisir — c'est voulu, on doit voir où l'on va en glissant. Mais si le
    // `pointerup` n'arrive jamais — relâché hors de la fenêtre, geste annulé
    // par le navigateur, capture perdue — `saisie` reste vrai et la bulle ne
    // se cache PLUS JAMAIS. Elle flotte alors au-dessus du lecteur, et l'on
    // croit qu'elle s'affiche sur ce que la souris survole : constaté sur le
    // bouton « Passer l'intro », qui se retrouvait coiffé d'un cadre d'aperçu
    // et d'une heure sans aucun rapport.
    //
    // Ces deux événements sont les seuls à dire « ce geste est fini et tu ne
    // recevras pas de `pointerup` ».
    for (const fin of ['pointercancel', 'lostpointercapture']) {
      piste.addEventListener(fin, () => {
        etat.saisie = false;
        const bulle = el('l-bulle');
        if (bulle) bulle.hidden = true;
      });
    }
    piste.addEventListener('pointerdown', (e) => {
      etat.saisie = true;
      try { piste.setPointerCapture(e.pointerId); } catch { /* capture refusee */ }
      survolBorne(e);
    });
    piste.addEventListener('pointerup', (e) => {
      if (!etat.saisie) return;
      etat.saisie = false;
      try { piste.releasePointerCapture(e.pointerId); } catch { /* deja relache */ }
      const bulle = el('l-bulle');
      if (bulle) bulle.hidden = true;
      const T = totale();
      if (T <= 0) return;
      // ⚠️ ON VA LÀ OÙ LA BULLE A PROMIS — voir `promesse`. Refaire le produit
      // ici servait une heure que personne n'avait vue.
      const f = fraction(e);
      const memeEndroit = promesse && Math.abs(promesse.fraction - f) < 0.0005;
      chercher(memeEndroit ? promesse.seconde : f * T);
    });
    piste.addEventListener('keydown', (e) => {
      const pas = e.shiftKey ? 60 : 10;
      // ⚠️ `stopPropagation` : sinon le raccourci du document reculait AUSSI de 10 s — après tout clic
      // sur la barre (elle garde le focus), chaque flèche faisait 20 s, jamais ce qu'on lui demandait.
      if (e.key === 'ArrowRight') { chercher(positionVue() + pas); e.preventDefault(); e.stopPropagation(); }
      if (e.key === 'ArrowLeft') { chercher(positionVue() - pas); e.preventDefault(); e.stopPropagation(); }
    });
  }

  // --- lecture, son ----------------------------------------------------------
  //
  // ⚠️ Le blason ne suit plus la lecture, il suit le VOILE (voir `animerAttente`).
  // Le seul blason du lecteur est celui de l'attente : le faire suivre l'état de
  // la vidéo revenait à l'arrêter pendant les pauses — donc pendant les
  // chargements les plus lents, ceux où l'on a le plus besoin de le voir bouger.
  const bascule = () => {
    // L'état est appliqué tout de suite, sans attendre l'événement : pendant
    // l'attente, la vidéo n'a encore jamais joué et n'émettrait donc rien.
    if (video.paused) {
      amorcer();
    } else {
      // Une pause voulue met fin a l'amorcage : sans cela, le chien de garde
      // relancerait la lecture que l'on vient d'arreter.
      annulerAmorce();
      video.pause();
    }
  };

  /**
   * ⚠️ `video.paused` est vrai PENDANT TOUT LE CHARGEMENT : le navigateur remet
   * l'élément en pause le temps de l'algorithme de source, sans rien émettre.
   * Le peindre tel quel annonçait « en pause » à quelqu'un dont l'épisode
   * n'avait pas encore commencé — c'est faux, et ça inquiète : rien ne
   * distinguait plus une attente d'une lecture que l'on aurait arrêtée soi-même.
   * `sourceVierge` porte déjà la distinction ; l'état affiché s'y range.
   */
  // ⚠️ UNE PAUSE DE NAVIGATION NE S'AFFICHE PAS.
  //
  // Toji : « quand on clique sur Retour et qu'on revient sur l'épisode, il y a le
  // bouton Pause affiché pendant une demi-seconde. Je ne veux pas le voir. C'est
  // en pause, mais je ne veux pas qu'on affiche quoi que ce soit par rapport à
  // ça. »
  //
  // Il a raison, et la distinction est nette : mettre en pause parce qu'on
  // QUITTE l'écran n'est pas une décision de lecture, c'est de la mécanique. Le
  // grand symbole au centre existe pour dire « quelqu'un a mis en pause » ; le
  // poser ici raconte quelque chose qui n'a pas eu lieu, et il reste à l'écran
  // le temps du retour.
  //
  // Le drapeau tombe dès qu'on revient vraiment (`reveiller`) ou qu'une nouvelle
  // source s'ouvre : une VRAIE pause, elle, doit toujours se voir.
  let misDeCote = false;

  function peindreLecture() {
    pose('l-play', 'innerHTML', video.paused ? ICONES.lecture : ICONES.pause);
    const p = el('l-play');
    if (p) {
      p.setAttribute('aria-label',
        sourceVierge ? 'Chargement en cours' : (video.paused ? 'Lecture' : 'Pause'));
    }
    racine.classList.toggle('en-pause', video.paused && !sourceVierge && !misDeCote);
  }

  function peindreSon() {
    const coupe = video.muted || video.volume === 0;
    const icone = coupe ? ICONES.sonCoupe : (video.volume > 0.5 ? ICONES.sonFort : ICONES.sonFaible);
    pose('l-vol-icone', 'innerHTML', icone);
    pose('l-mute', 'innerHTML', icone);
    el('l-mute')?.setAttribute('aria-label', video.muted ? 'Rétablir le son' : 'Couper le son');
    const c = el('l-vol-curseur');
    if (c && document.activeElement !== c) c.value = String(coupe ? 0 : video.volume);
    pose('l-vol-pct', 'textContent', `${coupe ? 0 : Math.round(video.volume * 100)} %`);
    const niveau = el('l-vol-niveau');
    if (niveau) niveau.style.transform = `scaleX(${coupe ? 0 : video.volume})`;
    el('l-vol-indic')?.setAttribute('aria-label', video.muted ? 'Rétablir le son' : 'Couper le son');
  }

  // L'indicateur de volume ne surgit que pour un changement fait À LA MAIN.
  // Le lecteur change aussi le son tout seul — la restauration du volume
  // retenu à l'ouverture, le repli en sourdine quand le navigateur refuse la
  // lecture — et une pastille qui surgit sans geste ressemble à un défaut.
  // `volumechange` ne dit pas qui a changé le son : on retient l'instant du
  // dernier geste (clavier, souris, doigt, molette).
  let derniereMain = -Infinity;
  for (const type of ['keydown', 'pointerdown', 'wheel']) {
    document.addEventListener(type, () => { derniereMain = performance.now(); }, { capture: true, passive: true });
  }
  let minuteurVolume = 0;
  /** Au-dessus du bouton son et de la barre, centrée sur le bouton — même barre masquée (sa boîte reste mesurable). */
  function placerPastilleVolume(indic) {
    const m = el('l-mute');
    if (!m) return;
    const a = m.getBoundingClientRect();
    const r = racine.getBoundingClientRect();
    indic.style.left = `${a.left + a.width / 2 - r.left}px`;
    // Au-dessus de la barre de progression aussi : posée sur le bouton, la pastille
    // couvrirait la piste (le bouton son est SOUS elle).
    const haut = Math.min(a.top, el('l-piste')?.getBoundingClientRect().top ?? a.top);
    indic.style.bottom = `${r.bottom - haut + 12}px`;
  }
  function montrerVolume() {
    const indic = el('l-vol-indic');
    if (!indic) return;
    placerPastilleVolume(indic);
    indic.classList.add('visible');
    clearTimeout(minuteurVolume);
    minuteurVolume = setTimeout(() => indic.classList.remove('visible'), 1200);
  }

  // --- menus flottants -------------------------------------------------------
  //
  // ⚠️ ON N'OUVRAIT QUE `hidden`, ET AU CLAVIER SEUL ON RESTAIT ENFERMÉ.
  //
  // La fabrique ne posait ni `role`, ni `aria-expanded`, et rien n'écoutait
  // Échap : on ouvrait le menu audio à la touche, et il n'existait plus aucun
  // geste au clavier pour en sortir — il fallait aller cliquer ailleurs. Le
  // lecteur d'écran, lui, annonçait un bouton dont rien ne disait qu'il venait
  // de déplier quelque chose.
  //
  // Trois choses posées ici, et une quatrième plus bas (Échap) :
  //   · `role="menu"` / `role="menuitem"` : ce n'est pas une pile de boutons ;
  //   · `aria-haspopup` sur le bouton, et `aria-expanded` TENU À JOUR — un
  //     `aria-expanded` figé à « false » ment plus qu'il n'informe ;
  //   · le focus rendu au bouton à la fermeture, sinon il retombe sur le
  //     `body` et la touche suivante ne va nulle part.

  /** Quel bouton ouvre quel menu — pour rendre le focus, et tenir l'ARIA. */
  const boutonDuMenu = new Map();

  /** Remet `aria-expanded` en accord avec ce qui est réellement affiché. */
  function synchroniserMenus() {
    for (const [idMenu, idBouton] of boutonDuMenu) {
      const m = el(idMenu);
      const b = el(idBouton);
      if (b) b.setAttribute('aria-expanded', String(!!m && !m.hidden));
    }
  }

  /** Le menu actuellement déplié, s'il y en a un. */
  const menuOuvert = () => [...racine.querySelectorAll('.l-menu')].find((m) => !m.hidden) ?? null;

  // -------------------------------------------------------------------------
  // UN MENU OUVERT MANGE LES FLÈCHES — SINON ELLES FILENT AU VOLUME
  //
  // ⚠️ CE DÉFAUT N'ÉTAIT DANS LE LOT DE PERSONNE, ET IL SE PAIE À CHAQUE MENU.
  //
  // On ouvre « Voix », on appuie sur ↓ pour descendre dans la liste : le menu
  // ne bouge pas d'un pixel et le SON BAISSE. ↑ le remonte, → avance de dix
  // secondes dans l'épisode, Début revient au générique. Les entrées du menu
  // sont des `<button>` : le garde-fou des champs de saisie (INPUT, TEXTAREA,
  // SELECT, contentEditable) ne les voit pas, et `actionPour` récupère la
  // frappe comme si l'on regardait l'image.
  //
  // La règle est celle de tous les menus : PENDANT QU'IL EST DÉPLIÉ, LE MENU
  // A LE CLAVIER. Elle se décompose en deux moitiés, et la seconde compte
  // autant que la première :
  //
  //   · les touches de navigation (les quatre flèches, Début, Fin) déplacent
  //     le focus d'une entrée à l'autre, et sont CONSOMMÉES ;
  //   · toute autre touche que le lecteur revendique (Espace, f, m, les
  //     chiffres…) est simplement laissée tranquille : on n'exécute pas le
  //     raccourci, et on n'appelle SURTOUT PAS `preventDefault`. Sans quoi
  //     Espace et Entrée n'activeraient plus l'entrée focalisée et Tab ne
  //     circulerait plus — « une touche confisquée en silence est le pire des
  //     symptômes », et ce serait échanger un défaut contre deux.
  //
  // Échap n'entre pas ici : il est traité plus haut, avant tout, et ferme le
  // menu (voir `echapper`).
  // -------------------------------------------------------------------------

  /** Les touches qui déplacent le focus DANS un menu déplié. */
  const TOUCHES_DU_MENU = ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'];

  /**
   * La frappe appartient-elle au menu ouvert ?
   *
   * @returns {boolean} `true` = l'appelant s'arrête là. Le déplacement du
   *   focus et le `preventDefault` sont faits ici, pour que le cas « touche
   *   revendiquée mais pas consommée » ne puisse pas se tromper de geste.
   */
  function clavierDuMenu(e) {
    const m = menuOuvert();
    if (!m) return false;
    if (!TOUCHES_DU_MENU.includes(e.key)) {
      // Le menu garde la main : le raccourci ne part pas. Mais la touche reste
      // au navigateur — c'est lui qui active l'entrée focalisée.
      return !!actionPour(e);
    }
    const entrees = [...m.querySelectorAll('button')];
    if (entrees.length > 0) {
      const dernier = entrees.length - 1;
      const ici = entrees.indexOf(document.activeElement);
      let vers;
      if (e.key === 'Home') vers = 0;
      else if (e.key === 'End') vers = dernier;
      else {
        const pas = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1 : -1;
        // Rien de focalisé dedans (on vient d'ouvrir au clic ou à Entrée) : on
        // entre par le bout d'où l'on vient — première entrée en descendant,
        // dernière en remontant.
        vers = ici < 0 ? (pas > 0 ? 0 : dernier) : (ici + pas + entrees.length) % entrees.length;
      }
      focaliser(entrees[vers]);
    }
    e.preventDefault();
    reveiller();
    return true;
  }

  /**
   * Replie les menus. Rend `true` si quelque chose était bien ouvert — c'est
   * ce que la touche Échap regarde pour savoir si elle a servi à quelque chose.
   */
  function fermerMenus(sauf = null) {
    let ferme = false;
    racine.querySelectorAll('.l-menu').forEach((m) => {
      if (m !== sauf && !m.hidden) { m.hidden = true; ferme = true; }
    });
    synchroniserMenus();
    if (ferme) peindreCommandes();
    return ferme;
  }

  function menu(idBouton, idMenu, entrees, surChoix) {
    const b = el(idBouton);
    const m = el(idMenu);
    if (!b || !m) return;
    boutonDuMenu.set(idMenu, idBouton);
    m.setAttribute('role', 'menu');
    m.setAttribute('aria-labelledby', idBouton);
    b.setAttribute('aria-haspopup', 'true');
    b.setAttribute('aria-expanded', String(!m.hidden));
    m.innerHTML = entrees
      .map((e, i) => `<button type="button" role="menuitem" data-i="${i}"${e.actif ? ' class="actif" aria-checked="true"' : ''}>${e.libelle}</button>`)
      .join('');
    m.querySelectorAll('button').forEach((n) => {
      n.onclick = (ev) => {
        ev.stopPropagation();
        surChoix(entrees[Number(n.dataset.i)]);
        m.hidden = true;
        synchroniserMenus();
        // Le choix est fait : la suite du clavier appartient au bouton, pas à
        // un menu qui n'est plus là.
        try { el(idBouton)?.focus({ preventScroll: true }); } catch { /* focus refusé */ }
      };
    });
    b.onclick = (e) => {
      e.stopPropagation();
      // Un seul menu ouvert a la fois.
      fermerMenus(m);
      // Deux panneaux ouverts en même temps par-dessus l’image, personne ne
      // sait plus lequel il regarde.
      fermerPanneaux();
      m.hidden = !m.hidden;
      synchroniserMenus();
    };
  }

  // --- panneaux, et la sortie clavier ---------------------------------------
  //
  // Trois panneaux vivent par-dessus l'image : la liste des raccourcis
  // (`l-aide`), les réglages de sous-titres (`st-panneau`) et, chez le
  // propriétaire, le changement de source (`l-panneau-sources`). Les deux
  // premiers n'avaient pas de sortie au clavier. Échap en est une, et une seule règle la
  // gouverne : ELLE NE MANGE LA TOUCHE QUE SI ELLE A FERMÉ QUELQUE CHOSE.
  // Échap sert ailleurs — quitter le plein écran, fermer une modale de la
  // page —, et une touche confisquée en silence est le pire des symptômes
  // (c'est déjà la règle d'`actionPour`, dans `raccourcis.js`).

  /** Ce qui avait le focus avant l'ouverture d'un panneau, pour le lui rendre. */
  let focusAvantPanneau = null;

  const focaliser = (id) => {
    try { (typeof id === 'string' ? el(id) : id)?.focus({ preventScroll: true }); }
    catch { /* focus refusé : rien de grave, mais on ne le laisse pas casser la fermeture */ }
  };

  /** Retient d'où l'on vient. Un focus hors du lecteur ne vaut pas la peine. */
  function retenirLeFocus() {
    const a = document.activeElement;
    focusAvantPanneau = (a && a !== document.body && racine.contains(a)) ? a : null;
  }

  /** Rend le focus à ce qui l'avait, ou au bouton qui a ouvert. */
  function rendreLeFocus(secours = null) {
    const cible = focusAvantPanneau ?? (secours ? el(secours) : null);
    focusAvantPanneau = null;
    focaliser(cible);
  }

  /** `aria-expanded` du bouton de sous-titres : il déplie un panneau, il le dit. */
  function majAriaST() {
    const b = el('l-st');
    if (b) {
      b.setAttribute('aria-haspopup', 'true');
      b.setAttribute('aria-expanded', String(!!panneauST?.estOuvert?.()));
    }
  }

  /** Ferme la liste des raccourcis. Rend `true` si elle était ouverte. */
  function fermerAide() {
    const boite = el('l-aide');
    if (!boite || boite.hidden) return false;
    boite.hidden = true;
    return true;
  }

  /** Replie tout ce qui recouvre l'image. Rend `true` si quelque chose s'est fermé. */
  function fermerPanneaux() {
    let ferme = fermerAide();
    if (panneauST?.estOuvert?.()) { panneauST.fermer(); majAriaST(); ferme = true; }
    if (fermerPanneauSources()) ferme = true;
    if (ferme) peindreCommandes();
    return ferme;
  }

  /**
   * Ce qu'Échap ferme, dans l'ordre de ce qui est le plus « par-dessus ».
   *
   * @returns {boolean} `false` = il n'y avait rien à fermer, et l'appelant ne
   *   doit surtout PAS appeler `preventDefault`.
   */
  function echapper() {
    const m = menuOuvert();
    if (m) {
      fermerMenus();
      // Sans cela le focus retombe sur le `body` : la frappe suivante ne va
      // nulle part, et il faut retrouver le bouton à la tabulation.
      focaliser(boutonDuMenu.get(m.id));
      return true;
    }
    if (fermerAide()) { rendreLeFocus(); return true; }
    if (panneauST?.estOuvert?.()) {
      panneauST.fermer();
      majAriaST();
      rendreLeFocus('l-st');
      return true;
    }
    if (fermerPanneauSources()) { rendreLeFocus('l-sources'); return true; }
    // Le carton de fin d'épisode : Échap y vaut « Annuler », ce qui est bien
    // ce qu'on veut dire en appuyant dessus devant un décompte.
    if (carton && !carton.hidden) { annulerFin(); return true; }
    return false;
  }

  /**
   * Une saisie de texte garde ses touches — Échap y annule une saisie.
   * Un curseur ou une case, non : ce sont des commandes, pas de l'écriture.
   */
  const saisieDeTexte = (n) => !!n && (n.isContentEditable
    || n.tagName === 'TEXTAREA'
    || (n.tagName === 'INPUT' && !/^(range|checkbox|radio|button|submit|reset|color)$/i.test(n.type)));

  // --- affichage des controles -----------------------------------------------
  let minuteur = null;
  // ⚠️ LES COMMANDES NE S'EFFACENT PAS SOUS LE CURSEUR QUI LES VISE.
  //
  // Toji (16/09) : « quand on passe la barre de gauche à droite, si on descend
  // un tout petit peu trop bas, la barre s'enlève ».
  //
  // Le masquage n'attendait QUE l'immobilité : `reveiller()` arme un minuteur,
  // et au bout d'`INACTIVITE` la barre disparaît si la lecture avance. Or on
  // s'immobilise tout le temps SUR la barre — pour lire l'heure sous le
  // curseur, pour regarder la vignette d'aperçu, pour viser le chapitre. La
  // barre s'effaçait donc précisément pendant qu'on s'en servait, et il fallait
  // remuer la souris pour la faire revenir.
  //
  // C'est ce que font tous les lecteurs : tant que le pointeur est SUR les
  // commandes, elles restent.
  //
  // ⚠️ UN SEUL ÉCRIVAIN DE `inactif` : `peindreCommandes` (audit du 19/10).
  // Il y en avait plusieurs (le minuteur, `pause`, le survol de `.l-bas`), et
  // chacun ne voyait qu'une partie de l'état : un menu ouvert n'empêchait pas
  // le masquage, le dégradé de 56 px de `.l-bas` le bloquait pour toujours, et
  // le drapeau de survol restait coincé à vrai si `pointerleave` n'arrivait
  // pas (plein écran, changement de vue sous un curseur immobile). On ne
  // retient plus d'état de survol : on DEMANDE au moment de peindre, et l'on
  // ne regarde que la piste, les rangées de boutons et « Passer l'intro » —
  // pas le dégradé.
  let reveilJusqua = 0;
  // Le survol ne compte qu'à la souris : au doigt, Chromium garde `:hover` sur
  // le dernier bouton touché, et la barre ne se masquerait plus jamais.
  let souris = true;
  // Le focus ne retient la barre que pendant une navigation à Tab : après un
  // clic sur « lecture », Espace rend le bouton `:focus-visible`, et la barre
  // resterait sur l'image pendant tout l'épisode.
  let aLaTabulation = false;
  const ouvert = (id) => { const n = el(id); return !!n && !n.hidden; };

  function doitMontrer() {
    return video.paused || !!etat.saisie || !!menuOuvert()
      || !!panneauST?.estOuvert?.() || ouvert('l-aide') || ouvert('l-panneau-sources')
      || (souris && !!racine.querySelector('.l-piste:hover, .l-rangee:hover, .l-saut:hover'))
      || (aLaTabulation && !!racine.querySelector('.l-ctr :focus-visible'))
      || performance.now() < reveilJusqua;
  }

  function peindreCommandes() {
    clearTimeout(minuteur);
    const montrer = doitMontrer();
    racine.classList.toggle('inactif', !montrer);
    // Retenue par autre chose que le réveil (survol, menu, focus) : on revient
    // voir toutes les demi-secondes, sans quoi rien ne la masquerait quand
    // cette chose tombe sans qu'un geste nous prévienne.
    if (montrer && !video.paused) {
      minuteur = setTimeout(peindreCommandes, Math.max(500, reveilJusqua - performance.now()));
    }
  }

  function reveiller() {
    // Le drapeau de mise de côté tombe dès qu'on revient devant l'écran : à partir
    // d'ici, une pause redevient une décision, et une décision se voit.
    misDeCote = false;
    reveilJusqua = performance.now() + INACTIVITE;
    peindreCommandes();
  }

  racine.addEventListener('focusin', (e) => { if (e.target.closest?.('.l-ctr')) reveiller(); });
  document.addEventListener('keydown', (e) => { aLaTabulation = e.key === 'Tab'; }, true);

  // --- branchements ----------------------------------------------------------
  brancherPiste();

  let boucle = 0;
  const suivre = () => {
    peindre();
    peindreSaut();
    preuveDeLecture();
    boucle = video.paused || video.ended ? 0 : requestAnimationFrame(suivre);
  };
  const lancerSuivi = () => { if (!boucle) boucle = requestAnimationFrame(suivre); };
  const arreterSuivi = () => { if (boucle) cancelAnimationFrame(boucle); boucle = 0; peindre(); };

  // `timeupdate` reste utile a l'arret : c'est lui qui rend compte d'un
  // deplacement effectue pendant une pause.
  //
  // C'est aussi le filet du voile. La boucle de rendu s'arrête d'elle-même dès
  // qu'une image trouve `video.paused` vrai, et poser une source le fait sans
  // émettre `pause` — il faut donc un second témoin, qui ne dépende pas d'elle.
  // `timeupdate` part quatre fois par seconde tant que la lecture avance.
  video.addEventListener('timeupdate', () => {
    peindreSaut();
    peindreFin();
    preuveDeLecture();
    if (!boucle) peindre();
  });
  video.addEventListener('progress', peindre);
  video.addEventListener('durationchange', peindre);
  /** Fait surgir le symbole au centre, le temps d'un battement. */
  function eclair(icone) {
    const c = el('l-central');
    if (!c) return;
    c.innerHTML = icone;
    // Retirer puis reposer la classe relance l'animation : sans ce temps mort,
    // deux bascules rapprochees n'en joueraient qu'une.
    c.classList.remove('flash');
    void c.offsetWidth;
    c.classList.add('flash');
  }

  video.addEventListener('play', () => {
    peindreLecture();
    // Une relance du chien de garde de l'amorce n'est pas un geste : elle ne
    // réveille pas la barre et ne fait pas d'éclair (sinon, toutes les 3,5 s).
    if (relanceAmorce) peindreCommandes();
    else { reveiller(); eclair(ICONES.lecture); }
    relanceAmorce = false;
    lancerSuivi();
  });
  video.addEventListener('playing', lancerSuivi);

  // ⚠️ L'IMAGE FIGÉE ALORS QUE LE SON CONTINUE — LE LECTEUR S'EN RATTRAPE ET LE DIT AU SERVEUR.
  //
  // Toji (22/10, ~04:10) : « image figée un instant, le son continuait ». La balise ne dit rien : `currentTime`
  // avance (l'audio joue), ni `waiting` ni `error` ne partent, rien n'arrivait au journal. Hypothèse (DÉDUITE,
  // non prouvée) : au remux d'un HEVC 2160p recopié (`-c:v copy`), la vidéo ne peut démarrer qu'à une image clé,
  // alors que l'audio démarre à la seconde demandée ; un décodeur matériel qui perd sa référence fige de même.
  // Ici, on MESURE : aucune image présentée (`requestVideoFrameCallback`) pendant plus de 1,5 s alors que la
  // lecture avance de plus de 0,5 s → un micro-saut sur place (le décodeur repart de l'image clé la plus proche)
  // et une ligne au journal serveur, pour avoir la preuve la prochaine fois.
  {
    let derniereImage = performance.now();
    let derniereLecture = -1;
    let rattrapages = 0;
    let dernierRattrapage = 0;
    let enAttente = false;
    const armer = () => {
      if (enAttente || typeof video.requestVideoFrameCallback !== 'function') return;
      enAttente = true;
      try {
        video.requestVideoFrameCallback(() => { enAttente = false; derniereImage = performance.now(); armer(); });
      } catch { enAttente = false; }
    };
    video.addEventListener('playing', armer);
    video.addEventListener('loadstart', () => { enAttente = false; rattrapages = 0; derniereLecture = -1; });
    const veille = setInterval(() => {
      const t = video.currentTime || 0;
      const avance = derniereLecture >= 0 ? t - derniereLecture : 0;
      derniereLecture = t;
      // Ne juge que ce qui joue vraiment et se voit : un onglet caché ne présente pas d'images.
      if (video.paused || video.seeking || video.ended || document.hidden || video.readyState < 3 || !video.videoWidth) {
        derniereImage = performance.now();
        return;
      }
      armer();
      const maintenant = performance.now();
      if (avance < 0.5 || maintenant - derniereImage < 1500) return;
      if (rattrapages >= 3 || maintenant - dernierRattrapage < 10_000) return;
      rattrapages += 1;
      dernierRattrapage = maintenant;
      journal?.('video', 'image figée, son qui continue — micro-saut', {
        position: Math.round(position()), muetDepuisMs: Math.round(maintenant - derniereImage),
        readyState: video.readyState, decalage: etat.decalage, cran: escalier.cran(), n: rattrapages,
      });
      derniereImage = maintenant;
      try { video.currentTime = t; } catch { /* pas déplaçable : le journal reste la preuve */ }
    }, 1000);
    veille?.unref?.(); // sous Node (bancs), ne retient pas le processus
  }
  video.addEventListener('pause', () => {
    peindreLecture(); peindreCommandes(); eclair(ICONES.pause);
    arreterSuivi();
  });
  // `ended` est branché plus bas, avec le carton de fin d'épisode : il n'y a
  // qu'un seul instant « l'épisode est fini », et tout ce qui en dépend doit
  // se lire au même endroit.
  video.addEventListener('volumechange', () => {
    peindreSon(); retenirSon();
    if (performance.now() - derniereMain < 1000) montrerVolume();
  });
  // ⚠️ Pas d'écouteur `waiting` : il ne relève plus le voile.
  //
  // Un tampon qui se vide au milieu d'un épisode n'est pas un chargement. Le
  // fichier est là, la lecture reprend seule, et faire réapparaître le blason
  // pour quelques dixièmes de seconde disait le contraire — puis restait, faute
  // de `playing` en face.
  video.addEventListener('playing', devoiler);
  video.addEventListener('canplay', () => {
    // `canplay` ne baisse pas le voile de lui-même : la source s'annonce prête,
    // elle n'a encore rien montré. C'est `preuveDeLecture` qui tranche.
    preuveDeLecture();
    // Si la lecture n'est toujours pas partie, c'est le moment de la relancer,
    // pas dans trois secondes.
    if (amorce && video.paused) video.play().catch(() => {});
  });
  // Sans cela, une source en erreur laissait le voile tourner pour toujours :
  // le lecteur ne disait rien, et on ne pouvait que deviner.
  video.addEventListener('error', () => {
    annulerAmorce();
    devoiler();
  });
  video.addEventListener('loadedmetadata', peindre);

  // ⚠️ SORTIE FRANCHE : MASQUÉ EN UN QUART DE SECONDE (Toji, 21/10 : « comme
  // YouTube, Netflix »). Souris sortie du lecteur ou de la fenêtre, ou partie
  // dans la bande des 8 % d'un bord, loin des commandes : on n'attend pas
  // l'inactivité. Ce qui retient la barre (pause, glisser, menu, panneau,
  // focus clavier) passe toujours par `doitMontrer`, qui l'emporte.
  const filer = () => {
    reveilJusqua = Math.min(reveilJusqua, performance.now() + 250);
    clearTimeout(minuteur);
    minuteur = setTimeout(peindreCommandes, 260);
  };
  // `.l-central` couvre toute l'image : ce n'est pas un bouton qu'on vise.
  const dansUneBande = (e) => {
    if (e.target.closest?.('.l-haut, .l-bas, .l-menu, .st-panneau, .l-panneau-sources, .l-aide, .l-vol-indic, button:not(.l-central)')) return false;
    const r = racine.getBoundingClientRect();
    const bord = r.width * 0.08;
    return e.clientX < r.left + bord || e.clientX > r.right - bord;
  };
  racine.addEventListener('pointermove', (e) => {
    souris = e.pointerType === 'mouse';
    if (souris && dansUneBande(e)) filer(); else reveiller();
  });
  racine.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') filer(); });
  document.addEventListener('mouseout', (e) => { if (!e.relatedTarget) filer(); });

  // ⚠️ Au doigt, le premier appui RÉVÈLE les commandes, il ne bascule pas la
  // lecture. Sans cette distinction, toucher l'écran pour voir où l'on en est
  // mettait la vidéo en pause — le geste le plus naturel devenait le plus
  // gênant.
  //
  // ⚠️ `preventDefault` SUR `pointerdown` N'EMPÊCHE PAS LE `click` QUI SUIT
  // (audit du 19/10). Le tap montrait les commandes, puis son `click` basculait
  // la lecture sur `#l-central` — ou, sur un bouton masqué mais cliquable,
  // passait à l'épisode suivant sans qu'on l'ait vu. Le tap qui révèle pose donc
  // `avaler`, et le `click` qui le suit est mangé en capture. Le drapeau
  // retombe au début de chaque appui : un clic orphelin ne reste jamais armé.
  //
  // Le tactile se décide à l'appui (`pointerType`), pas une fois pour toutes
  // par `matchMedia` : un PC hybride a une souris ET un écran tactile.
  //
  // À la souris, le même défaut existe pour un clic sans aucun mouvement avant :
  // si les commandes sont masquées et qu'on clique là où dort un bouton, on les
  // montre et on avale le clic.
  let avaler = false;
  racine.addEventListener('pointerdown', (e) => {
    avaler = false;
    souris = e.pointerType === 'mouse';
    aLaTabulation = false;
    if (!racine.classList.contains('inactif')) return;
    if (e.target.closest?.('.l-saut.seul')) return;
    if (e.pointerType === 'mouse' && !e.target.closest('.l-ctr')) return;
    reveiller();
    avaler = true;
    e.preventDefault();
    e.stopPropagation();
  }, true);
  racine.addEventListener('click', (e) => {
    if (!avaler) return;
    avaler = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);
  // Entrée/Espace sur un bouton émettent un `click` sans appui : il ne doit pas
  // être mangé par un drapeau resté d'un tap.
  racine.addEventListener('keydown', () => { avaler = false; }, true);
  racine.addEventListener('click', (e) => {
    // ⚠️ Ne pas basculer la lecture ici : le bouton central a deja son propre
    // `onclick`, et l'evenement remonte jusqu'a nous. On basculait donc deux
    // fois, ce qui annulait l'effet du clic.
    if (!e.target.closest('.l-menu, .st-panneau, .l-panneau-sources, .l-fin')) {
      // Par `fermerMenus` et non à la main : `aria-expanded` doit retomber
      // avec le menu, sinon le bouton continue d'annoncer qu'il est déplié.
      fermerMenus();
    }
  });

  /**
    * Plein écran, avec repli.
    *
    * Dans l'iframe Discord, `requestFullscreen` est refusé et la promesse est
    * rejetée sans que rien ne l'indique : le bouton paraissait simplement
    * mort. On bascule alors sur un plein cadre applicatif — l'en-tête
    * disparaît et le lecteur occupe toute la surface, ce qui est le plein
    * écran réel là-dedans, l'activité ayant déjà sa propre fenêtre.
    */
   function pleinCadre(actif) {
     document.body.classList.toggle('plein-cadre', actif);
     racine.classList.toggle('en-plein', actif);
     pose('l-plein', 'innerHTML', actif ? ICONES.reduire : ICONES.plein);
   }

   function plein() {
     // Savoir si le vrai plein ecran est seulement permis : dans une iframe sans
     // `allow="fullscreen"`, l API existe mais refuse — et la refuser en
     // silence est ce qui faisait paraitre le bouton mort.
     if (!document.fullscreenEnabled && !document.body.classList.contains("plein-cadre")) {
       return void pleinCadre(true);
     }
     if (document.fullscreenElement) {
       screen.orientation?.unlock?.();
       return void document.exitFullscreen().catch(() => {});
     }
     if (document.body.classList.contains('plein-cadre')) return void pleinCadre(false);

     const promesse = racine.requestFullscreen?.({ navigationUI: 'hide' });
     if (!promesse) return void pleinCadre(true);
     // Téléphone : le vrai plein écran se regarde en paysage (Toji, 23/10). Refusé
     // sur ordinateur et sur iPhone : on n'en dit rien, l'écran reste plein.
     promesse
       .then(() => { if (matchMedia('(pointer: coarse)').matches) screen.orientation?.lock?.('landscape')?.catch?.(() => {}); })
       .catch(() => pleinCadre(true));
   }
  // Double-clic n'importe ou sur l'image : `.l-central` (bouton plein cadre) et
  // les couches posees par-dessus interceptent le geste, `<video>` ne le recoit
  // jamais. On ecoute donc la racine, hors boutons, menus, panneaux et piste.
  // Les deux clics ont deja bascule la lecture deux fois (`play()` rend
  // `paused` tout de suite) : l'etat de depart revient, rien a corriger.
  racine.addEventListener('dblclick', (e) => {
    if (e.target.closest('button:not(.l-central), a, input, .l-menu, .st-panneau, .l-panneau-sources, .l-aide, .l-fin, .l-piste, .l-vol-indic')) return;
    plein();
  });

  const brancher = (id, fn) => { const n = el(id); if (n) n.onclick = fn; };
  // Les icônes du repos viennent du jeu dédié (icones.js), pas du HTML dessiné à la main.
  for (const [id, nom] of Object.entries(ICONES_DES_BOUTONS)) {
    const ancienne = el(id)?.querySelector('svg');
    if (ancienne) ancienne.outerHTML = ICONES[nom];
  }
  brancher('l-play', bascule);
  brancher('l-vol-indic', () => { video.muted = !video.muted; });
  brancher('l-mute', () => { video.muted = !video.muted; });
  const curseur = el('l-vol-curseur');
  if (curseur) {
    curseur.oninput = () => {
      video.volume = Number(curseur.value);
      video.muted = video.volume === 0;
    };
  }
  brancher('l-central', bascule);
  brancher('l-plein', plein);
  brancher('l-prec', () => etat.voisins?.precedent?.());
  brancher('l-suiv', () => etat.voisins?.suivant?.());

  // La TV lit, le téléphone pilote (cast.js) : le bouton se pose à côté du plein écran.
  const tele = brancherCast({
    emplacement: $('l-plein')?.parentElement, racine: $('lecteur'),
    courant: () => etat.ref && {
      ref: etat.ref, seconde: position(), audio: etat.audio, duree: etat.totale, serie: !!etat.episode, ...etat.pourLaTv,
    },
    pauseLocale: () => video.pause(),
    reprendreLocale: (s) => { chercher(s); video.play().catch(() => {}); },
    suivant: () => etat.voisins?.suivant?.(),
    precedent: () => etat.voisins?.precedent?.(),
    jeton,
  });
  // Pendant la diffusion, la balise d'ici ne joue pas (l'autoplay d'un nouvel épisode compris).
  video.addEventListener('play', () => { if (tele.actif) video.pause(); });

  // La molette sur l'image règle le son, par crans de 5 %. Pas dans les
  // panneaux et les menus : là, elle fait défiler ce qu'on lit. Ctrl+molette
  // reste le zoom du navigateur.
  racine.addEventListener('wheel', (e) => {
    if (e.ctrlKey || !e.deltaY) return;
    if (e.target.closest?.('.st-panneau, .l-panneau-sources, .l-aide, .l-menu')) return;
    if (document.getElementById('v-lecture')?.hidden) return;
    e.preventDefault();
    if (e.deltaY < 0) { video.muted = false; video.volume = Math.min(1, video.volume + 0.05); }
    else video.volume = Math.max(0, video.volume - 0.05);
  }, { passive: false });

  document.addEventListener('fullscreenchange', () => {
    racine.classList.toggle('en-plein', !!document.fullscreenElement);
    pose('l-plein', 'innerHTML', document.fullscreenElement ? ICONES.reduire : ICONES.plein);
  });

  // Les raccourcis sont posés plus bas, une fois la séance et la vitesse
  // déclarées : ils ont besoin des deux.

  /** Redemande la durée tant qu'elle manque, quelques fois puis abandonne. */
  function reclamerDuree(ref, essai = 0) {
    if (essai > 20) return;

    // ⚠️ LE PLAFOND D'ESSAIS N'ÉTAIT CONSULTÉ QUE DANS LA BRANCHE RÉUSSIE.
    //
    // Une coupure réseau (Wi-Fi qui lâche, DNS qui rate, serveur qui redémarre)
    // ne rend pas une réponse sans durée — elle fait REJETER la promesse, et
    // tombe dans le `.catch`. Un `.catch(() => {})` y arrêtait la boucle NET,
    // sans avoir consommé le moindre essai sur les vingt permis, alors qu'un
    // accroc réseau mérite exactement la même reprise qu'une réponse « pas
    // encore prête ». Les deux chemins reprogramment donc désormais le même
    // rappel, au même rythme.
    const reessayer = () => {
      if (etat.ref !== ref) return; // une autre source a pris la main
      // L'attente s'allonge peu a peu : rapide au debut, patiente ensuite.
      setTimeout(() => reclamerDuree(ref, essai + 1), Math.min(2000 + essai * 800, 8000));
    };

    fetch(base(`/api/duree/${ref}`), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { duree: null }))
      .then(({ duree }) => {
        if (etat.ref !== ref) return; // une autre source a pris la main
        if (duree) {
          etat.totale = duree;
          etat.approx = false;
          peindre();
          // Les bornes n'ont pas pu partir sans durée (voir `reclamerBornes`) :
          // la voilà, on les demande maintenant.
          if (!bornes) reclamerBornes(etat.episode, duree);
          return;
        }
        reessayer();
      })
      .catch(reessayer);
  }

  /**
   * Réclame la planche de vignettes. Sa fabrication demande de lire le fichier
   * entier : on repasse donc à intervalles espacés, et on renonce au bout d'un
   * moment plutôt que d'interroger indéfiniment.
   */
  function reclamerApercu(ref, essai = 0) {
    if (essai > 12) return;
    const T = totale();
    if (!T) { setTimeout(() => reclamerApercu(ref, essai), 4000); return; }

    // L'ÉPISODE part avec la demande : c'est lui qui identifie la planche, pas
    // le lien. Sans ce paramètre, le serveur ne savait pas de quoi on parlait
    // et rendait « pas d'aperçu » à chaque fois — pour tout le monde.
    fetch(avecAcces(base(`/api/apercus/${ref}?duree=${Math.round(T)}`
      + (etat.episode ? `&ep=${encodeURIComponent(etat.episode)}` : ''))), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { apercu: null }))
      .then(({ apercu }) => {
        if (etat.ref !== ref) return; // une autre source a pris la main
        if (!apercu) { setTimeout(() => reclamerApercu(ref, essai + 1), 20_000); return; }
        etat.apercu = apercu;
        const vue = el('l-bulle')?.querySelector('.l-bulle-vue');
        if (vue) vue.style.backgroundImage = `url("${base(apercu.fichier)}")`;
        // La classe `avec-vue` n'est plus posée ici : c'est le survol qui
        // décide, survol par survol, si cette planche a le droit d'être
        // montrée. La poser d'avance revenait à promettre une image avant
        // d'avoir vérifié qu'elle correspondait au fichier.

        // ⚠️ UNE PLANCHE « SUPPOSÉE » EST PROVISOIRE.
        //
        // C'est une planche d'avant le correctif : elle a de vraies images
        // mais ne dit pas quel instant chacune montre, si bien qu'on ne peut
        // que les répartir au jugé. Le serveur, en nous la donnant, en a
        // commandé une neuve. On la garde sous la main pour que la barre ne
        // soit pas vide, et on repasse — lentement, il n'y a plus d'urgence —
        // pour prendre la version mesurée dès qu'elle sort. Sans cela, la
        // correction n'arriverait qu'à la prochaine ouverture du lecteur.
        if (apercu.echelle === 'supposee') {
          setTimeout(() => reclamerApercu(ref, essai + 1), 60_000);
        }
      })
      // ⚠️ MÊME PANNE QUE `reclamerDuree` : LE PLAFOND NE VALAIT QUE POUR LA
      // RÉPONSE RÉUSSIE.
      //
      // Un `fetch` qui échoue (coupure réseau, serveur qui redémarre en plein
      // sondage) tombait dans un `.catch(() => {})` qui arrêtait la boucle sans
      // avoir compté le moindre essai sur les douze permis — alors qu'un
      // accroc passager mérite la même reprise que « pas encore d'aperçu »,
      // au même délai de vingt secondes.
      .catch(() => {
        if (etat.ref !== ref) return; // une autre source a pris la main
        setTimeout(() => reclamerApercu(ref, essai + 1), 20_000);
      });
  }

  // --- passer l'intro, passer la fin -----------------------------------------
  //
  // Un bouton qui n'existe que pendant le générique. Un bouton permanent
  // inviterait à cliquer au hasard ; celui-ci ne propose que ce qui a du sens
  // à l'instant où on le voit.
  //
  // Pendant la fin, il ne dit pas « passer » mais « épisode suivant » : c'est
  // ce qu'on veut vraiment faire à ce moment-là, et la source du suivant a
  // justement été préparée pendant les dernières minutes.
  //
  // Le vote part d'ICI, pas de la console : c'est le même bouton, à l'endroit
  // où l'on regarde. Aller chercher une commande ailleurs pendant que l'intro
  // défile, personne ne le fait.
  let bornes = null;
  let dernierEtatSaut = '';
  let vueSeance = null;   // ce que la séance rapporte : la main, les votes, le seuil
  let voterSeance = null; // branché par la séance ; absent, le bouton agit seul

  // -------------------------------------------------------------------------
  // VITESSE DE LECTURE
  //
  // ⚠️ ELLE NE PEUT PAS ÊTRE LIBRE PENDANT UNE SÉANCE, ET CE N'EST PAS UNE
  // LIMITE QU'ON LÈVERA UN JOUR.
  //
  // La séance garde toute une salle sur la même image : quand quelqu'un
  // dérive, elle corrige en jouant 7 % plus vite ou plus lentement le temps
  // que l'écart se résorbe (`seance.js`, `RATTRAPAGE`). Lire à 1,5× pendant
  // ce temps, c'est dériver à chaque seconde plus vite que la correction ne
  // rattrape : on se ferait ramener en arrière sans fin, et le film
  // deviendrait injouable pour soi comme pour les autres.
  //
  // Deux personnes qui ne regardent pas à la même vitesse ne regardent pas
  // ensemble. Le lecteur refuse donc, et le DIT — plutôt que de laisser la
  // séance écraser le réglage une seconde plus tard sans explication.
  // -------------------------------------------------------------------------
  const CLE_VITESSE = '14d:vitesse';

  /** La vitesse retenue de la dernière fois, ou 1 si rien de valable. */
  function vitesseRetenue() {
    try {
      const v = Number(localStorage.getItem(CLE_VITESSE));
      // Un cran inconnu (réglage d'une ancienne version, valeur bidouillée)
      // retombe sur 1 : mieux vaut la vitesse normale qu'une vitesse dont
      // aucun bouton du menu ne rendrait compte.
      return VITESSES.includes(v) ? v : 1;
    } catch {
      return 1; // stockage refusé
    }
  }

  /** Le bouton porte la vitesse en toutes lettres, et se marque hors norme. */
  function afficherVitesse() {
    const b = el('l-vit');
    if (!b) return;
    const mot = String(etat.vitesse).replace('.', ',');
    b.textContent = mot + '×';
    b.classList.toggle('hors-norme', etat.vitesse !== 1);
    b.setAttribute('aria-label', 'Vitesse de lecture : ' + mot + ' fois');
  }

  /**
   * Applique la vitesse voulue à la balise.
   *
   * ⚠️ À rappeler après CHAQUE nouvelle source : `load()` remet `playbackRate`
   * à 1 sans rien dire, et le réglage se perdait à chaque épisode.
   */
  function appliquerVitesse() {
    if (video.playbackRate !== etat.vitesse) video.playbackRate = etat.vitesse;
  }

  /**
   * Change la vitesse, ou explique pourquoi c'est refusé.
   *
   * @returns {boolean} `false` quand la séance l'interdit.
   */
  function poserVitesse(v) {
    if (voterSeance) {
      annoncer('La vitesse suit la séance — tout le monde regarde à la même.');
      return false;
    }
    etat.vitesse = VITESSES.includes(v) ? v : 1;
    appliquerVitesse();
    try { localStorage.setItem(CLE_VITESSE, String(etat.vitesse)); } catch { /* tant pis */ }
    afficherVitesse();
    peindreMenuVitesse();
    return true;
  }

  function peindreMenuVitesse() {
    menu('l-vit', 'l-menu-vitesse',
      VITESSES.map((v) => ({
        libelle: v === 1 ? 'Normale' : String(v).replace('.', ',') + '×',
        valeur: v,
        actif: v === etat.vitesse,
      })).concat(entreesReperes()),
      ({ valeur }) => { if (typeof valeur === 'string') reperer(valeur); else poserVitesse(valeur); });
  }

  // -------------------------------------------------------------------------
  // LES REPÈRES À LA MAIN (admin seulement)
  //
  // Toji : quand l'automatique se trompe, il met en pause au bon endroit et
  // clique « Début de l'intro ici », etc. La seconde EXACTE de la pause, au
  // dixième. Les deux bornes d'un segment posées, le PUT part ; le serveur met
  // son cache à jour, et toute la salle l'a au prochain `/api/sauts`.
  // -------------------------------------------------------------------------
  let admin = false;
  let reperes = {}; // `intro.debut` → secondes, posées mais pas encore envoyées
  let reperesDe = null; // l'épisode où ils ont été posés
  const REPERES = [
    ['intro', 'debut', 'Début de l’intro ici'], ['intro', 'fin', 'Fin de l’intro ici'],
    ['recap', 'debut', 'Début du récap ici'], ['recap', 'fin', 'Fin du récap ici'],
    ['outro', 'debut', 'Début du générique ici'], ['outro', 'fin', 'Fin du générique ici'],
  ];
  const mmss = (t) => `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, '0')}`;
  // Le jeton en EN-TÊTE, pas dans l'adresse (`avecAcces`) : les routes admin
  // refusent un jeton en clair, et l'activité Discord n'a pas de cookie.
  const avecJeton = (extra = {}) => {
    const j = jeton?.();
    return { credentials: 'same-origin', ...extra, headers: { ...(extra.headers ?? {}), ...(j ? { authorization: `Bearer ${j}` } : {}) } };
  };
  Promise.resolve()
    .then(() => fetch(base('/api/moi'), avecJeton()))
    .then((r) => (r?.ok ? r.json() : null))
    .then((m) => { admin = m?.droits?.admin === true; if (admin) peindreMenuVitesse(); })
    .catch(() => { /* pas d'identité : pas de repères */ });

  function entreesReperes() {
    if (!admin || !etat.episode) return [];
    return [
      { libelle: `Repères : ${bornes?.source ?? 'aucune'}`, valeur: 'rep:etat' },
      ...REPERES.map(([q, b, mot]) => {
        const v = reperesDe === etat.episode ? reperes[`${q}.${b}`] : null;
        return { libelle: v == null ? mot : `${mot} · ${mmss(v)}`, valeur: `rep:${q}:${b}` };
      }),
      ...(bornes?.source === 'manuel' ? [{ libelle: 'Revenir à l’automatique', valeur: 'rep:auto' }] : []),
    ];
  }

  function reperer(valeur) {
    const [, quoi, borne] = valeur.split(':');
    const cible = etat.episode;
    if (!admin || !cible || quoi === 'etat') return;
    if (reperesDe !== cible) { reperes = {}; reperesDe = cible; }
    let envoi = null;
    if (quoi === 'auto') envoi = { method: 'DELETE' };
    else {
      // `position()` et pas `video.currentTime` seul : la balise peut repartir
      // d'un décalage (reprise côté serveur), les bornes parlent en temps d'épisode.
      reperes[`${quoi}.${borne}`] = Math.round(position() * 10) / 10;
      const debut = reperes[`${quoi}.debut`];
      const fin = reperes[`${quoi}.fin`];
      annoncer(`${{ intro: 'Intro', recap: 'Récap' }[quoi] ?? 'Générique'} : ${debut == null ? '?' : mmss(debut)} → ${fin == null ? '?' : mmss(fin)}`);
      if (debut != null && fin != null) {
        envoi = { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [quoi]: { debut, fin } }) };
      }
    }
    peindreMenuVitesse();
    if (!envoi) return;
    const [serie, saison = '0', episode = '0'] = String(cible).split(':');
    fetch(base(`/api/admin/sauts/${encodeURIComponent(serie)}/${saison}/${episode}`), avecJeton(envoi))
      .then((r) => r.json().catch(() => ({})).then((d) => { if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); }))
      .then(() => {
        delete reperes[`${quoi}.debut`];
        delete reperes[`${quoi}.fin`];
        annoncer(quoi === 'auto' ? 'Repères : retour à l’automatique' : 'Repères enregistrés');
        // Tout de suite, sans recharger : les bornes de l'épisode se relisent.
        if (etat.episode === cible) reclamerBornes(cible, etat.totale);
      })
      .catch((err) => annoncer(`Repères refusés — ${err?.message ?? err}`));
  }

  // -------------------------------------------------------------------------
  // LES RACCOURCIS
  //
  // La table vit dans `raccourcis.js` — éprouvée, et affichable. Ici on ne
  // fait qu'exécuter ce qu'elle nomme.
  //
  // ⚠️ CTRL, META ET ALT NE NOUS APPARTIENNENT PAS. `actionPour` les écarte,
  // et c'était un vrai défaut : « Ctrl+F » passait en plein écran au lieu
  // d'ouvrir la recherche du navigateur, « Ctrl+L » sautait trente secondes
  // au lieu de viser la barre d'adresse. Le `preventDefault` rendait ces
  // gestes silencieusement inopérants — le pire des symptômes.
  // -------------------------------------------------------------------------
  const GESTES = {
    lecture: () => bascule(),
    avancer: (n) => chercher(positionVue() + n),
    reculer: (n) => chercher(positionVue() - n),
    debut: () => chercher(0),
    // Pas tout à fait la fin : s'y poser exactement déclenche `ended`, donc
    // l'épisode suivant. On s'arrête cinq secondes avant.
    fin: () => { const T = totale(); if (T > 5) chercher(T - 5); },
    proportion: (part) => { const T = totale(); if (T > 0) chercher(T * part); },

    plusVite: () => poserVitesse(vitesseSuivante(etat.vitesse, +1)),
    moinsVite: () => poserVitesse(vitesseSuivante(etat.vitesse, -1)),
    vitesseNormale: () => poserVitesse(1),

    muet: () => { video.muted = !video.muted; },
    plusFort: (n) => { video.muted = false; video.volume = Math.min(1, video.volume + n); },
    moinsFort: (n) => { video.volume = Math.max(0, video.volume - n); },

    basculerST: () => basculerSousTitres(),
    stEnRetard: (n) => decalerSousTitres(+n),
    stEnAvance: (n) => decalerSousTitres(-n),

    plein: () => plein(),
    vignette: () => detacher(),
    aide: () => basculerAide(),
  };

  // -------------------------------------------------------------------------
  // L'ÉCRAN VERROUILLÉ, LE CASQUE, LES TOUCHES MÉDIA (Media Session)
  //
  // Toji (20/10) : « quand on est sur la PWA, mes boutons de son soient
  // connectés à la PWA en priorité ».
  //
  // ⚠️ LES TOUCHES DE VOLUME PHYSIQUES NE PARVIENNENT À AUCUNE PAGE WEB. Android
  // et iOS les gardent pour le système : elles règlent le volume média de
  // l'appareil, qui s'applique déjà au son du lecteur. Ce qu'une page PEUT
  // recevoir, ce sont les commandes média — écran verrouillé, centre de
  // contrôle, casque Bluetooth, touches lecture/suivant d'un clavier. On les
  // branche ici sur les mêmes gestes que les boutons du lecteur.
  // -------------------------------------------------------------------------
  const sessionMedia = typeof navigator !== 'undefined' ? navigator.mediaSession : null;
  if (sessionMedia) {
    const action = (nom, fn) => { try { sessionMedia.setActionHandler(nom, fn); } catch { /* action inconnue de ce navigateur */ } };
    action('play', () => { if (video.paused) bascule(); });
    action('pause', () => { if (!video.paused) bascule(); });
    action('seekbackward', (d) => chercher(positionVue() - (d?.seekOffset || 10)));
    action('seekforward', (d) => chercher(positionVue() + (d?.seekOffset || 10)));
    action('seekto', (d) => { if (Number.isFinite(d?.seekTime)) chercher(d.seekTime); });
    action('previoustrack', () => etat.voisins?.precedent?.());
    action('nexttrack', () => etat.voisins?.suivant?.());
  }
  /** Titre, œuvre et affiche de ce qui joue, pour l'écran verrouillé. */
  function poserSessionMedia({ titre, oeuvre, affiche } = {}) {
    if (!sessionMedia || typeof MediaMetadata === 'undefined') return;
    try {
      sessionMedia.metadata = new MediaMetadata({
        title: titre || oeuvre || '',
        artist: oeuvre && oeuvre !== titre ? oeuvre : '',
        artwork: affiche ? [{ src: new URL(affiche, location.href).href }] : [],
      });
    } catch { /* une affiche illisible ne doit jamais gêner la lecture */ }
  }
  /** La barre de l'écran verrouillé suit la nôtre (même position, même durée). */
  // ⚠️ `navigator.mediaSession` relu ici, pas la constante : `peindre` peut
  // tourner avant que la constante ne soit initialisée (zone morte de `const`).
  // ⚠️ PAS À CHAQUE IMAGE. `peindre` tourne à chaque frame : chaque appel
  // traversait Chrome jusqu'au service Windows des médias (NPSMSvc) et à tous
  // ses abonnés (widgets « en cours de lecture ») — 57 % du CPU et des gels de
  // 5 s chez Toji (22/10). Le système extrapole seul la position à partir de
  // la vitesse : on ne la repose que si elle a dérivé (saut, pause, vitesse).
  let positionMedia = null;
  function majPositionMedia(T) {
    const sm = typeof navigator !== 'undefined' ? navigator.mediaSession : null;
    if (!sm?.setPositionState || !(T > 0)) return;
    const pos = Math.min(T, Math.max(0, positionVue()));
    const vitesse = video.playbackRate > 0 ? video.playbackRate : 1;
    const maintenant = performance.now();
    const p = positionMedia;
    if (p && p.T === T && p.vitesse === vitesse && p.arret === video.paused) {
      const attendue = p.pos + (video.paused ? 0 : (maintenant - p.t) / 1000 * vitesse);
      if (Math.abs(attendue - pos) < 1) return;
    }
    positionMedia = { T, pos, vitesse, arret: video.paused, t: maintenant };
    try {
      sm.setPositionState({ duration: T, position: pos, playbackRate: vitesse });
    } catch { /* état refusé (durée qui change) : le prochain rafraîchissement le reposera */ }
  }

  document.addEventListener('keydown', (e) => {
    const vue = document.getElementById('v-lecture');
    if (!vue || vue.hidden) return;
    const cible = e.target;

    // ⚠️ ÉCHAP SE TRAITE AVANT LE GARDE-FOU DES CHAMPS, ET C'EST NÉCESSAIRE.
    //
    // Le panneau de sous-titres est FAIT de champs — des curseurs, des listes.
    // Au clavier seul, on y entre par tabulation, et le garde-fou juste en
    // dessous rendait la main au champ : la touche n'arrivait jamais jusqu'à
    // nous, et le panneau ne se fermait pas. C'était exactement l'impasse
    // décrite. Une SAISIE DE TEXTE garde en revanche sa touche : Échap y annule
    // ce qu'on est en train d'écrire, et ce geste-là ne nous appartient pas.
    if (e.key === 'Escape' && !saisieDeTexte(cible)) {
      // Rien d'ouvert : on ne mange pas la touche. Elle sert ailleurs — sortir
      // du plein écran, fermer une modale de la page.
      if (!echapper()) return;
      e.preventDefault();
      reveiller();
      return;
    }

    // Une frappe dans un champ appartient au champ. `isContentEditable`
    // couvre en plus les zones de saisie riches, qui ne sont pas des INPUT.
    if (cible && (/^(INPUT|TEXTAREA|SELECT)$/.test(cible.tagName) || cible.isContentEditable)) return;

    // ⚠️ APRÈS le garde-fou des champs, AVANT la table des raccourcis : un
    // menu déplié a le clavier (voir « UN MENU OUVERT MANGE LES FLÈCHES »).
    if (clavierDuMenu(e)) return;

    const quoi = actionPour(e);
    if (!quoi) return;
    const geste = GESTES[quoi.action];
    if (!geste) return;
    e.preventDefault();
    reveiller();
    geste(quoi.valeur);
  });

  /**
   * Le style de l'attente et de la jauge, posé par le code qui les fabrique.
   *
   * Ces deux-là n'existent que pendant un vote : les laisser dans la feuille de
   * la page, c'est laisser derrière soi des règles dont plus rien ne dit à quoi
   * elles servent le jour où le vote change de forme.
   */
  function poserStyleSaut() {
    if (document.getElementById('l-saut-style')) return;
    const style = document.createElement('style');
    style.id = 'l-saut-style';
    // La position, le fond et la police restent ceux de `.l-saut` : rien ici
    // ne redéfinit ce qui existe déjà, on ne fait qu'ajouter l'attente.
    style.textContent = `
      .l-saut { display: inline-flex; align-items: center; gap: 9px; }
      /* En attente, ce n'est plus un bouton : il rend compte. Le survol ne
         l'allume donc pas, et le curseur ne promet pas de clic. */
      .l-saut.attente { cursor: default; opacity: .82; }
      .l-saut.attente:hover {
        background: rgba(10,10,12,.82); color: var(--texte);
        border-color: rgba(255,255,255,.28);
      }
      .l-jauge { display: inline-flex; gap: 3px; }
      .l-jauge i { width: 4px; height: 11px; border-radius: 1px; background: rgba(255,255,255,.24); }
      /* currentColor : au survol le bouton s'inverse, les barres suivent sans
         qu'on ait à décrire deux fois la même couleur. */
      .l-jauge i.pleine { background: currentColor; }
    `;
    document.head.appendChild(style);
  }
  poserStyleSaut();

  /** Une petite barre par voix attendue, allumée à mesure qu'elles arrivent. */
  function jauge(voix, seuil) {
    const boite = document.createElement('span');
    boite.className = 'l-jauge';
    // Au-delà de dix barres on ne compte plus, on regarde : chacune vaut alors
    // une fraction du seuil. Le compte écrit juste à côté reste la mesure
    // exacte, la jauge n'est là que pour dire « on y est presque ».
    const barres = Math.min(seuil, 10);
    const pleines = seuil > barres ? Math.round((voix / seuil) * barres) : voix;
    for (let i = 0; i < barres; i += 1) {
      const barre = document.createElement('i');
      if (i < pleines) barre.className = 'pleine';
      boite.appendChild(barre);
    }
    return boite;
  }

  /** Ce que le minutage propose à cette seconde : rien, l'intro, la suite, la fin. */
  function sautCourant() {
    const t = position();
    const dans = (b) => b && t >= b.debut && t < b.fin - 0.5;
    if (dans(bornes?.recap)) return 'recap';
    if (dans(bornes?.intro)) return 'intro';
    // L'outro ne propose plus l'épisode suivant dès son début : ça coupait la
    // scène post-générique. La carte suivante vient à `momentEpisodeSuivant`.
    if (dans(bornes?.outro)) return 'generique';
    // En séance, un vote ouvert se voit et se rejoint même sans bornes locales :
    // l'état du vote vient du serveur, pas du minutage de ce poste.
    return voteOuvert(vueSeance);
  }

  // Le bouton se montre SEUL dix secondes quand un segment commence, puis
  // suit les commandes (CSS `.lecteur.inactif .l-saut:not(.seul)`).
  const SAUT_SEUL_MS = 10_000;
  let segmentAnnonce = '';
  let minuteurSautSeul = 0;
  function annoncerSegment(bouton, quoi) {
    const cle = `${quoi}|${etat.episode}`;
    if (cle === segmentAnnonce) return;
    segmentAnnonce = cle;
    clearTimeout(minuteurSautSeul);
    bouton.classList.remove('seul');
    void bouton.offsetWidth; // relance l'animation de la barre de temps
    bouton.style.setProperty('--l-saut-duree', `${SAUT_SEUL_MS}ms`);
    bouton.classList.add('seul');
    minuteurSautSeul = setTimeout(() => bouton.classList.remove('seul'), SAUT_SEUL_MS);
  }

  // Hors de `.l-bas`, le bouton se cale au-dessus de la piste, quelle que soit
  // la hauteur des commandes (téléphone, paysage, temps sur sa ligne) : un
  // nombre écrit en dur redeviendrait faux à la prochaine retouche (porté du 14e).
  function placerSaut(bouton) {
    const piste = el('l-piste');
    if (!piste) return;
    const ecart = racine.getBoundingClientRect().bottom - piste.getBoundingClientRect().top;
    if (ecart > 0) bouton.style.bottom = `${Math.round(ecart + 12)}px`;
  }

  function peindreSaut() {
    const bouton = el('l-saut');
    if (!bouton) return;
    const vue = etatDuSaut(sautCourant(), vueSeance);

    // L'empreinte porte le texte ET le compte, pas seulement le geste proposé :
    // une voix qui arrive ne change pas la nature du bouton mais doit s'y voir.
    // Sans cette comparaison, on refait le DOM soixante fois par seconde.
    const empreinte = vue ? `${vue.quoi}|${vue.texte}|${vue.voix}/${vue.seuil}` : '';
    if (empreinte === dernierEtatSaut) return;
    dernierEtatSaut = empreinte;

    if (!vue) { bouton.hidden = true; bouton.disabled = false; segmentAnnonce = ''; return; }
    bouton.hidden = false;
    placerSaut(bouton);
    annoncerSegment(bouton, vue.quoi);
    bouton.disabled = vue.attente;
    bouton.classList.toggle('attente', vue.attente);
    bouton.textContent = vue.texte;
    if (vue.ouvert) bouton.appendChild(jauge(vue.voix, vue.seuil));
  }

  /**
   * Fait le saut.
   *
   * Appelé par le clic de qui tranche seul, et par la séance quand un vote
   * vient d'être emporté — chez tout le monde à la fois, sans que personne
   * n'ait à valider : sans quoi ce ne serait plus un vote.
   */
  function executerSaut(quoi) {
    if (quoi === 'intro') {
      if (bornes?.intro) chercher(bornes.intro.fin);
      return;
    }
    if (quoi === 'recap') {
      if (bornes?.recap) chercher(bornes.recap.fin);
      return;
    }
    // À la fin, enchaîner vaut mieux que sauter : la source du suivant a été
    // préparée pendant les dernières minutes, il part sans attendre.
    if (quoi === 'suivant' && etat.voisins?.suivant) { etat.voisins.suivant(); return; }
    if (['suivant', 'fin', 'generique'].includes(quoi) && bornes?.outro) chercher(bornes.outro.fin);
  }

  brancher('l-saut', () => {
    const vue = etatDuSaut(sautCourant(), vueSeance);
    // Le vote court déjà avec ma voix : il n'y a rien à recliquer.
    if (!vue || vue.attente) return;
    if (!vue.seul) { voterSeance?.(vue.quoi); return; }
    executerSaut(vue.quoi);
  });

  // -------------------------------------------------------------------------
  // LE CARTON DE FIN D'ÉPISODE
  //
  // Voir `etatFinDePartie` en tête de fichier pour le POURQUOI. Ici, rien que
  // la mécanique : un carton, un décompte, et une annulation qui tient.
  //
  // Le carton est fabriqué ICI plutôt que posé dans `index.html`, comme le
  // style du bouton de saut (`poserStyleSaut`) : il n'existe que quelques
  // secondes par épisode, et le laisser dans la page mettrait dix lignes de
  // balisage sous les yeux de qui lit le lecteur sans jamais dire à quoi elles
  // servent. S'il se trouve déjà dans la page (un jour, pour la feuille de
  // style), on le réutilise tel quel.
  // -------------------------------------------------------------------------

  /** Annulé pour CET épisode : le carton ne revient pas tant qu'on ne bouge pas. */
  let finAnnulee = false;
  // L'enchaînement est parti pour CET épisode : on ne le relance pas à chaque
  // `timeupdate` restant (ni à `ended`, qui suit `durée - 0,5 s`).
  let suiteLancee = false;
  let empreinteFin = '';
  // La source posée a-t-elle joué ? Sans ça, un `ended`/`timeupdate` encore en
  // file de l'épisode QUITTÉ, lu avec les voisins du nouveau, enchaînait aussitôt
  // une seconde fois (mesuré au cran mse : E2 → E3 → S2E1 en 110 ms).
  let aJoue = false;
  video.addEventListener('playing', () => { aJoue = true; });

  function poserStyleFin() {
    if (document.getElementById('l-fin-style')) return;
    const style = document.createElement('style');
    style.id = 'l-fin-style';
    // Même matière que `.l-aide` et `.l-menu` : fond presque opaque, filet
    // clair, ombre portée. Un carton qui ne ressemblerait à rien d'autre du
    // lecteur se lirait comme une fenêtre du navigateur.
    style.textContent = `
      .l-fin {
        position: absolute; right: 24px; bottom: 104px; z-index: 9;
        width: min(320px, calc(var(--vw1, 1vw) * 100 - 48px));
        background: rgba(10,10,12,.97); border: 1px solid rgba(255,255,255,.28);
        border-radius: 8px; padding: 14px 16px 13px;
        box-shadow: 0 18px 50px rgba(0,0,0,.66);
        overflow: hidden;
      }
      .l-fin[hidden] { display: none; }
      .l-fin-vignette {
        display: block; width: calc(100% + 32px); margin: -14px -16px 10px;
        aspect-ratio: 16 / 9; object-fit: cover; background: #000;
      }
      .l-fin-vignette[hidden] { display: none; }
      .l-fin-sous { margin: 0 0 8px; font-size: 13px; line-height: 1.35; opacity: .9; }
      .l-fin-sous:empty { display: none; }
      /* Le décompte se VOIT dans le bouton : les secondes à gauche, un remplissage
         sur toute la hauteur qui avance de gauche à droite (scaleX, animation
         liée au temps de la vidéo : elle se fige en pause). */
      .l-fin-oui { position: relative; overflow: hidden; display: flex; align-items: center; gap: 10px; }
      .l-fin-rempli {
        position: absolute; inset: 0; background: rgba(255,255,255,.34);
        transform-origin: left; transform: scaleX(0); pointer-events: none;
      }
      .l-fin-sec, .l-fin-mot { position: relative; }
      .l-fin-sec { min-width: 1.5em; text-align: left; font-variant-numeric: tabular-nums; font-weight: 400; }
      .l-fin-sec:empty { display: none; }
      .l-fin-mot { flex: 1; text-align: center; }
      .l-fin-titre {
        margin: 0 0 4px; font-family: "Shippori Mincho", Georgia, serif;
        font-size: 14px; font-weight: 700;
      }
      .l-fin-note { margin: 0 0 12px; font-size: 12.5px; line-height: 1.45; opacity: .74; }
      .l-fin-gestes { display: flex; gap: 8px; align-items: center; }
      .l-fin-gestes button {
        font-family: "Bebas Neue", "Archivo Narrow", sans-serif;
        font-size: 15px; letter-spacing: .06em; cursor: pointer;
        padding: 9px 16px; border-radius: 4px; border: 1px solid rgba(255,255,255,.28);
        background: rgba(255,255,255,.06); color: inherit;
        transition: background .15s ease, border-color .15s ease;
      }
      .l-fin-oui { flex: 1; }
      .l-fin-gestes button:hover { background: #fff; color: #111; border-color: #fff; }
      /* En attente, le bouton rend compte : ni curseur de clic, ni survol qui
         s'allume. Exactement la règle de \`.l-saut.attente\`. */
      .l-fin-gestes button[disabled] { cursor: default; opacity: .82; }
      .l-fin-gestes button[disabled]:hover {
        background: rgba(255,255,255,.06); color: inherit; border-color: rgba(255,255,255,.28);
      }
      /* « Voir le générique » (étude UI 22/10, point 7) : le geste secondaire, en
         voile translucide sans filet, pour que « Suivant » reste le seul bouton
         cerné — donc le principal. */
      .l-fin-gestes .l-fin-non { flex: none; white-space: nowrap; background: rgba(255,255,255,.14); border-color: transparent; }
    `;
    document.head.appendChild(style);
  }

  /** Le carton, fabriqué au premier besoin. */
  let carton = null;
  function assurerCarton() {
    if (carton) return carton;
    poserStyleFin();
    let boite = el('l-fin');
    if (!boite) {
      boite = document.createElement('div');
      boite.id = 'l-fin';
      racine.appendChild(boite);
    }
    boite.className = 'l-fin';
    boite.hidden = true;
    // `group` et non `dialog` : il ne prend pas le focus de force et on peut
    // continuer d'utiliser les commandes derrière. `aria-live` porte le
    // décompte, qui change sans que rien ne soit cliqué.
    boite.setAttribute('role', 'group');
    boite.setAttribute('aria-label', 'Fin de l’épisode');
    boite.innerHTML = '<img class="l-fin-vignette" id="l-fin-vignette" alt="" hidden>'
      + '<p class="l-fin-titre" id="l-fin-titre"></p>'
      + '<p class="l-fin-sous" id="l-fin-sous"></p>'
      + '<p class="l-fin-note" id="l-fin-note" aria-live="polite"></p>'
      + '<div class="l-fin-gestes">'
      + '<button type="button" class="l-fin-oui" id="l-fin-oui"><i class="l-fin-rempli" id="l-fin-rempli" aria-hidden="true"></i>'
      + '<b class="l-fin-sec" id="l-fin-sec"></b><span class="l-fin-mot" id="l-fin-mot"></span></button>'
      + '<button type="button" class="l-fin-non" id="l-fin-non">Voir le générique</button>'
      + '</div>';
    // Un clic dans le carton ne doit pas atteindre le lecteur : sinon annuler
    // le décompte mettrait aussi la vidéo en pause.
    boite.addEventListener('click', (e) => e.stopPropagation());
    el('l-fin-oui').onclick = () => {
      const vue = vueFin();
      if (!vue || vue.attente) return;
      if (!vue.seul) { voterSeance?.(vue.quoi); peindreFin(); return; }
      suiteLancee = true;
      fermerCarton();
      executerSaut('suivant');
    };
    el('l-fin-non').onclick = () => annulerFin();
    carton = boite;
    return carton;
  }

  /**
   * Seconde où la carte « Épisode suivant » paraît (Toji, 21/10 : « comme sur
   * Netflix ») : la règle des bornes (`momentEpisodeSuivant`), sinon les
   * `CARTE_SANS_BORNES_S` dernières secondes — pas sur une durée approchée,
   * qui la ferait paraître en plein épisode.
   */
  function momentCarte() {
    // Durée sûre : toujours 10 s avant la fin, que des bornes existent ou non
    // (le compte à rebours doit valoir 10 → 0, pas dépendre du générique).
    const T = totale();
    if (T > 0 && !etat.approx) return T - CARTE_SANS_BORNES_S;
    return momentEpisodeSuivant(bornes) ?? Infinity;
  }
  // La toute dernière seconde : l'enchaînement part à `durée - 0,5 s` quand la
  // durée est sûre, à `ended` sinon.
  const finSure = () => (!etat.approx && totale() > 0 ? totale() - 0.5 : null);

  const vueFin = () => etatFinDePartie({
    fini: aJoue && (video.ended || position() >= momentCarte()),
    voisinSuivant: !!etat.voisins?.suivant,
    seance: vueSeance,
    annule: finAnnulee,
  });

  /**
   * « Voir le générique » (l'ancien « Annuler », étude UI 22/10 point 7 : deux
   * boutons comme Netflix) : le carton part, et RIEN ne part avec lui — la
   * vidéo continue, le générique se regarde jusqu'au bout.
   *
   * Le drapeau tient jusqu'au prochain épisode ou au prochain déplacement de
   * la tête de lecture — sans quoi le carton reviendrait au battement suivant,
   * et le clic sur « Annuler » n'aurait servi qu'à le faire clignoter.
   */
  function annulerFin() {
    finAnnulee = true;
    fermerCarton();
    // Les commandes reviennent : on vient de refuser l'enchaînement, il faut
    // pouvoir faire autre chose (revenir en arrière, quitter) sans chercher.
    reveiller();
  }

  function fermerCarton() {
    if (carton) carton.hidden = true;
    rempliAnim?.cancel();
    rempliAnim = null;
  }

  // Le remplissage du bouton « Suivant » : une animation `scaleX` calée sur le
  // temps de la VIDÉO (position recalée à chaque battement, vitesse de lecture
  // suivie, figée en pause) — pas un minuteur qui dérive.
  let rempliAnim = null;
  function animerRempli(debut, fin) {
    const rempli = el('l-fin-rempli');
    if (!rempli?.animate) return;
    const duree = Math.max(0.5, fin - debut) * 1000;
    if (!rempliAnim || rempliAnim.effect.getTiming().duration !== duree) {
      rempliAnim?.cancel();
      rempliAnim = rempli.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: duree, fill: 'both', easing: 'linear' });
      rempliAnim.pause();
    }
    const voulu = Math.min(duree, Math.max(0, (position() - debut) * 1000));
    const enMarche = !video.paused && !video.ended && voulu < duree;
    if (!enMarche || Math.abs((rempliAnim.currentTime ?? 0) - voulu) > 150) rempliAnim.currentTime = voulu;
    rempliAnim.playbackRate = video.playbackRate || 1;
    if (enMarche) rempliAnim.play(); else rempliAnim.pause();
  }

  /**
   * La carte de fin : paraît au moment dit, compte jusqu'à la dernière seconde,
   * puis enchaîne — pour qui tranche seul. En séance sans la main, elle propose
   * de demander et attend la salle (la bascule suit le pilote).
   *
   * Appelée par `timeupdate`, par `ended`, et par `majSeance` — la main peut
   * arriver APRÈS la fin : la carte doit alors enchaîner sans qu'on reclique.
   * Le DOM n'est refait que quand ce qu'elle dit change.
   */
  function peindreFin() {
    const vue = vueFin();
    if (!vue) { fermerCarton(); return; }

    const boite = assurerCarton();
    const info = etat.voisins?.infoSuivant ?? null;
    const empreinte = `${vue.texte}|${vue.attente}|${vue.voix}/${vue.seuil}|${vue.note}|${info?.titre}|${info?.vignette}`;
    const oui = el('l-fin-oui');
    if (boite.hidden || empreinte !== empreinteFin) {
      empreinteFin = empreinte;
      boite.hidden = false;
      pose('l-fin-titre', 'textContent', vue.titre);
      pose('l-fin-sous', 'textContent', info?.titre ?? '');
      const img = el('l-fin-vignette');
      if (img) {
        img.hidden = !info?.vignette;
        if (info?.vignette && img.getAttribute('src') !== info.vignette) img.src = info.vignette;
      }
      pose('l-fin-mot', 'textContent', vue.texte);
      oui.disabled = vue.attente;
      if (vue.ouvert) oui.appendChild(jauge(vue.voix, vue.seuil));
      if (!vue.decompte) pose('l-fin-note', 'textContent', vue.note ?? '');
      // Le focus seulement une fois l'épisode FINI, et s'il traîne hors du
      // lecteur : pendant la lecture, Espace enchaînerait au lieu de mettre en pause.
      if (video.ended && !racine.contains(document.activeElement)) {
        try { oui.focus({ preventScroll: true }); } catch { /* focus refusé */ }
      }
    }

    if (!vue.decompte) { pose('l-fin-sec', 'textContent', ''); rempliAnim?.cancel(); rempliAnim = null; return; }
    const fin = finSure();
    const debut = Math.min(momentCarte(), fin ?? Infinity);
    const reste = fin === null ? 0 : Math.max(0, Math.ceil(fin - position()));
    pose('l-fin-sec', 'textContent', String(reste));
    pose('l-fin-note', 'textContent', '');
    if (fin !== null) animerRempli(debut, fin);
    // ⚠️ PAS D'ENCHAÎNEMENT EN COULISSE (bug SEANCE-2610212351-mlh). Le lecteur
    // mis de côté (retour à la liste, la séance joue toujours) finissait son
    // épisode, lançait le suivant… et reprenait l'écran par-dessus la liste —
    // « Mes réglages » se dérobait sous le clic. Hors de l'écran, le carton
    // attend qu'on revienne.
    if (!suiteLancee && racine?.getClientRects().length && (video.ended || (fin !== null && position() >= fin))) {
      suiteLancee = true;
      fermerCarton();
      executerSaut('suivant');
    }
  }

  // ⚠️ `ended` NE FAISAIT QU'ARRÊTER LA BOUCLE DE RENDU. C'était le seul
  // écouteur de fin de tout le projet — rien, nulle part, ne proposait la
  // suite.
  video.addEventListener('ended', () => { arreterSuivi(); peindreFin(); });
  // Revenu à l'écran après une fin en coulisse : le carton reprend la main.
  try { new ResizeObserver(() => { if (video.ended) peindreFin(); }).observe(racine); } catch { /* sans observateur */ }

  // Bouger la tête de lecture, c'est reprendre la main : le carton n'a plus
  // lieu d'être, et une annulation ne vaut plus pour la fin qu'on va
  // re-atteindre.
  video.addEventListener('seeking', () => {
    finAnnulee = false;
    suiteLancee = false;
    fermerCarton();
  });

  // -------------------------------------------------------------------------
  // L'ÉCRAN D'ERREUR DE LECTURE (étude UI 22/10, point 6)
  //
  // ⚠️ LA PANNE FINALE NE SE DISAIT QUE DANS `l-infos` : « source indisponible
  // — reviens en arrière et réessaie », en petites capitales grises, dans une
  // barre qui s'efface au bout de 2,6 s. L'image restait noire et l'action était
  // à deviner. `brancherReprise` (main.js) appelle désormais `montrerErreur`
  // quand la relance automatique n'a rien donné : un message, un code court (ce
  // qu'on nous rapporte), « Réessayer », et « Autre source » — le panneau de
  // changement de source existant. Ce panneau est réservé au droit `sources` :
  // sans lui, le bouton ne s'affiche pas plutôt que de promettre ce que le
  // serveur refuserait.
  //
  // Fabriqué au premier besoin, comme le carton de fin. Sous les commandes
  // (z-index 3 < 4) : « Retour » reste à portée ; au-dessus de l'image.
  // -------------------------------------------------------------------------
  let ecranErreur = null;
  function assurerEcranErreur() {
    if (ecranErreur) return ecranErreur;
    const style = document.createElement('style');
    style.id = 'l-erreur-style';
    // La matière du carton de fin (`.l-fin`) : un écran du même lecteur.
    style.textContent = `
      .l-erreur {
        position: absolute; inset: 0; z-index: 3;
        display: flex; align-items: center; justify-content: center; padding: 24px;
        background: rgba(0,0,0,.72);
      }
      .l-erreur[hidden] { display: none; }
      .l-erreur-boite {
        width: min(400px, 100%); text-align: center;
        background: rgba(10,10,12,.97); border: 1px solid rgba(255,255,255,.28);
        border-radius: 8px; padding: 22px 22px 18px; box-shadow: 0 18px 50px rgba(0,0,0,.66);
      }
      .l-erreur-titre { margin: 0 0 6px; font-family: "Shippori Mincho", Georgia, serif; font-size: 18px; font-weight: 700; }
      .l-erreur-texte { margin: 0 0 6px; font-size: 13.5px; line-height: 1.5; opacity: .9; }
      .l-erreur-code { margin: 0 0 16px; font-size: 11.5px; letter-spacing: .08em; color: var(--texte-3); font-variant-numeric: tabular-nums; }
      .l-erreur-gestes { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
      .l-erreur-gestes button {
        font-family: "Bebas Neue", "Archivo Narrow", sans-serif;
        font-size: 15px; letter-spacing: .06em; cursor: pointer;
        padding: 9px 18px; border-radius: 4px; border: 1px solid rgba(255,255,255,.28);
        background: rgba(255,255,255,.06); color: inherit;
      }
      .l-erreur-gestes button[hidden] { display: none; }
      .l-erreur-gestes button:hover, .l-erreur-gestes .l-erreur-oui { background: var(--blanc); color: var(--encre); border-color: var(--blanc); }
      .l-erreur-gestes .l-erreur-oui:hover { background: #ddd; border-color: #ddd; }
    `;
    document.head.appendChild(style);
    ecranErreur = document.createElement('div');
    ecranErreur.id = 'l-erreur';
    ecranErreur.className = 'l-erreur';
    ecranErreur.hidden = true;
    ecranErreur.setAttribute('role', 'alertdialog');
    ecranErreur.setAttribute('aria-labelledby', 'l-erreur-titre');
    ecranErreur.setAttribute('aria-describedby', 'l-erreur-texte');
    ecranErreur.innerHTML = '<div class="l-erreur-boite">'
      + '<p class="l-erreur-titre" id="l-erreur-titre">Lecture impossible</p>'
      + '<p class="l-erreur-texte" id="l-erreur-texte"></p>'
      + '<p class="l-erreur-code" id="l-erreur-code"></p>'
      + '<div class="l-erreur-gestes">'
      + '<button type="button" class="l-erreur-oui" id="l-erreur-reessayer">Réessayer</button>'
      + '<button type="button" id="l-erreur-autre">Autre source</button>'
      + '</div></div>';
    // Un clic dans l'écran ne doit pas atteindre le lecteur (lecture/pause).
    ecranErreur.addEventListener('click', (e) => e.stopPropagation());
    racine.appendChild(ecranErreur);
    // Le panneau s'ouvre PAR-DESSUS (z-index 7) : fermé sans choisir, on
    // retrouve l'écran et ses deux gestes au lieu d'une image noire.
    el('l-erreur-autre').onclick = () => { if (!panneauSourcesOuvert()) basculerPanneauSources(); };
    return ecranErreur;
  }

  /**
   * @param {object} o
   * @param {string} o.message ce qui s'est passé, en clair.
   * @param {string} [o.code] code court, recopiable.
   * @param {(() => void)|null} [o.reessayer] relance la lecture ; absent, pas de bouton.
   */
  function montrerErreur({ message = '', code = '', reessayer = null } = {}) {
    const ecran = assurerEcranErreur();
    pose('l-erreur-texte', 'textContent', message);
    pose('l-erreur-code', 'textContent', code ? `Code ${code}` : '');
    const oui = el('l-erreur-reessayer');
    oui.hidden = typeof reessayer !== 'function';
    oui.onclick = () => { masquerErreur(); reessayer(); };
    el('l-erreur-autre').hidden = !(etat.proprietaire && etat.sourcesDe);
    fermerCarton();
    devoiler();
    ecran.hidden = false;
    reveiller();
    try { (oui.hidden ? el('l-erreur-autre') : oui).focus({ preventScroll: true }); } catch { /* focus refusé */ }
  }
  function masquerErreur() { if (ecranErreur) ecranErreur.hidden = true; }
  // Une nouvelle source (relance, autre cran, autre lien) ou une image qui
  // repart : l'écran n'a plus rien à dire.
  video.addEventListener('loadstart', masquerErreur);
  video.addEventListener('playing', masquerErreur);

  /**
   * Va chercher les bornes de cet épisode. Sans elles, pas de bouton.
   *
   * La référence de lecture part avec la demande : elle permet au serveur de
   * sonder les chapitres du fichier, qui décrivent CE montage à l'image près.
   * Ce sondage prend plusieurs secondes, donc on redemande — la première
   * réponse vient d'une base communautaire, les suivantes du fichier.
   *
   * ⚠️ LES DÉLAIS SONT CALÉS SUR LE GÉNÉRIQUE, PAS SUR LE CONFORT DU SERVEUR.
   *
   * On repassait toutes les vingt-cinq secondes : la bonne borne arrivait donc
   * au mieux à la vingt-cinquième, au pire à la cinquantième — pour une intro
   * qui commence souvent à la soixantième. Un ffprobe distant met une dizaine
   * de secondes : on repasse d'abord à huit, ce qui laisse le temps d'être
   * juste AVANT le générique plutôt que pendant.
   */
  const REPASSAGES = [8_000, 18_000, 35_000];
  function reclamerBornes(cible, duree, essai = 0) {
    if (essai === 0) {
      bornes = null;
      dernierEtatSaut = '';
      const bouton = el('l-saut');
      if (bouton) bouton.hidden = true;
    }
    if (!cible || !duree) return;

    // Un film (cible = son seul identifiant) se demande en `0/0` : IntroDB
    // connaît son générique de fin.
    const [serie, saison = '0', episode = '0'] = String(cible).split(':');

    fetch(avecAcces(base(`/api/sauts/${encodeURIComponent(serie)}/${saison}/${episode}`
      + `?duree=${Math.round(duree)}&ref=${encodeURIComponent(etat.ref ?? '')}`)),
      { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        if (etat.episode !== cible) return; // on a changé d'épisode entre-temps
        if (d.bornes) {
          bornes = d.bornes;
          dernierEtatSaut = ''; // les bornes ont changé : le bouton se recalcule
          // ⚠️ CET APPEL VISAIT UNE FONCTION QUI N'EXISTE NULLE PART ICI.
          //
          // `journal(...)` n'était ni importée, ni posée par `creerLecteur`, ni
          // définie plus bas : chaque fois que le serveur rendait vraiment des
          // bornes, l'appel levait un `ReferenceError` — avalé par le
          // `.catch(() => {})` de toute la chaîne, deux lignes plus bas. Ce qui
          // s'en trouvait avalé avec lui, c'est le REPASSAGE juste après : la
          // promesse rejetée ne redescendait jamais jusqu'au `setTimeout` qui
          // reprogramme `reclamerBornes` pour aller chercher la borne précise
          // sondée dans le fichier. Le bouton « Passer l'intro » restait donc
          // calé sur l'estimation grossière (base communautaire), sans jamais
          // recevoir la correction du fichier — silencieusement, à chaque
          // épisode où le serveur avait pourtant quelque chose à dire.
          //
          // `journal` est maintenant un paramètre optionnel de `creerLecteur` ;
          // `?.()` ne lève rien tant que personne ne le fournit.
          journal?.('sauts', `récap ${bornes.recap?.debut ?? '—'}s · intro ${bornes.intro?.debut ?? '—'}s · fin ${bornes.outro?.debut ?? '—'}s (${bornes.source})`);
        }
        if (admin) peindreMenuVitesse(); // la ligne « Repères : <source> »
        // Le fichier a le dernier mot : tant qu'il n'a pas parlé, on repasse.
        // Un fichier sans chapitres est noté comme tel côté serveur : ces
        // repassages ne relancent alors aucun ffprobe.
        if (d.bornes?.source !== 'fichier') repasserBornes(cible, duree, essai);
      })
      // ⚠️ UNE COUPURE RÉSEAU NE DOIT PAS METTRE FIN À LA BOUCLE.
      //
      // Le plafond de tentatives (`essai < REPASSAGES.length`) n'était consulté
      // que dans la branche `.then()` réussie. Un `fetch` qui échoue tombe ici,
      // dans le `.catch` — et un `.catch(() => {})` arrêtait la boucle NET, sans
      // avoir consommé le moindre essai, là où une vraie coupure méritait une
      // nouvelle tentative comme les autres. Le `.catch` compte donc désormais
      // comme un essai et reprogramme au même rythme que le repassage normal.
      .catch(() => repasserBornes(cible, duree, essai));
  }

  /** Reprogramme `reclamerBornes`, si le plafond n'est pas atteint et que
   * l'épisode n'a pas changé entre-temps. Partagé par la réussite sans
   * fichier et par l'échec réseau : les deux méritent le même essai suivant. */
  function repasserBornes(cible, duree, essai) {
    if (etat.episode !== cible) return; // on a changé d'épisode entre-temps
    if (essai < REPASSAGES.length) {
      setTimeout(() => reclamerBornes(cible, duree, essai + 1), REPASSAGES[essai]);
    }
  }

  // --- sous-titres -----------------------------------------------------------
  let reglagesST = lireReglages();
  const calque = el('st-calque');
  const rendu = brancherRendu({
    video,
    calque,
    reglages: () => reglagesST,
    positionCourante: position,
  });
  appliquerStyle(calque, reglagesST);

  const panneauST = construirePanneau({
    panneau: el('st-panneau'),
    video,
    obtenir: () => reglagesST,
    definir: (r) => {
      reglagesST = r;
      ecrireReglages(r);
      appliquerStyle(calque, r);
      rendu.peindre();
      decalerRenduRiche();
    },
    rendu,
    // Tant que le panneau est ouvert, un texte d'exemple tient la place des
    // répliques : les réglages se jugent sur l'image, même à l'arrêt.
    surApercu: (actif) => rendu.apercu(actif ? EXEMPLE : null),
  });
  brancher('l-st', () => panneauST?.basculer());
  brancher('l-detacher', () => detacher());

  // --- sous-titres riches : l'ASS par JASSUB, le PGS par libpgs -------------
  //
  // Le rendu maison ci-dessus peint du TEXTE. Une piste ASS ou image passe par
  // une bibliothèque qui peint elle-même sur un canvas, dans `#st-riches`
  // (voir `st-riches.js`). C'est main.js qui sait QUELLE piste est riche — il
  // tient la table des pistes et observe `video.textTracks` — et qui appelle
  // `poserPisteRiche` / `retirerPisteRiche`, exposés plus bas. Ici on ne fait
  // que vivre et mourir avec les pistes : un autre fichier (`installer`,
  // `ouvrirUneSource` retirent les balises `<track>` — le rendu part avec) ou
  // un détachement de fenêtre détruit le rendu.
  //
  // ⚠️ PAS DE DESTRUCTION SUR UNE SIMPLE REPOSE DE LA MÊME SOURCE. Un
  // déplacement au cran remux, un changement de piste audio, une descente de
  // l'escalier repassent tous par `poserSource` sur le MÊME fichier : les
  // pistes restent, la piste factice reste active, et aucun `change` ne
  // viendrait redemander le rendu. Le détruire là, c'était perdre l'ASS au
  // premier clic dans la barre. Seul le décalage est à refaire (`decaler`).
  const conteneurRiche = el('st-riches');
  let renduRiche = null; // { cle, rendu, memo: { format, urls, polices } }

  /** Secondes à ajouter au temps de la balise : le remux, moins le réglage du panneau. */
  const decalageRiche = () => (Number(etat.decalage) || 0) - (Number(reglagesST.decalageTemps) || 0);
  function decalerRenduRiche() { renduRiche?.rendu.decaler(decalageRiche()); }

  function retirerPisteRiche() {
    if (!renduRiche) return;
    const r = renduRiche;
    renduRiche = null;
    r.rendu.detruire();
    if (conteneurRiche) conteneurRiche.hidden = true;
    // Le calque texte reprend la main : la piste choisie, s'il y en a une, est
    // à lui.
    calque?.classList.remove('st-efface');
    rendu.peindre();
  }

  /**
   * Monte le rendu riche d'une piste — `format` ('ass' | 'image'), ses `urls`
   * (jeton compris, fabriquées par main.js) et les `polices` du conteneur
   * (des URL). Rend la promesse de démarrage.
   */
  function poserPisteRiche({ format, urls, polices = [] }) {
    const cle = `${format}:${urls?.ass ?? urls?.sup ?? ''}`;
    // Le `change` de `textTracks` part une fois par piste dont le mode bouge :
    // le même choix arrive donc plusieurs fois — on ne reconstruit pas.
    if (renduRiche?.cle === cle) return renduRiche.rendu.pret;
    retirerPisteRiche();
    if (!conteneurRiche) return Promise.reject(new Error('#st-riches absent de la page'));
    conteneurRiche.hidden = false;
    const r = creerRenduRiche({
      format, video, conteneur: conteneurRiche, urls, polices,
      // Les vrais `import()` restent écrits ici, en clair : c'est eux
      // qu'esbuild découpe en morceaux séparés (voir build.mjs) — une variable
      // à leur place casserait le découpage, comme pour le démuxeur.
      chargerJassub: () => import('jassub'),
      chargerLibpgs: () => import('libpgs'),
      urlsOutils: urlsDesOutils(base),
    });
    renduRiche = { cle, rendu: r, memo: { format, urls, polices } };
    // Pendant qu'une bibliothèque peint, le calque texte se tait : la piste
    // factice n'a aucune réplique, et l'aperçu du panneau parlerait d'un
    // style qui ne s'applique pas à un ASS.
    calque?.classList.add('st-efface');
    r.pret.then(() => {
      if (renduRiche?.rendu !== r) return;
      decalerRenduRiche();
      journal?.('sous-titres', 'rendu riche prêt', { format });
    }).catch((err) => {
      journal?.('sous-titres', 'rendu riche impossible', { format, erreur: err?.message ?? String(err) });
      if (renduRiche?.rendu !== r) return; // déjà remplacé ou retiré : rien à dire
      retirerPisteRiche();
      annoncer(format === 'image' ? 'Sous-titres image indisponibles ici.' : 'Sous-titres ASS indisponibles ici.');
    });
    return r.pret;
  }

  // La piste qu'on vient de masquer, pour pouvoir la rendre. Sans elle, « C »
  // couperait les sous-titres sans jamais savoir lesquels remettre.
  let pisteMasquee = null;

  /** Coupe les sous-titres, ou remet ceux qui étaient là. */
  function basculerSousTitres() {
    const pistes = [...video.textTracks];
    const active = pistes.find((p) => p.mode === 'hidden' || p.mode === 'showing');
    if (active) {
      pisteMasquee = active;
      active.mode = 'disabled';
      window.__retenirST?.('aucun');
      rendu.peindre();
      annoncer('Sous-titres masqués.');
      return;
    }
    // Celle d'avant si elle appartient toujours à ce fichier, sinon la
    // première : après un changement d'épisode, les anciennes pistes ont
    // disparu et remettre une référence morte ne ferait rien du tout.
    const remettre = pistes.includes(pisteMasquee) ? pisteMasquee : pistes[0];
    if (!remettre) {
      annoncer('Aucun sous-titre sur ce fichier.');
      return;
    }
    if (typeof window.__activerPisteST === 'function') window.__activerPisteST(remettre);
    else { remettre.mode = 'hidden'; rendu.peindre(); }
    annoncer('Sous-titres affichés.');
  }

  /**
   * Décale les répliques dans le temps, sans toucher à la vidéo.
   *
   * Le curseur du panneau fait la même chose ; le raccourci existe parce
   * qu'un décalage se règle À L'OREILLE, en écoutant — ouvrir un panneau
   * par-dessus l'image pour ça, c'est perdre ce qu'on essaie de juger.
   */
  function decalerSousTitres(pas) {
    // Les mêmes bornes que le curseur du panneau, au dixième près : deux
    // chemins vers le même réglage ne doivent pas accepter deux plages.
    const avant = Number(reglagesST.decalageTemps) || 0;
    const apres = Math.round(Math.min(10, Math.max(-10, avant + pas)) * 10) / 10;
    reglagesST = { ...reglagesST, decalageTemps: apres };
    ecrireReglages(reglagesST);
    rendu.peindre();
    decalerRenduRiche();
    // Le panneau porte le meme reglage sur un curseur : ouvert, il montrerait
    // encore l ancienne valeur. On le repeint plutot que de laisser deux
    // chiffres se contredire a l ecran (`ouvrir` repeint tout).
    if (panneauST?.estOuvert?.()) panneauST.ouvrir();
    const mot = apres === 0 ? 'à l’heure'
      : (apres > 0 ? '+' : '') + String(apres).replace('.', ',') + ' s';
    annoncer('Sous-titres : ' + mot);
  }

  /**
   * Sort le lecteur de la page — fenêtre détachée, ou vignette à défaut.
   *
   * ⚠️ TOUT ÉTAIT DÉJÀ ÉCRIT, ET BRANCHÉ SUR RIEN. `detacherLecteur` (dans
   * `main.js`) sait faire une fenêtre de document détachée qui emporte le
   * lecteur ENTIER — commandes et sous-titres compris —, se replie sur la
   * vignette image seule, et remet tout en place au retour. Aucun bouton,
   * aucune touche ne l'appelait : la fonction n'avait pas un seul appelant
   * dans tout le projet.
   */
  function detacher() {
    if (typeof window.__detacherLecteur !== 'function') {
      annoncer('Cette fenêtre ne peut pas être détachée ici.');
      return;
    }
    // ⚠️ LE CANVAS D'UN RENDU RICHE NE SUIT PAS DANS UNE AUTRE FENÊTRE. Il a
    // cédé son contrôle à un worker (`transferControlToOffscreen`) : déplacé
    // dans le document détaché, il ne se repeint plus. On le détruit avant,
    // on repose la même piste après — dans le document où le lecteur a
    // atterri (`creerRenduRiche` crée le canvas dans `ownerDocument`).
    // Refermer la fenêtre rend le lecteur à la page sur `pagehide`, APRÈS
    // que l'appel a rendu la main : on attend qu'il soit revenu.
    const memo = renduRiche?.memo ?? null;
    retirerPisteRiche();
    const avant = racine.ownerDocument;
    const reposer = (essais) => {
      if (!memo) return;
      if (racine.ownerDocument !== avant || racine.ownerDocument === document || essais <= 0) {
        poserPisteRiche(memo);
        return;
      }
      setTimeout(() => reposer(essais - 1), 100);
    };
    Promise.resolve(window.__detacherLecteur()).catch(() => {}).then(() => reposer(30));
  }

  // -------------------------------------------------------------------------
  // LA LISTE DES RACCOURCIS
  //
  // Elle se peint depuis la table de `raccourcis.js` : elle ne peut donc pas
  // mentir sur ce que le lecteur fait vraiment. Une aide écrite à la main se
  // désynchronise au premier raccourci ajouté, et devient pire que rien.
  // -------------------------------------------------------------------------
  function peindreAide() {
    const boite = el('l-aide');
    if (!boite) return;
    const echapper = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const groupes = raccourcisAffichables().map((g) => {
      const lignes = g.lignes
        .map((r) => '<dt><kbd>' + echapper(r.touche) + '</kbd></dt><dd>' + echapper(r.libelle) + '</dd>')
        .join('');
      return '<h4>' + echapper(g.nom) + '</h4><dl>' + lignes + '</dl>';
    }).join('');
    boite.innerHTML = '<h3>Raccourcis</h3>' + groupes
      + '<p class="l-aide-pied">La vitesse ne se change pas pendant une séance :'
      + ' tout le monde regarde à la même.</p>';
  }

  function basculerAide() {
    const boite = el('l-aide');
    if (!boite) return;
    if (!boite.hidden) { fermerAide(); rendreLeFocus(); return; }
    // Un seul panneau par-dessus l'image, et un seul menu : la liste des
    // raccourcis prend la place, elle ne s'ajoute pas.
    fermerMenus();
    if (panneauST?.estOuvert?.()) { panneauST.fermer(); majAriaST(); }
    fermerPanneauSources();
    // ⚠️ ON RETIENT D'OÙ L'ON VIENT AVANT D'OUVRIR.
    //
    // La liste s'ouvre à la touche « ? », donc depuis n'importe quel bouton du
    // lecteur. Sans ce repère, Échap la fermerait en laissant le focus sur le
    // `body` : la frappe suivante n'irait nulle part.
    retenirLeFocus();
    peindreAide();
    // Un panneau qu'on ouvre au clavier doit pouvoir se lire au clavier : sans
    // `tabindex`, il n'est pas atteignable et le lecteur d'écran ne l'annonce
    // qu'au survol.
    boite.setAttribute('role', 'dialog');
    boite.tabIndex = -1;
    boite.hidden = false;
    focaliser(boite);
  }

  // La vitesse retenue de la dernière fois se repose ici, une fois le bouton
  // et le menu en place. On ne passe pas par `poserVitesse` : elle parle, et
  // il n'y a rien à annoncer pour un réglage qu'on ne fait que retrouver.
  etat.vitesse = vitesseRetenue();
  appliquerVitesse();
  afficherVitesse();
  peindreMenuVitesse();

  // --- bascule de langue -----------------------------------------------------
  // Deux chemins, et le premier est de loin le meilleur : si le fichier porte
  // deja les deux bandes-son, on change de piste sans rien retelecharger. Ce
  // n'est qu'a defaut qu'on redemande un autre fichier au serveur.
  //
  // ⚠️ Une entrée « … · autre fichier » du menu « Voix » se lit comme « cette
  // version existe ». Le drapeau d'avant s'affichait dès qu'un rappel de
  // bascule était fourni, c'est-à-dire sur tout épisode de série : sur le 412,
  // qui n'a aucun doublage, le drapeau français apparaissait quand même et le
  // clic ne pouvait que décevoir. L'entrée ne se propose plus que si le serveur
  // a VU un autre fichier qui porte la langue (`/api/voix`), et elle dit
  // « (non vérifié) » quand seul le nom du fichier l'annonce.

  /**
   * Rang de la piste qui porte cette langue dans le fichier courant, ou -1.
   * Le menu et le clic lisent la MÊME réponse : sans cela, l'un pouvait
   * promettre ce que l'autre ne savait pas faire.
   *
   * ⚠️ `MARQUES = { vf: …, vo: /^(ja|jpn|jp)$/i }` A ÉTÉ RETIRÉE : c'est elle
   * qui imposait le français sur une série anglaise (voir « VO NE VEUT PAS
   * DIRE JAPONAIS » plus haut). Le type de l'œuvre entre maintenant dans la
   * question, et la règle est pure — c'est le banc qui la juge, pas la
   * lecture.
   */
  const rangDeLangue = (vise) => rangDeLaLangueVoulue({
    pistes: etat.pistes, vise, typeOeuvre: etat.typeOeuvre,
  });

  // ⚠️ CE QUE CETTE QUESTION COÛTE, ET POURQUOI ON NE LA POSE QUE SUR UN CLIC.
  //
  // `/api/langue` n'a aucun cache : elle interroge toutes les sources actives
  // (un appel réseau par add-on, 45 s de délai chacun) puis ouvre jusqu'à huit
  // liens pour vérifier qu'ils répondent. Posée à chaque ouverture d'épisode,
  // et chaque spectateur ayant son propre lecteur, une séance à cinq personnes
  // déclenchait cinq résolutions complètes et jusqu'à quarante sondes de liens
  // — sur le chemin exact qui conditionne la lecture. Si le débrideur
  // rate-limite sous cette charge, ce n'est pas un menu qui casse, c'est la
  // lecture de toute la salle.
  //
  // Le menu « Voix » ne la pose donc JAMAIS. Ce qu'il montre des autres
  // fichiers vient de `/api/voix` (`etat.voixDe`), qui lit le mapping et les
  // sondes déjà faites et ne résout rien. La question chère ne part que sur un
  // CLIC sur une entrée « … · autre fichier », et une seule fois : c'est la
  // bascule elle-même qui la pose.
  //
  // Le drapeau `l-langue`, qui doublait ce menu, a été retiré — Toji (10/09) :
  // « on a un doublon au niveau de la gestion de la voix : le switch entre deux
  // fichiers et le switch avec la voix directement. Je veux que le switch de
  // fichiers soit dans l'onglet de la gestion de la voix. » `basculerLangue`
  // reste l'ACTION ; c'est elle que l'entrée « autre fichier » déclenche. La
  // mémoire locale de ce qu'un clic apprenait (`languesConnues`) est partie
  // avec le drapeau : c'est le serveur qui sait désormais quelles langues un
  // autre fichier porte, et il le sait pour tout le monde.

  /**
   * Passe dans la langue visée : sur la piste du fichier courant si elle y
   * est, sinon sur un autre fichier, à la même seconde.
   *
   * @param {'vf'|'vo'|null} vise sans valeur : l'autre langue que celle écoutée.
   */
  function basculerLangue(vise = null) {
    if (vise !== 'vf' && vise !== 'vo') vise = (etat.langue || 'vo') === 'vf' ? 'vo' : 'vf';
    // Geste explicite : le filet `langueVoulue` se tait pour cette source.
    etat.pisteChoisieALaMain = true;
    etat.surChoixLangue?.(vise); // retenu pour cette œuvre : rechargement, épisode suivant

    // Le fichier porte-t-il déjà la piste voulue ?
    const cherchee = rangDeLangue(vise);
    if (cherchee >= 0 && cherchee !== etat.audio) {
      choisirAudio(cherchee);
      etat.langue = vise;
      return;
    }

    // UNE SEULE RÉSOLUTION PAR CLIC, ET C'EST LA BASCULE ELLE-MÊME.
    //
    // Une sonde partait ici, juste avant la bascule : deux appels à
    // `/api/langue` pour un seul clic, sur le même épisode et la même langue.
    // La route n'a pas de cache — chacun des deux interrogeait toutes les
    // sources puis ouvrait jusqu'à huit liens, soit jusqu'à seize sondes pour
    // un drapeau. Sur le chemin qui conditionne la lecture, et multiplié par le
    // nombre de spectateurs : sous cette charge, ce n'est pas le drapeau qui
    // casse, c'est la lecture de toute la salle.
    //
    // La sonde ne servait qu'à SAVOIR si la version existe. Une fois qu'on a
    // décidé de basculer, elle n'apprend plus rien que la bascule ne dise : la
    // bascule pose la même question à la même route, et va jusqu'à ouvrir le
    // fichier.
    if (!etat.changerLangue) return;
    Promise.resolve(etat.changerLangue(vise, positionVue()))
      // Une bascule qui casse ne doit pas rester invisible — la bascule avale
      // déjà l'absence de source, elle seule.
      .catch((err) => console.warn('[lecteur] bascule de langue interrompue :', err?.message ?? err));
  }

  // --- pistes audio ----------------------------------------------------------
  // ffmpeg ne sort qu'une bande-son a la fois : changer de piste veut dire
  // redemander le flux. On conserve donc la position pour y revenir.
  // La table des noms de langues vit dans `voix-menu.js` (`nomDeLangue`) : le
  // menu s'y construit, et elle doit s'éprouver sans DOM.

  // ⚠️ `CANAUX` A ÉTÉ RETIRÉE (`{ 1: 'Mono', 2: 'Stéréo', 6: '5.1', 8: '7.1' }`) :
  // AUCUN CODE NE LA LISAIT, ICI NI AILLEURS DANS LE PROJET.
  //
  // Elle voulait traduire un nombre de canaux en mot courant, mais
  // `entreesDuMenuVoix` (voix-menu.js) ne construit ses entrées qu'à partir de
  // `p.langue` — une seule entrée par langue, la première piste trouvée — et
  // ne s'est jamais servi du nombre de canaux pour les distinguer. Le serveur
  // le fournit pourtant (`p.canaux`, déjà lu ailleurs dans `main.js` pour
  // l'admin) : brancher `CANAUX` demanderait de revoir le regroupement par
  // langue pour ne plus faire disparaître une seconde piste de même langue
  // mais de canalisation différente — un changement de comportement, pas une
  // simple activation, donc hors périmètre ici.

  /** Rejoue le flux sur une autre piste audio, à la même seconde. */
  function choisirAudio(rang) {
    if (rang === etat.audio) return;
    etat.audio = rang;
    // Le verdict portait sur l'ancienne piste : la nouvelle sera rejugée.
    etat.sonRecopiable = false;
    const reprise = positionVue();
    // ⚠️ MÊME TROU NOIR QUE LA BASCULE DE LANGUE, POUR LA MÊME RAISON.
    // ffmpeg ne sort qu'une bande-son à la fois : changer de piste REDEMANDE
    // le flux, donc vide la balise. Sans le gel, le cadre passait au noir le
    // temps de la réouverture — voir « GELER L'IMAGE » en tête de fichier.
    basculerVers(etat.ref, { mot: 'Autre piste audio', seconde: reprise, audio: rang });
    // Le relais direct ignore `pos` : c'est la balise qui se replace. Le CRAN
    // le dit, pas `etat.direct` — un .mp4 descendu au remux est coupé par le
    // serveur, et le replacer ici compterait la seconde deux fois.
    //
    // ⚠️ ET LE CRAN NE SUFFIT PLUS. Depuis que `rang > 0` force le remux au
    // cran `direct` (voir `serveurRemuxe`), `cran === 'direct'` ne veut plus
    // dire « le serveur a relayé depuis le début » : sur cette bascule-ci il
    // coupe à `reprise`, la balise repart de zéro, et se replacer ici
    // compterait la seconde DEUX FOIS — l'épisode sauterait à 2 × reprise, et
    // sur un pilote toute la salle avec.
    amorcer(() => {
      const cran = escalier.cran();
      if (cran === 'direct' && !serveurRemuxe({ cran, audio: rang })) video.currentTime = reprise;
    });
  }

  /**
   * LE menu « Voix » : les pistes du fichier, puis les autres fichiers pour
   * les langues que le fichier n'a pas (voir `entreesDuMenuVoix`).
   *
   * @param {Array} pistes celles du fichier courant.
   * @param {Array} autres ce que `/api/voix` a vu chez les autres fichiers.
   * @param {boolean} pistesInconnues la liste des pistes n'a pas pu être lue.
   */
  function menuVoix(pistes, autres, pistesInconnues = false) {
    const entrees = entreesDuMenuVoix({
      pistes, audioActif: etat.audio, autres, typeOeuvre: etat.typeOeuvre, nom: nomDeLangue,
    });
    // ⚠️ LE BOUTON RESTAIT MORT POUR TOUT L'ÉPISODE.
    //
    // Quand `/api/audio` échoue, on ne sait PAS si le fichier n'a qu'une
    // piste — indiscernable d'une panne. Griser serait mentir ; on laisse une
    // entrée qui dit ce qui s'est passé et qui REDEMANDE, au lieu d'un bouton
    // désactivé jusqu'à la fin de l'épisode.
    if (pistesInconnues) entrees.push({ libelle: 'Pistes audio indisponibles — réessayer', reessayer: true, actif: false });
    const b = el('l-audio');
    if (b) b.disabled = !boutonVoixActif(entrees) && !pistesInconnues;
    menu('l-audio', 'l-menu-audio', entrees, (choix) => {
      if (choix.reessayer) { chargerAudio(etat.ref); return; }
      if (choix.autre) { basculerLangue(choix.autre.langue); return; }
      // Un clic dans le menu est une décision : le filet `langueVoulue` ne
      // doit plus la défaire sur cette source (voir `appliquerLaLangueVoulue`).
      etat.pisteChoisieALaMain = true;
      choisirAudio(choix.rang);
      // La langue écoutée suit la piste choisie ; le menu se repeint pour que
      // la coche suive aussi.
      const p = pistes[choix.rang];
      if (p) {
        etat.langue = FRANCAIS.test(p.langue) ? 'vf' : 'vo';
        etat.surChoixLangue?.(etat.langue);
      }
      menuVoix(pistes, autres, pistesInconnues);
    });
  }

  /**
   * Les pistes du fichier ET les voix des autres fichiers, en parallèle : la
   * seconde question ne coûte rien (mapping et sondes déjà faits) et ne doit
   * pas retarder la première.
   */
  function chargerAudio(ref) {
    const b = el('l-audio');
    if (b) b.disabled = true;
    const pistesPromises = fetch(avecAcces(base(`/api/audio/${ref}`)), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { pistes: [] }))
      .then(({ pistes }) => (Array.isArray(pistes) ? pistes : []));
    // Une réponse absente (pas de rappel, ou une route qui tombe) vaut « aucun
    // autre fichier connu » : le menu montre alors les seules pistes du fichier.
    const voixPromise = Promise.resolve()
      .then(() => etat.voixDe?.(ref))
      .then((r) => (Array.isArray(r?.autres) ? r.autres : []));
    Promise.allSettled([pistesPromises, voixPromise]).then(([rp, rv]) => {
      if (etat.ref !== ref) return; // une autre source a pris la main
      const pistes = rp.status === 'fulfilled' ? rp.value : [];
      if (rp.status === 'rejected') console.warn('[lecteur] pistes audio indisponibles :', rp.reason?.message ?? rp.reason);
      const autres = rv.status === 'fulfilled' ? rv.value : [];
      if (rv.status === 'rejected') console.warn('[lecteur] voix des autres fichiers indisponibles :', rv.reason?.message ?? rv.reason);
      etat.pistes = pistes;
      // La langue écoutée se déduit de la piste servie.
      const active = pistes[etat.audio];
      if (active) etat.langue = FRANCAIS.test(active.langue) ? 'vf' : 'vo';
      appliquerLaLangueVoulue(pistes);
      menuVoix(pistes, autres, rp.status === 'rejected');
    });
  }

  /**
   * LE FILET — et seulement le filet.
   *
   * Le bon chemin est le rang reçu à l'ouverture (`installer`, champ `audio`) :
   * il ne coûte rien, la source part directement sur la bonne piste. Tant que
   * personne ne l'a tranché, ce filet-ci évite au moins que le réglage « VF » /
   * « VO » soit à repayer À LA MAIN à chaque épisode. Il coûte une bascule —
   * donc un gel — et c'est pour ça qu'il est encadré par QUATRE gardes, chacune
   * protégeant un effet existant :
   *
   *   1. `rangImpose` — qui ouvre a déjà choisi : on ne défait pas son calcul,
   *      et surtout on ne rouvre pas un flux qui est déjà le bon.
   *   2. `langueAppliquee` — UNE fois par source. `chargerAudio` est rappelé
   *      par l'entrée « réessayer » du menu ; sans ce verrou, chaque reprise
   *      relancerait une bascule.
   *   3. `pisteChoisieALaMain` — quelqu'un a choisi sa piste dans le menu :
   *      lui réimposer la préférence par-dessus serait lui reprendre sa main.
   *   4. la langue servie est déjà la bonne, ou aucune piste ne porte la
   *      voulue : il n'y a rien à faire, et surtout rien à rouvrir.
   *
   * ⚠️ CE QUE CETTE BASCULE DÉTRUIT, ET QU'IL FAUT SAVOIR : `choisirAudio`
   * remet `etat.sonRecopiable` à faux — le verdict que la machine du client
   * venait de rendre portait sur l'ANCIENNE piste. Le serveur repassera donc
   * par un réencodage AAC pour cette pose-là (`bitDuClient`, stream.js), le
   * temps que le démuxeur rejuge la nouvelle piste. C'est un coût réel : une
   * raison de plus pour que le rang arrive dès l'ouverture.
   */
  function appliquerLaLangueVoulue(pistes) {
    if (!etat.langueVoulue) return;
    if (etat.rangImpose || etat.langueAppliquee || etat.pisteChoisieALaMain) return;
    if (!Array.isArray(pistes) || pistes.length === 0) return;
    // Le droit est consommé même quand on ne bascule pas : les pistes ont
    // parlé, la question est tranchée pour cette source.
    etat.langueAppliquee = true;
    // ⚠️ `if (etat.langue === etat.langueVoulue) return;` A ÉTÉ RETIRÉ, ET
    // C'ÉTAIT UN SECOND TROU, INDÉPENDANT DU PREMIER.
    //
    // `etat.langue` ne connaît que deux valeurs, et « vo » y signifie
    // seulement « pas français ». Sur une SÉRIE dont la piste 0 est japonaise
    // et qui porte aussi une piste anglaise, ce raccourci lisait
    // « vo === vo » et renonçait : on écoutait du japonais avec « anglais »
    // réglé, sans qu'aucune ligne ne le dise. Deux étiquettes grossières ne
    // peuvent pas répondre à une question qui distingue trois langues.
    //
    // Le RANG, lui, y répond sans ambiguïté : `rangDeLangue` sait déjà ce que
    // « vo » veut dire pour CE type d'œuvre, et « c'est déjà la bonne piste »
    // se dit exactement `rang === etat.audio`. Une garde de moins, et elle
    // couvre strictement plus de cas.
    const rang = rangDeLangue(etat.langueVoulue);
    if (rang < 0 || rang === etat.audio) return;
    console.log(`[lecteur] préférence ${etat.langueVoulue} : piste ${rang} au lieu de ${etat.audio}`);
    choisirAudio(rang);
    etat.langue = etat.langueVoulue === 'vf' ? 'vf' : 'vo'; // `etat.langue` ne parle que vf|vo
  }

  // --- changement de source (propriétaire) ----------------------------------
  //
  // Toji (10/09) : « côté admin, un onglet "Changement de source", dispo que
  // pour moi, qui affiche toutes les sources dispo : depuis le lecteur je
  // clique et je change de source directement. »
  //
  // Même mécanique que `st-panneau` : un seul panneau à la fois, Échap le
  // ferme, `fermerPanneaux` aussi. Le lecteur ne connaît aucune route : les
  // trois rappels (`sourcesDe`, `ouvrirSourceDe`) viennent de l'appelant, et
  // le bouton n'existe que si `etat.proprietaire` le dit.

  /** Ce que le panneau a listé en dernier, pour retrouver une ligne au clic. */
  let sourcesListees = [];

  const panneauSourcesOuvert = () => { const p = el('l-panneau-sources'); return !!p && !p.hidden; };

  /** Ferme le panneau de sources. Rend `true` s'il était ouvert. */
  function fermerPanneauSources() {
    const p = el('l-panneau-sources');
    if (!p || p.hidden) return false;
    p.hidden = true;
    el('l-sources')?.setAttribute('aria-expanded', 'false');
    return true;
  }

  const echapperHTML = (t) => String(t ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /** Peint le panneau : en-tête, état, liste. `message` remplace la liste (chargement, erreur). */
  function peindrePanneauSources({ reponse = null, message = null, erreur = false } = {}) {
    const p = el('l-panneau-sources');
    if (!p) return;
    const titre = el('l-titre')?.textContent || etat.episode || '';
    const apercu = reponse ? (reponse.apercu ? 'aperçu prêt' : 'aperçu absent') : '';
    const lignes = reponse ? lignesDuPanneauSources(reponse.sources) : [];
    sourcesListees = reponse?.sources ?? [];
    const liste = lignes.length
      ? lignes.map((l, i) => `<button type="button" class="ls-ligne${l.courant ? ' courant' : ''}${l.exclu ? ' exclu' : ''}" data-i="${i}">`
        + `<span class="ls-titre">${echapperHTML(l.titre)}</span>`
        + `<span class="ls-badges">${l.badges.map((b) => `<i>${echapperHTML(b)}</i>`).join('')}<b>${l.score}</b></span>`
        + '</button>').join('')
      : (reponse ? '<p class="ls-message">Aucune source connue pour cet épisode.</p>' : '');
    p.innerHTML = '<div class="st-entete">'
      + `<div><span class="st-titre">Changement de source</span><span class="ls-episode">${echapperHTML(titre)}${apercu ? ` · ${apercu}` : ''}</span></div>`
      + '<button type="button" class="st-fermer" data-fermer aria-label="Fermer">✕</button>'
      + '</div>'
      + '<div class="ls-actions"><button type="button" class="ls-actualiser" data-actualiser>Actualiser (interroger les add-ons)</button></div>'
      + (message ? `<p class="ls-message${erreur ? ' erreur' : ''}">${echapperHTML(message)}</p>` : '')
      + `<div class="ls-liste">${liste}</div>`;
    p.querySelector('[data-fermer]').onclick = () => { fermerPanneauSources(); rendreLeFocus('l-sources'); };
    p.querySelector('[data-actualiser]').onclick = () => chargerPanneauSources(true);
    p.querySelectorAll('.ls-ligne').forEach((n) => { n.onclick = () => choisirSource(lignes[Number(n.dataset.i)]?.url); });
  }

  /** Demande la liste — `frais` interroge les add-ons, sinon le mapping suffit. */
  function chargerPanneauSources(frais = false) {
    const ref = etat.ref;
    peindrePanneauSources({ message: frais ? 'Interrogation des add-ons…' : 'Lecture des sources…' });
    Promise.resolve()
      .then(() => etat.sourcesDe?.(ref, frais))
      .then((reponse) => {
        if (etat.ref !== ref || !panneauSourcesOuvert()) return; // une autre source a pris la main, ou le panneau est fermé
        if (!reponse) { peindrePanneauSources({ message: 'Aucun rappel de sources : le panneau ne peut rien lister.', erreur: true }); return; }
        peindrePanneauSources({ reponse });
      })
      // Une panne se LIT dans le panneau, pas dans la console seulement.
      .catch((err) => {
        if (etat.ref !== ref || !panneauSourcesOuvert()) return;
        peindrePanneauSources({ message: `Sources indisponibles — ${err?.message ?? err}`, erreur: true });
      });
  }

  /** Vérifie le lien côté serveur, puis reprend dessus à la même seconde. */
  function choisirSource(url) {
    if (!url) return;
    const ref = etat.ref;
    const seconde = position();
    peindrePanneauSources({ reponse: { apercu: null, sources: sourcesListees }, message: 'Vérification du lien…' });
    // Sans rappel, la réponse est `undefined` et se lit dans le panneau comme
    // une panne : un clic qui ne fait rien en silence est le pire des symptômes.
    Promise.resolve()
      .then(() => etat.ouvrirSourceDe?.(url))
      .then((choix) => {
        if (etat.ref !== ref) return;
        if (!choix?.ref) throw new Error(choix ? 'réponse sans référence' : 'aucun rappel d’ouverture');
        fermerPanneauSources();
        ouvrirUneSource(choix, seconde);
        // Un autre fichier ne commence pas forcément à la même seconde : la
        // bascule VF/VO mesure l'écart entre les deux montages et recale ; un
        // changement de source doit faire pareil (relevé du 10/09).
        etat.surSourceOuverte?.(ref, choix.ref, seconde);
        annoncer(`Source : ${choix.source ?? '?'}`);
      })
      .catch((err) => {
        // Se dit TOUJOURS : une fois la source ouverte, le panneau est fermé
        // et `etat.ref` a changé — sans cette ligne, une exception levée par
        // l'ouverture disparaissait sans un mot.
        console.warn('[lecteur] changement de source :', err?.message ?? err);
        if (etat.ref !== ref || !panneauSourcesOuvert()) return;
        peindrePanneauSources({ reponse: { apercu: null, sources: sourcesListees }, message: `Ce lien ne répond pas — ${err?.message ?? err}`, erreur: true });
      });
  }

  function basculerPanneauSources() {
    const p = el('l-panneau-sources');
    if (!p) return;
    if (!p.hidden) { fermerPanneauSources(); rendreLeFocus('l-sources'); return; }
    if (!etat.proprietaire) return; // le bouton est caché, mais un raccourci ne doit pas contourner
    // Un seul panneau par-dessus l'image, et un seul menu.
    fermerMenus();
    fermerAide();
    if (panneauST?.estOuvert?.()) { panneauST.fermer(); majAriaST(); }
    retenirLeFocus();
    p.setAttribute('role', 'dialog');
    p.tabIndex = -1;
    p.hidden = false;
    el('l-sources')?.setAttribute('aria-expanded', 'true');
    chargerPanneauSources(false);
    focaliser(p);
  }
  brancher('l-sources', basculerPanneauSources);
  el('l-sources')?.setAttribute('aria-haspopup', 'true');

  /**
   * Reprend l'épisode sur un autre fichier, à la seconde où l'on en était. La
   * position est conservée de part et d'autre : changer de fichier ne doit pas
   * faire perdre sa place.
   *
   * @param {{ref: string, direct: boolean, source?: string}} choix
   * @param {number} seconde
   * @param {'vf'|'vo'|null} vers la langue demandée quand c'est une bascule de
   *   langue ; `null` sinon, et ce sont les pistes qui la diront.
   */
  function ouvrirUneSource(choix, seconde, vers = null) {
    // Une bascule de langue passe par l'écran d'attente, donc par `mettreDeCote` :
    // ouvrir la nouvelle source, c'est revenir à l'écran.
    horsEcran = false;
    video.autoplay = true;
    etat.ref = choix.ref;
    etat.direct = !!choix.direct;
    etat.audio = 0;
    etat.langue = vers;
    etat.pistes = [];
    // ⚠️ ON NE PEUT PAS DEVINER LE RANG ICI, et l'audit proposait de le faire.
    //
    // `rangDeLangue(vers)` lit `etat.pistes`, c'est-à-dire les pistes du
    // fichier QU'ON QUITTE — celui qu'on ouvre n'a pas encore été sondé. Le
    // rang qu'on en tirerait n'aurait aucun rapport avec le nouveau conteneur :
    // ce serait un « audio=3 » au hasard, et le bon cas (`basculerLangue` ne
    // vient ici que si le fichier courant N'A PAS la langue) garantit
    // justement que la réponse y vaut -1. Donc 0, et c'est le filet
    // ci-dessous qui corrige quand les vraies pistes arrivent.
    //
    // La demande explicite devient la langue voulue du nouveau fichier, et
    // rouvre le droit au filet : sans ça, un clic « Français · autre fichier »
    // qui tombe sur une release dont la piste 0 est japonaise laisserait
    // entendre du japonais après avoir demandé le français.
    if (vers) { etat.langueVoulue = vers; etat.pisteChoisieALaMain = false; }
    etat.rangImpose = false;
    etat.langueAppliquee = false;
    etat.sonRecopiable = false; // autre fichier, autre piste : le verdict est à refaire
    // Le décalage est posé par `poserSource` : le deviner ici depuis
    // `etat.direct` le comptait deux fois sur un MKV, où c'est le démuxeur
    // local qui prend la main et se déplace tout seul.

    // Les répliques appartiennent au fichier qu'on quitte : elles n'ont
    // aucune raison de valoir pour celui qu'on ouvre.
    for (const t of video.querySelectorAll('track')) t.remove();
    retirerPisteRiche();
    etat.surSousTitres?.(choix.ref);

    // ⚠️ C'EST ICI QU'ÉTAIT L'ÉCRAN NOIR — les deux, celui de la langue et
    // celui du changement de lien : ils passent tous les deux par cette ligne.
    // Mesuré par `essais/bascule-de-source.mjs` avant le gel : 479 ms de cadre
    // noir au cran `direct`, 3 309 ms quand le démuxeur rouvre le fichier, et
    // dans les deux cas ce qu'on avait sous les yeux était la balise VIDE.
    basculerVers(choix.ref, {
      mot: vers ? `Passage en ${vers.toUpperCase()}` : 'Changement de source',
      seconde,
    });
    // Même règle que `choisirAudio` : c'est le cran qui dit qui se replace.
    amorcer(() => { if (escalier.cran() === 'direct') video.currentTime = seconde; });
    pose('l-infos', 'textContent', vers ? `${choix.source} · ${vers.toUpperCase()}` : String(choix.source ?? ''));
    chargerAudio(choix.ref);
  }

  pose('retour2', 'innerHTML', ICONES.retour);
  pose('l-prec', 'innerHTML', ICONES.precedent);
  pose('l-suiv', 'innerHTML', ICONES.suivant);
  pose('l-audio', 'innerHTML', ICONES.audio);
  pose('l-st', 'innerHTML', ICONES.soustitres);
  pose('l-plein', 'innerHTML', ICONES.plein);

  // ⚠️ Le son est restauré AVANT le premier rendu : appliqué après, un volume
  // au maximum aurait le temps de se faire entendre.
  const son = lireSon();
  if (son) {
    if (Number.isFinite(son.volume)) video.volume = Math.max(0, Math.min(1, son.volume));
    video.muted = !!son.coupe;
  }

  peindreLecture();
  peindreSon();
  // Rien n'est encore chargé : la vague du blason n'a personne à rassurer, et
  // SMIL tourne même sur un calque à opacité nulle.
  animerAttente(false);

  /**
   * Installe une source dans la balise : tout ce qu'il faut pour qu'un épisode
   * soit LISIBLE — le fichier, la durée, les voisins, l'aperçu, les pistes.
   *
   * Ce corps était celui de `ouvrir`. Il en a été sorti parce que deux
   * appelants en ont besoin et qu'ils ne veulent PAS la même fin :
   *
   *   · `ouvrir` montre et lance — c'est quelqu'un qui vient de choisir ;
   *   · `preparerEnFond` ne montre rien et ne lance rien — c'est le lecteur qui
   *     se tient prêt pendant que la personne regarde autre chose.
   *
   * Le partage n'est pas cosmétique : c'est la seule façon d'être certain que
   * le lecteur préparé est COMPLET. Une préparation qui ne poserait que le
   * fichier rendrait, au retour, un lecteur sans durée, sans aperçu au survol,
   * sans bouton de langue et sans voisins — un lecteur qui a l'air cassé.
   *
   * @param {number} depart secondes demandées AU SERVEUR (voir `poserSource`).
   * @param {boolean} enFond aucune image ne sera montrée : pas de voile.
   */
  function installer(
    {
      ref, direct, duree, dureeApprox, titre, infos, voisins, changerLangue, surSousTitres, episode, typeOeuvre,
      oeuvre, affiche, proprietaire, voixDe, sourcesDe, ouvrirSourceDe, surSourceOuverte,
      // ⚠️ LA LANGUE VOULUE NE CHOISISSAIT QUE LE FICHIER, JAMAIS LA PISTE.
      //
      // `etat.audio = 0` était posé sans condition, APRÈS `poserSource` : une
      // release MULTI dont la piste 0 est japonaise s'ouvrait en japonais même
      // avec « VF » réglé, et il fallait rebasculer à la main À CHAQUE
      // ÉPISODE — en repayant le gel de la bascule à chaque fois.
      //
      // Deux entrées, et elles ne font pas la même chose :
      //   · `audio` — le rang DÉJÀ TRANCHÉ par qui ouvre (l'appelant a sondé
      //     les pistes). C'est le bon chemin : le rang est posé AVANT
      //     `poserSource`, donc aucune seconde ouverture de flux.
      //   · `langueVoulue` — 'vf' | 'vo', le réglage du spectateur. Filet de
      //     sécurité UNIQUEMENT : il ne sert que si personne n'a tranché le
      //     rang, et il coûte alors une bascule (voir `appliquerLaLangueVoulue`).
      audio, langueVoulue, surChoixLangue,
    },
    { depart = 0, enFond = false } = {},
  ) {
    // ⚠️ UN GEL NE TRAVERSE PAS UN CHANGEMENT D'ÉPISODE. Il garde la dernière
    // image d'un fichier qu'on rouvre — pas d'un épisode qu'on quitte. Laissé
    // en place, il montrerait la fin de l'épisode précédent par-dessus le
    // début du suivant, ce que `viderLaBalise` existe justement pour empêcher
    // (« sur un épisode qu'on n'a pas encore vu, ça montre exactement ce qu'on
    // ne voulait pas montrer »). Ici c'est l'écran d'attente de main.js et sa
    // vignette qui couvrent l'ouverture, et lui seul.
    degeler();
    etat.episode = episode ?? null;
    etat.changerLangue = changerLangue ?? null;
    etat.surChoixLangue = typeof surChoixLangue === 'function' ? surChoixLangue : null;
    etat.typeOeuvre = typeOeuvre ?? null;
    etat.proprietaire = proprietaire === true;
    etat.voixDe = typeof voixDe === 'function' ? voixDe : null;
    etat.sourcesDe = typeof sourcesDe === 'function' ? sourcesDe : null;
    etat.ouvrirSourceDe = typeof ouvrirSourceDe === 'function' ? ouvrirSourceDe : null;
    etat.surSourceOuverte = typeof surSourceOuverte === 'function' ? surSourceOuverte : null;
    // Le panneau de sources n'existe que pour le propriétaire — et une liste
    // dressée pour l'épisode précédent n'a rien à dire sur celui-ci.
    fermerPanneauSources();
    const bs = el('l-sources');
    if (bs) bs.hidden = !etat.proprietaire;
    etat.surSousTitres = surSousTitres ?? null;
    etat.pistes = [];
    etat.sonRecopiable = false; // nouvelle source : le verdict de l'ancienne ne vaut plus
    etat.voisins = voisins ?? null;
    // Un bouton qui ne mene nulle part vaut mieux desactive qu'inerte.
    const p = el('l-prec');
    const s = el('l-suiv');
    if (p) p.disabled = !voisins?.precedent;
    if (s) {
      // Pas de suivant (dernier épisode, pas encore sorti, film) : visible mais
      // grisé, et l'infobulle dit quand il sort si on le sait.
      s.disabled = !voisins?.suivant;
      s.setAttribute('aria-disabled', String(s.disabled));
      s.title = s.disabled ? (voisins?.aVenir ?? 'Pas d’épisode suivant') : 'Épisode suivant';
    }
    etat.ref = ref;
    etat.pourLaTv = {
      titre: typeof titre === 'string' ? titre : null,
      sousTitre: typeof infos === 'string' ? infos : null,
      image: typeof affiche === 'string' ? affiche : null,
    };
    etat.direct = !!direct;
    // Une nouvelle source, un nouveau droit au carton de fin : « Annuler » ne
    // vaut que pour l'épisode sur lequel on a appuyé.
    finAnnulee = false;
    suiteLancee = false;
    aJoue = false;
    fermerCarton();
    // La duree annoncee par le catalogue sert d'echelle immediate ; ffprobe
    // la remplacera par la vraie. Sans elle, la barre reste sans repere
    // pendant toute la sonde.
    etat.totale = Number(duree) || 0;
    etat.approx = !etat.totale && Number(dureeApprox) > 0;
    if (etat.approx) etat.totale = Number(dureeApprox);
    etat.decalage = 0;
    etat.visee = null; // un autre épisode : la cible de l’ancien ne vaut plus

    for (const t of video.querySelectorAll('track')) t.remove();
    retirerPisteRiche();
    // ⚠️ LE RANG SE POSE AVANT LA SOURCE, PAS APRÈS.
    //
    // `etat.audio = 0` vivait TOUT EN BAS de cette fonction, après
    // `poserSource` : la source partait donc toujours sur la piste 0, et la
    // seule façon d'en changer était `choisirAudio` — c'est-à-dire une SECONDE
    // ouverture du flux, mesurée à 479 ms au cran `direct` et 3 309 ms quand le
    // démuxeur rouvre (`essais/bascule-de-source.mjs`). Posé ici, le rang
    // voyage avec la première pose et ne coûte rien.
    etat.audio = Number.isInteger(audio) && audio >= 0 ? audio : 0;
    // Qui ouvre a-t-il tranché lui-même ? Si oui, le filet `langueVoulue` se
    // tait : il ne doit pas défaire un rang qu'on vient de calculer.
    etat.rangImpose = Number.isInteger(audio) && audio >= 0;
    // Seules 'vf' et 'vo' franchissent cette porte : 'auto', null ou un code
    // inconnu ne doivent JAMAIS atteindre `rangDeLangue`. (La règle ne lève
    // plus sur un code inconnu — voir `rangDeLaLangueVoulue` — mais la porte
    // reste : une préférence qu'on ne comprend pas ne doit pas se traduire par
    // une bascule au hasard.)
    //
    // ⚠️ ET UNE PRÉFÉRENCE JETÉE LE DIT. `langueVoulue: 'vostfr'` — ce que la
    // clé `langue` du compte contient encore chez tout le monde — tombait ici
    // à `null` en silence : le réglage n'avait aucun effet, et rien, nulle
    // part, ne permettait de s'en apercevoir. `langueVoulueLue` en traduit ce
    // qui se traduit ; ce qui ne se traduit pas s'écrit dans la console plutôt
    // que de disparaître.
    etat.langueVoulue = langueVoulueLue(langueVoulue);
    if (!etat.langueVoulue && langueVoulue != null && String(langueVoulue) !== 'auto') {
      console.warn(`[lecteur] préférence de langue ignorée, vocabulaire inconnu : ${langueVoulue}`);
    }
    // Un nouvel épisode rouvre le droit au filet, et efface le choix manuel
    // fait sur l'épisode précédent.
    etat.langueAppliquee = false;
    etat.pisteChoisieALaMain = false;
    poserSource(ref, depart, etat.audio);
    pose('l-titre', 'textContent', titre ?? '');
    poserSessionMedia({ titre, oeuvre, affiche });
    pose('l-infos', 'textContent', infos ?? '');
    // ⚠️ ON AFFICHE LA VITESSE VOULUE, PAS CELLE DE LA BALISE.
    //
    // La séance écrit 1,07 ou 0,93 sur `playbackRate` pour rattraper un
    // retard : montrer cette valeur ferait clignoter « 1,07× » à l'écran
    // pendant une correction que personne n'a demandée.
    afficherVitesse();
    // ⚠️ PAS DE VOILE SUR UN ÉCRAN QU'ON NE REGARDE PAS.
    //
    // Le voile sert à couvrir le temps de chargement SOUS LES YEUX. En fond il
    // ne couvre rien — mais il laisserait `sourceVierge` levé, et le lecteur
    // afficherait « Chargement en cours » par-dessus une image déjà prête au
    // moment du retour. Il fait tourner, en plus, l'animation du blason pour
    // personne.
    if (!enFond) voiler();
    peindre();

    // La durée peut n'être pas encore sondée : on la réclame en arrière-plan
    // et la barre se met à l'échelle dès qu'elle arrive. La lecture, elle,
    // n'attend rien.
    if (!etat.totale || etat.approx) reclamerDuree(ref);
    etat.apercu = null;
    const bulle = el('l-bulle');
    if (bulle) bulle.classList.remove('avec-vue');
    reclamerApercu(ref);
    reclamerBornes(etat.episode, etat.totale);
    // ⚠️ `etat.audio = 0` ÉTAIT ICI, et c'était le défaut : posé après
    // `poserSource`, il ne pouvait plus rien changer au flux déjà demandé. Il
    // est désormais posé plus haut, AVANT la pose, à partir du rang reçu.
    // La langue du fichier précédent n'a rien à faire sur celui-ci : tant que
    // les pistes n'ont pas dit ce qu'on écoute, on ignore vers quoi l'on irait.
    etat.langue = null;
    chargerAudio(ref);
  }

  // Le minuteur qui restaure le texte précédent après un `annoncer()` : sans
  // le garder, un second signal rapproché ne peut pas annuler celui du
  // premier.
  let minuteurSignal = null;

  /**
   * Message passager dans la ligne d'informations.
   *
   * ⚠️ DEUX SIGNAUX RAPPROCHÉS FAISAIENT RÉAPPARAÎTRE UN TEXTE PÉRIMÉ.
   *
   * Chaque appel capture le texte affiché AVANT lui et programme sa
   * restauration à 4 s. Sans annuler celle du précédent, un second signal
   * posait bien SON texte — puis se le voyait écrasé par la restauration du
   * PREMIER, qui remettait un texte déjà périmé. Un seul signal a donc le
   * droit de se restaurer à la fois.
   *
   * Le corps vit ici plutôt que dans la méthode publique : les commandes du
   * lecteur (vitesse refusée, sous-titres décalés) ont autant besoin de
   * parler que la page qui l'entoure.
   */
  /**
   * Lecture refusée faute de geste (`NotAllowedError`) : on le DIT au centre
   * de l'image, là où vit le bouton lecture, avec le panneau `#l-amorce`.
   * Toji (21/10) : « Touche l'écran… » s'affichait en haut à droite, petit,
   * gris, en capitales — et disait « Touche » à une souris. Au doigt, le
   * panneau est déjà branché par `debloquerAmorce` (main.js) ; à la souris,
   * on le branche ici : un clic lance, la lecture qui part le range.
   */
  function demanderUnGeste() {
    const panneau = el('l-amorce');
    const doigt = matchMedia('(hover: none)').matches;
    if (!panneau) { annoncer(doigt ? 'Touche l’écran pour lancer la lecture.' : 'Clique pour lancer la lecture.'); return; }
    const titre = panneau.querySelector('b');
    const mot = el('l-amorce-mot');
    if (titre) titre.textContent = doigt ? 'Touche l’écran pour lancer' : 'Clique pour lancer la lecture';
    if (mot) mot.textContent = doigt ? 'Un téléphone ne démarre jamais une vidéo tout seul.'
      : 'Le navigateur attend un premier geste avant de jouer le son.';
    panneau.hidden = false;
    if (doigt) return;
    panneau.onclick = () => { panneau.hidden = true; amorcer(); };
    video.addEventListener('playing', () => { panneau.hidden = true; }, { once: true });
  }

  function annoncer(texte) {
    clearTimeout(minuteurSignal);
    const avant = el('l-infos')?.textContent ?? '';
    pose('l-infos', 'textContent', texte);
    minuteurSignal = setTimeout(() => pose('l-infos', 'textContent', avant), 4000);
  }

  return {
    /** Signal de variante de la fiche, discret, dans la ligne d'infos existante (`l-infos`). */
    direVariante(v) {
      const phrase = phraseVariante(v);
      if (phrase) pose('l-infos', 'textContent', phrase);
    },
    /**
     * TIRE LE MORCEAU DU DÉMUXEUR PENDANT QU'ON RÉSOUT LA SOURCE.
     *
     * Sans effet de bord : aucune balise touchée, aucune source posée, aucune
     * ligne au capteur. À appeler quand la fiche d'une œuvre s'ouvre — le
     * navigateur est oisif à ce moment-là, et `/api/play-now` va prendre
     * plusieurs secondes.
     *
     * ⚠️ IL NE NOTE RIEN AU JOURNAL, ET CE N'EST PAS UN OUBLI : c'est
     * `parLeDemuxeur` — et lui seul — qui compte `chunk-absent` au capteur, sur
     * une lecture RÉELLE. Un préchauffage raté ne casse aucune lecture :
     * l'import sera simplement refait à l'ouverture. Il parle quand même : un
     * `catch` muet est ce qui rend ce dépôt aveugle.
     */
    prechauffer(suivant = null, reste = 0) {
      if (suivant?.ref) { prechaufferSuivant(suivant, reste); return; }
      if (!mseDisponible()) return;
      demuxeurPret().catch((err) => console.warn(
        '[lecteur] préchauffage du démuxeur raté — il sera refait à l’ouverture :',
        err?.message ?? err?.name ?? err,
      ));
    },

    /** Prend en charge une nouvelle source. */
    ouvrir(quoi) {
      // La balise porte `autoplay` : c'est lui qui fait partir la première
      // image sans attendre. Une préparation en fond le retire — voir
      // `preparerEnFond` —, et il faut donc le remettre ici, sinon un épisode
      // ouvert après une préparation ne démarrerait plus tout seul.
      video.autoplay = true;
      horsEcran = false;
      // ⚠️ `load()` remet `playbackRate` à 1 sans rien dire : sans ce rappel,
      // la vitesse choisie se perdait au changement d épisode.
      appliquerVitesse();
      installer(quoi);
      reveiller();
      amorcer();
      tele.nouvelleSource();
      this.direVariante(quoi?.variante);
    },

    /**
     * LE LECTEUR SE TIENT PRÊT PENDANT QU'ON REGARDE AILLEURS.
     *
     * Toji : « quand je reviens dans la page gestion des sessions, que je
     * rafraîchis, et que je retourne de nouveau dans "revenir au lecteur", ça
     * doit être instantané comme de base. »
     *
     * Un rechargement détruit la balise et son tampon — c'est le navigateur, on
     * ne peut rien y faire. Ce qu'on peut faire, c'est ne pas ATTENDRE le clic
     * pour recommencer le travail : dès qu'on sait quel épisode la séance
     * porte, la source est résolue et posée ici, en pause, sur un écran qui
     * n'est pas affiché. Le clic ne fait alors plus que changer d'écran, et
     * `estPret()` le dit — c'est la branche instantanée du bouton « Revenir au
     * lecteur », celle qui servait déjà quand on n'avait PAS rechargé.
     *
     * ⚠️ TROIS CHOSES QU'ON NE FAIT SURTOUT PAS ICI.
     *
     *   · `amorcer()` — donc aucun `play()`. La séance lit les gestes de la
     *     balise : un `play` parti d'ici serait pris pour quelqu'un qui appuie
     *     sur lecture, et repartirait au serveur en ordre pour toute la salle
     *     (quand on pilote) ou en demande de reprise (quand on regarde).
     *   · `reveiller()` — les contrôles n'ont personne à réveiller, et leur
     *     minuteur d'inactivité tournerait pour rien.
     *   · le voile — voir `installer`.
     *
     * Le vidage de la balise, lui, passe déjà par le canal muet : `poserSource`
     * enveloppe son `pause()`/`load()` dans `sansEcho`. C'est ce qui fait
     * qu'une préparation n'envoie RIEN à la séance.
     *
     * @param {number} position seconde où la séance en est. Le lecteur préparé
     *   s'y place tout de suite : une balise prête mais restée à zéro se
     *   déclare « en lecture au générique » auprès de la séance, et qui
     *   pilote ramènerait toute la salle au début.
     */
    preparerEnFond(quoi, position = 0) {
      // ⚠️ `autoplay` EST SUR LA BALISE, DANS LA PAGE.
      //
      // `<video id="video" playsinline autoplay>` : poser un `src` suffit donc
      // à faire PARTIR la lecture, sans que personne n'appelle `play()`. Une
      // préparation en fond se mettait ainsi à jouer toute seule — le son
      // sortait pendant qu'on lisait la liste des séances, et la séance
      // recevait « en lecture au générique » une fois par seconde.
      //
      // On le retire le temps de la préparation. `ouvrir` le remet, et le
      // retour devant l'écran aussi (`reprendreDeCote`) : rien d'autre ne
      // dépend de cet attribut, `amorcer` lançant la lecture explicitement.
      video.autoplay = false;
      horsEcran = true;

      // Le serveur ne sait démarrer un flux à une seconde donnée que sur le
      // chemin du remux. Un fichier lu nativement ignore `pos` — le lui passer
      // ferait compter le décalage deux fois (voir `poserSource`).
      const depart = quoi?.direct ? 0 : Math.max(0, Math.floor(position) || 0);
      installer(quoi, { depart, enFond: true });

      // Ce que le serveur n'a pas fait, la balise le fait elle-même — mais pas
      // avant de savoir ce qu'elle tient. `etat.decalage` dit lequel des deux
      // chemins a servi : nul, le flux part du début et il faut se déplacer ;
      // non nul, le serveur a déjà coupé au bon endroit.
      if (position > 0) {
        // La source pour laquelle on prepare : si elle a change avant que les
        // metadonnees arrivent, ce rappel ne nous regarde plus.
        const pour = quoi?.ref ?? null;
        video.addEventListener('loadedmetadata', () => {
          // ⚠️ CE RAPPEL SURVIVAIT A LA SOURCE QUI L A DEMANDE.
          //
          // `{ once: true }` ne le retire qu au premier `loadedmetadata` —
          // pas au changement de source. Une preparation qui echoue AVANT ce
          // moment laissait donc un ecouteur en embuscade, qui se declenchait
          // sur la source SUIVANTE et la placait a une seconde perimee.
          // Toutes les autres boucles du fichier ont cette garde, et disent
          // pourquoi ; celle-ci ne l avait pas.
          if (pour !== null && etat.ref !== pour) return;
          if (etat.decalage > 0) return;
          const T = totale();
          const t = Math.max(0, T > 0 ? Math.min(position, T - 1) : position);
          try { sansEcho(() => { video.currentTime = t; }); } catch { /* pas déplaçable */ }
        }, { once: true });
      }
    },

    /**
     * Reprend l'épisode sur un autre fichier, à la seconde où l'on en était —
     * le panneau « Changement de source », ou tout appelant qui tient un
     * `choix` de la forme de `/api/langue`. La langue se lira dans les pistes.
     */
    ouvrirSource(choix, seconde) { ouvrirUneSource(choix, seconde, null); },

    /**
     * La même reprise, quand c'est une bascule de LANGUE : `vers` est connu
     * avant que les pistes ne le disent, et la ligne d'informations l'affiche.
     */
    ouvrirAutreLangue(choix, vers, seconde) { ouvrirUneSource(choix, seconde, vers); },

    /** Message passager dans la ligne d'informations. Voir `annoncer`. */
    signaler: annoncer,

    /** Lecture refusée faute de geste : le panneau central, adapté souris/doigt. */
    demanderGeste: demanderUnGeste,

    /**
     * Qui reprendre quand la source meurt en cours de lecture.
     *
     * Se pose comme `v.onerror` dans `main.js`, au même endroit et pour le
     * même traitement — le rappel reçoit `{ ref, raison }`. Il existe parce
     * que le chemin MKV peut mourir SANS que la balise en dise rien : c'est
     * exactement le défaut qu'on corrige (voir `creerSignalDePanne`, et
     * `creerRelaisDePanne` dans `mkv.js`). Côté `main.js`, c'est
     * `brancherReprise` qui le pose — le même rappel que `v.onerror`.
     *
     * @param {null|((panne: {ref: string, raison: string}) => void)} rappel
     */
    surPanneSource(rappel) { signalDePanne.brancher(rappel); },

    /** L'écran d'erreur de lecture — voir « L'ÉCRAN D'ERREUR DE LECTURE ». */
    montrerErreur,

    /**
     * La balise a refusé le format (erreur 4) : rejouer la MÊME source un cran
     * plus bas, à la même seconde — voir « L'ESCALIER ».
     *
     * Rend `{ de, vers, reprise }` quand un cran a été pris, `null` quand
     * l'escalier est épuisé : c'est alors à `main.js` de dire « source
     * indisponible » — et de ne PAS re-résoudre pour ce motif-là. Appelé par
     * `brancherReprise`, sur le code 4 de la balise et lui seul.
     *
     * @param {object} [o]
     * @param {boolean} [o.enFond] en préparation : ni voile ni `play()` — la
     *   séance lirait ce `play` comme un geste (voir `preparerEnFond`).
     */
    /** Le cran courant de l'escalier — `brancherReprise` s'en sert pour savoir
     *  si une erreur au remux peut etre un refus du serveur (plafond). */
    cran: () => escalier.cran(),
    /** Une ligne de chemin posee de l'exterieur, dans le meme vocabulaire ferme
     *  et sous le meme plafond (une par couple et par lecture). */
    noterChemin: (chemin, raison) => capteur.noter({ chemin, raison }),
    descendre(o) { return descendreDUnCran(o); },

    /** À appeler quand les pistes viennent d'être ajoutées à la vidéo. */
    pistesPretes() {
      panneauST?.peindrePistes();
      rendu.peindre();
    },
    /**
     * Une piste ASS ou image vient d'être choisie : main.js, qui observe
     * `video.textTracks`, demande son rendu par bibliothèque — voir
     * « sous-titres riches ». `retirerPisteRiche` quand une autre piste, ou
     * aucune, prend sa place.
     */
    poserPisteRiche(quoi) { return poserPisteRiche(quoi); },
    retirerPisteRiche() { retirerPisteRiche(); },
    /** Replace la lecture à une seconde précise, sans changer de fichier. */
    recaler(seconde) { chercher(seconde); },

    /**
     * Branche la séance sur le bouton de saut.
     *
     * C'est la séance qui sait s'il faut agir ou voter, et elle seule sait
     * parler au serveur. Le lecteur, lui, n'apprend que le geste à poser — il
     * n'ouvre aucune connexion et reste utilisable sans séance du tout.
     */
    brancherSeance({ voter, sansEcho: taire } = {}) {
      voterSeance = voter ?? null;
      // ⚠️ ON REVIENT À 1× EN ENTRANT DANS UNE SÉANCE.
      //
      // Quelqu'un qui regardait seul à 1,5× rejoint une salle : sans ce
      // rappel, il dériverait aussitôt et la séance passerait son temps à le
      // ramener en arrière, sans que rien ne dise pourquoi.
      if (voterSeance && etat.vitesse !== 1) {
        etat.vitesse = 1;
        appliquerVitesse();
        afficherVitesse();
        peindreMenuVitesse();
        annoncer('Vitesse revenue à 1× : la séance se regarde ensemble.');
      }
      // Ce que le lecteur fait de lui-même à la balise ne doit pas repartir au
      // serveur : la séance prête sa fenêtre, elle seule sait la tenir.
      sansEchoSeance = taire ?? null;
    },

    /**
     * La séance a bougé : la main, un vote, le seuil. Le bouton doit peut-être
     * changer de mot, et la boucle de rendu ne tourne pas à l'arrêt — d'où le
     * redessin immédiat plutôt qu'à la prochaine image.
     */
    majSeance(vue) {
      vueSeance = vue ?? null;
      dernierEtatSaut = null; // rien ne le compare : le prochain rendu passe
      peindreSaut();
      // ⚠️ ET LE CARTON DE FIN AUSSI. La main peut arriver APRÈS la dernière
      // image — quelqu'un quitte la séance, le serveur redonne la main, un
      // vote est emporté. Sans ce redessin, le carton resterait figé sur
      // « la salle décide » alors qu'on peut désormais trancher seul, et rien
      // ne relancerait jamais le décompte : la boucle de rendu, elle, ne
      // tourne pas sur une vidéo terminée.
      if (video.ended) peindreFin();
    },

    /** Seconde où la carte « Épisode suivant » peut paraître, `null` sans bornes. */
    momentEpisodeSuivant: () => momentEpisodeSuivant(bornes),

    /** Un vote emporté, ou un pilote qui tranche : le saut se fait ici. */
    executerSaut,

    /** Référence du fichier en cours, pour aligner un autre montage dessus. */
    ref: () => etat.ref,

    /**
     * L'épisode que la balise tient, sous la clé `<série>:<saison>:<épisode>`.
     *
     * Sert à la préparation en fond : savoir qu'une source est prête ne suffit
     * pas, il faut savoir si c'est LA BONNE. La séance peut avoir changé
     * d'épisode pendant qu'on lisait la liste, et rendre l'écran sur le
     * précédent serait pire que de le recharger.
     */
    episodeCharge: () => etat.episode ?? null,

    /**
     * Le lecteur reste PRÊT, il est seulement mis de côté.
     *
     * Toji : « le lecteur, dans tous les cas, il est tout le temps ouvert en
     * fond. Même quand je quitte le lecteur, il est toujours prêt, il est juste
     * techniquement mis de côté. Comme ça, quand l'utilisateur revient, c'est
     * instantané. Il n'y a pas de délai. »
     *
     * Sortir du lecteur faisait `removeAttribute('src')` puis `load()` : la
     * source était JETÉE. Revenir coûtait alors tout le chemin — résoudre un
     * lien, ouvrir le conteneur, remplir le tampon —, plusieurs secondes pour
     * revoir une image qu'on venait de quitter.
     *
     * Ici on ne jette rien : on met en pause, et la balise garde son fichier,
     * sa position et son tampon. Revenir n'est plus qu'un changement d'écran.
     *
     * ⚠️ SANS ÉCHO, et c'est indispensable : cette pause est un geste de
     * navigation, pas une décision de séance. Sans cette précaution, quitter le
     * lecteur mettrait TOUT LE MONDE en pause — ou ouvrirait un vote de pause
     * chez les autres.
     *
     * La bande passante ne continue pas de couler : une balise en pause cesse
     * de tirer dès que son tampon est plein.
     */
    mettreDeCote() {
      try {
        // Le drapeau AVANT la pause : l'événement `pause` repeint aussitôt, et il
        // doit déjà savoir que ce n'est pas une décision de lecture.
        misDeCote = true;
        // La pause seule ne tenait pas : le chien de garde de l'amorce relançait
        // 3,5 s plus tard une lecture quittée avant sa première image, et
        // `autoplay` faisait partir toute source reposée en coulisse (repli,
        // escalier). On coupe les deux, et le jeton ferme les relances à venir.
        horsEcran = true;
        annulerAmorce();
        video.autoplay = false;
        // Le tampon du suivant tirait encore le flux derrière la liste.
        lacherChaud();
        sansEcho(() => video.pause());
        peindreLecture();
      } catch { /* rien à mettre de côté */ }
    },

    /**
     * ON REVIENT DEVANT L'ÉCRAN. Le lecteur n'était que mis de côté.
     *
     * Deux chemins y mènent, et ils demandent la même chose : le retour au
     * lecteur sans rechargement (la source n'a jamais quitté la balise) et le
     * retour après un rechargement (la source vient d'être préparée en fond).
     *
     * Ce qu'il faut rendre : l'attribut `autoplay`, que la préparation a
     * retiré, les contrôles, et la lecture elle-même.
     *
     * ⚠️ `jouer` VIENT DE LA SÉANCE, ET C'EST INDISPENSABLE.
     *
     * Revenir devant l'écran n'est pas appuyer sur lecture. Si la salle est en
     * pause, repartir ici la ferait repartir aussi — depuis qui pilote — ou
     * ouvrirait une demande de reprise chez les autres. On ne fait donc que
     * rejoindre ce qui se passe déjà.
     *
     * Et sans écho, pour la même raison : ce `play` est un geste de
     * navigation, pas une décision. La séance l'ignore, et c'est elle qui
     * recalera la seconde exacte au battement suivant.
     */
    reprendreDeCote(jouer = true) {
      video.autoplay = true;
      horsEcran = false;
      reveiller();
      if (!jouer || !video.paused) return;
      sansEcho(() => amorcer());
    },

    /**
     * Y a-t-il de quoi revenir sans rien recharger ?
     *
     * ⚠️ LE CRITÈRE N'EST PAS LA DURÉE. Mesuré : elle n'est pas finie.
     *
     * C'était `currentSrc && Number.isFinite(duration)`. Sur un MKV démuxé en
     * JavaScript, la balise ne connaît pas la durée du fichier — elle reçoit des
     * segments poussés dans un `MediaSource`, et `duration` reste `Infinity`
     * jusqu'à ce que le flux se termine. La réponse était donc TOUJOURS « pas
     * prêt », même avec trente-deux secondes de vidéo déjà en tampon : revenir au
     * lecteur relançait tout, et le préchargement ne servait à rien.
     *
     * La bonne question est celle qu'on pose partout ailleurs depuis : y a-t-il
     * de la matière à peindre ? `buffered` la porte, quel que soit le chemin —
     * démuxage local ou remux serveur.
     */
    estPret: () => Boolean(video.currentSrc)
      && (video.readyState >= 2 || video.buffered?.length > 0),

    /**
     * Ce lecteur est-il seulement MIS DE CÔTÉ ?
     *
     * La séance en a besoin pour ne pas confondre deux choses qui se ressemblent
     * à la balise près : quelqu'un qui met en pause, et quelqu'un qui s'en va.
     */
    estMisDeCote: () => misDeCote,
    /** Hors de l'écran (mis de côté ou préparé en fond) : rien ne doit relancer la balise. */
    estHorsEcran: () => horsEcran,
    video,
    position,
    totale,
    reveiller,
  };
}
